import { Show, For, createSignal, createEffect, onCleanup, untrack, type Component } from "solid-js";
import { useWs } from "@/shared/ws";
import { annexBToAvcc, buildAvcDecoderConfig, extractAvcDecoderConfig, extractH264ParameterSets } from "@/shared/h264";
import type { OutboundMessage as VideoWorkerOutbound } from "./video-decode.worker";
import type { AgentCredentialPublicKey, AgentDependencyReport, EncryptedCredential, RdsSession } from "@/shared/types";
import { codeToVk, isMacPlatform, VK } from "@/shared/keymap";
import { evaluateKey } from "@/shared/keyboard-profile";
import { KeyboardProfileEditor } from "@/shared/keyboard-profile-editor";
import { useKeyboardProfile } from "@/shared/use-keyboard-profile";
import {
  createClipboardEngine,
  entryFromSnapshot,
  type ClipboardSnapshot,
  type ClipboardTransport,
} from "@/shared/clipboard-engine";
import {
  VIDEO_PRESETS,
  QUALITY_PROFILES,
  ABR_LADDER,
  abrStepDown,
  abrStepUp,
  abrRungForBitrate,
  buildAbrUpdateParams,
  buildVideoStartParams,
  defaultVideoPreset,
  preferredDefaultVideoPreset,
  pickCodecForProfile,
  defaultQualityProfile,
  findVideoPreset,
  findQualityProfile,
  isPresetAgentSupported,
  type VideoCodecProfile,
  type VideoPreset,
  type QualityProfile,
  type FreeParams,
  type FramingMode,
} from "@/shared/video-settings";
import { VideoBridge, isNativeApp, type ViewportRect } from "@/shared/video-bridge";
import { apiFetch } from "@/shared/api-fetch";
import { wsUrlList } from "@/shared/native";
import { refreshRuntimeConfig } from "@/shared/runtime-config";
import { encryptCredentialPassword } from "@/shared/credential-encryption";
import { CredentialInput, type CredentialInputResult } from "@/shared/credential-input";
import { sealCredentialVaultCredentialForAgent } from "@/shared/credential-vault-client";
import { E2eSession, e2eEncryptionFlag, isE2eSupported, E2E_SUITE } from "@/shared/e2e-crypto";
import { formatBytes } from "@/shared/format-utils";
import { PanelBadge } from "@/shared/panel-primitives";
import { thumbGeometry, trackDeltaToDesktop } from "@/shared/viewer-scroll";
import { StatusDot } from "../console/ui";
import RdpPanel from "./rdp";
import MsraInlinePanel from "./msra-inline";
import "./video.css";

// ── Types ─────────────────────────────────────────────────────────────────────

type VideoMode = "fmp4" | "annexb";

interface VideoStartMsg {
  type: "video:start";
  mime?: string;
  codec?: string;
  resolution?: { w: number; h: number };
  /** Real remote desktop resolution (physical pixels) — used to map input coordinates. */
  desktopResolution?: { w: number; h: number };
  fps?: number;
  mode?: VideoMode;
  /** Portion du bureau effectivement capturée (px natifs) — présent en mode 1:1/crop. */
  sourceRect?: { x: number; y: number; w: number; h: number };
  /** Cadrage appliqué côté agent. */
  framingMode?: FramingMode;
  /** Type de session source (console physique vs RDP/RDS). */
  sessionKind?: "console" | "rdp";
}

interface VideoEndMsg {
  type: "video:end";
  reason?: string;
}

interface VideoNaluChunkMsg {
  type: "video:nalu:chunk";
  data: string; // base64
}

interface BrowserFrameMsg {
  type: "frame";
  data?: string;
  width?: number;
  height?: number;
  encodedWidth?: number;
  encodedHeight?: number;
  source?: string;
}

function unwrapVideoPayload(rawMsg: unknown): unknown {
  if (rawMsg && typeof rawMsg === "object" && "data" in rawMsg) {
    const data = (rawMsg as { data?: unknown }).data;
    if (data && typeof data === "object") return data;
  }
  return rawMsg;
}

interface VideoModeActiveMsg {
  type: "video:mode_active";
  mode: VideoMode;
}

interface WebRtcOfferMsg {
  type: "webrtc:offer";
  operatorId?: string;
  sdp: string;
}

interface WebRtcIceMsg {
  type: "webrtc:ice";
  operatorId?: string;
  candidate: RTCIceCandidateInit;
  fromBridge?: boolean;
}

interface VideoMetricsLiveMsg {
  type: "video:metrics:live";
  metrics?: {
    bufferedAmount?: number;
    droppedFrames?: number;
    decodeQueueSize?: number;
    decodeMs?: number;
    at?: number | null;
    ageMs?: number | null;
  };
  host?: {
    os?: string | null;
    ip?: string | null;
    vlan?: string | number | null;
  };
}

// Windows VK code map for common keys
const KEY_TO_VK: Record<string, number> = {
  Backspace: 0x08,
  Tab: 0x09,
  Enter: 0x0d,
  Shift: 0x10,
  Control: 0x11,
  Alt: 0x12,
  Pause: 0x13,
  CapsLock: 0x14,
  Escape: 0x1b,
  Space: 0x20,
  PageUp: 0x21,
  PageDown: 0x22,
  End: 0x23,
  Home: 0x24,
  ArrowLeft: 0x25,
  ArrowUp: 0x26,
  ArrowRight: 0x27,
  ArrowDown: 0x28,
  Insert: 0x2d,
  Delete: 0x2e,
  F1: 0x70,
  F2: 0x71,
  F3: 0x72,
  F4: 0x73,
  F5: 0x74,
  F6: 0x75,
  F7: 0x76,
  F8: 0x77,
  F9: 0x78,
  F10: 0x79,
  F11: 0x7a,
  F12: 0x7b,
};

type KeyboardDebugEntry = {
  id: number;
  action: string;
  key: string;
  code: string;
  vk: string;
  modifiers: string;
  note?: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function base64ToUint8Array(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function isMseSupported(): boolean {
  return typeof MediaSource !== "undefined";
}

function isWebCodecsSupported(): boolean {
  return typeof VideoDecoder !== "undefined";
}

// WS-C — flag de décodage vidéo déporté dans un Web Worker + OffscreenCanvas.
// Posé sur window au boot par bootstrapConfig (src/start/main.tsx) depuis
// /config.json (champ `videoWorkerDecode`). OFF par défaut.
function videoWorkerDecodeFlag(): boolean {
  return (
    (window as unknown as { __remotews_video_worker_decode__?: boolean }).__remotews_video_worker_decode__ === true
  );
}

// WS-D — flag de métriques navigateur fines (decode-time, frames droppées,
// taille de file du décodeur). Posé sur window au boot depuis /config.json
// (champ `videoFineMetrics`). OFF par défaut : aucune mesure/affichage en plus.
function videoFineMetricsFlag(): boolean {
  return (window as unknown as { __remotews_video_fine_metrics__?: boolean }).__remotews_video_fine_metrics__ === true;
}

// WS-G — flag de rendu WebGPU + sharpening texte (CAS) DANS le worker de décode.
// Posé sur window au boot depuis /config.json (champ `videoWebGpuRender`). OFF
// par défaut : le worker dessine en Canvas2D (drawImage) à l'identique. Quand ON,
// le worker TENTE WebGPU avec cascade silencieuse → Canvas2D si indisponible.
function videoWebGpuRenderFlag(): boolean {
  return (
    (window as unknown as { __remotews_video_webgpu_render__?: boolean }).__remotews_video_webgpu_render__ === true
  );
}

// WS-B — flag de préférence H265 au démarrage (kill-switch). Posé sur window au
// boot depuis /config.json (champ `videoH265Default`). OFF par défaut : la
// sélection de codec reste strictement identique à aujourd'hui (h264).
function videoH265DefaultFlag(): boolean {
  return (window as unknown as { __remotews_video_h265_default__?: boolean }).__remotews_video_h265_default__ === true;
}

// WS-I — flag de politique codec par profil (réseau + contenu). Posé sur window
// au boot depuis /config.json (champ `videoCodecPolicy`). OFF par défaut : la
// sélection de codec par défaut reste celle de WS-B (cf. createEffect plus bas).
function videoCodecPolicyFlag(): boolean {
  return (window as unknown as { __remotews_video_codec_policy__?: boolean }).__remotews_video_codec_policy__ === true;
}

// Support requis pour activer le worker : Worker + VideoDecoder + OffscreenCanvas
// (+ transferControlToOffscreen sur le canvas cible, vérifié au start). Toute
// condition manquante → fallback strict sur le chemin Canvas 2D thread-principal.
function isOffscreenWorkerDecodeSupported(): boolean {
  return typeof Worker !== "undefined" && typeof VideoDecoder !== "undefined" && typeof OffscreenCanvas !== "undefined";
}

function keyToVk(key: string): number | null {
  if (KEY_TO_VK[key] !== undefined) return KEY_TO_VK[key];
  // Single alphanumeric characters
  if (key.length === 1) {
    const code = key.toUpperCase().charCodeAt(0);
    // A-Z: 0x41–0x5A, 0-9: 0x30–0x39
    if (code >= 0x30 && code <= 0x5a) return code;
  }
  return null;
}

// ── Host Item type (from /host/list) ─────────────────────────────────────────

interface HostItem {
  hostId: string;
  hostname: string;
  displayName: string | null;
  persistentSessionCode: string | null;
  online: boolean;
  lastSeen: string | null;
  platform: string | null;
  ip?: string | null;
  domainFqdn?: string | null;
  systemInfo?: Record<string, unknown> | null;
  user?: string | null;
}

const RDP_TUNNEL_HOST = "127.0.0.1:3389";

function trimOrNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v.length > 0 ? v : null;
}

function firstNonEmpty(...values: unknown[]): string | null {
  for (const value of values) {
    const v = trimOrNull(value);
    if (v) return v;
  }
  return null;
}

function splitDomainAndUser(raw: string): { domain: string | null; username: string | null } {
  const value = raw.trim();
  if (!value) return { domain: null, username: null };
  if (value.includes("\\")) {
    const [a, b] = value.split("\\");
    return {
      domain: trimOrNull(a),
      username: trimOrNull(b),
    };
  }
  if (value.includes("@") && value.indexOf("@") < value.length - 1) {
    const [u, d] = value.split("@", 2);
    return {
      domain: trimOrNull(d),
      username: trimOrNull(u),
    };
  }
  return { domain: null, username: value };
}

function hostDefaultsForRdp(host: HostItem | null): {
  host: string;
  domain: string;
  username: string;
} {
  const systemInfo = host?.systemInfo && typeof host.systemInfo === "object" ? host.systemInfo : null;
  const userFromSystem = firstNonEmpty(systemInfo?.currentUser, systemInfo?.loggedInUser, systemInfo?.user, host?.user);
  const parsed = userFromSystem ? splitDomainAndUser(userFromSystem) : { domain: null, username: null };
  const domain = firstNonEmpty(host?.domainFqdn, systemInfo?.domain, systemInfo?.dnsDomain, parsed.domain);
  const username = parsed.username;
  const domainFallback = username && username.includes("\\") ? splitDomainAndUser(username).domain : null;
  return {
    host: RDP_TUNNEL_HOST,
    domain: domain ?? domainFallback ?? "",
    username: parsed.username ?? "",
  };
}

function isWindowsHost(host: HostItem | null): boolean {
  const value = host?.platform ?? "";
  return /windows/i.test(value) || value.toLowerCase().includes("win");
}

type SpecialKeyPreset = { label: string; run: () => void };
export type ControlLaunchMode = "ws" | "rdp";
export interface ControlLaunchRequest {
  mode: ControlLaunchMode;
  nonce: number;
  sessionCode?: string | null;
}
type PendingWsLaunch = {
  sessionCode: string | null;
  accessRequested: boolean;
};
const CREDENTIAL_KEY_REQUEST_TIMEOUT_MS = 5000;

// ── Idle view : liste des hôtes quand pas de streaming ───────────────────────

const MainIdleView: Component<{
  selectedPresetId: string;
  selectedQualityId: string;
  freeParams: FreeParams;
  dependencyReport: AgentDependencyReport | null;
  onPresetChange: (id: string) => void;
  onQualityChange: (id: string) => void;
  onFreeParamsChange: (p: Partial<FreeParams>) => void;
  isPresetSupported: (preset: VideoPreset) => boolean;
  onStart: (presetId?: string) => void;
  onStartWebRtc?: () => void;
  onStartRdp?: (host?: HostItem) => void;
  onStartMsra?: (host?: HostItem) => void;
  onConnecting?: (host: HostItem, protocol: "wss" | "webrtc" | "h264" | "h265" | "rdp") => void;
  sessionHost?: HostItem | null;
}> = (props) => {
  const ws = useWs();
  const hasCurrentEndpoint = () => ws.state.peers.some((p) => p.role === "client" || p.role === "agent");
  const hasScreen = () => ws.state.agentFeatures?.screen === true || hasCurrentEndpoint();
  const selectedPreset = () => findVideoPreset(props.selectedPresetId);
  const isFreeMode = () => props.selectedQualityId === "free";
  const dependencyHint = () => {
    const preset = selectedPreset();
    if (props.isPresetSupported(preset)) return null;
    const item = props.dependencyReport?.items?.find((entry) => entry.codec === preset.codec && entry.state !== "ok");
    if (!item) return "Codec non disponible sur cet agent ou ce navigateur.";
    if (item.localPath)
      return `${item.label} préparé dans ${item.localPath}. Installation locale requise si Windows demande UAC.`;
    if (item.downloadUrl)
      return `${item.label}: ${item.reason} Pré-requis dans ${props.dependencyReport?.downloadDirectory ?? "RemoteWS"}.`;
    return `${item.label}: ${item.reason}`;
  };

  function connect(host: HostItem) {
    if (!host.persistentSessionCode) return;
    ws.send({ type: "session:join", role: "operator", sessionCode: host.persistentSessionCode });
  }

  // 1-clic : rejoint la session de l'hôte ET lance le streaming dans le
  // protocole choisi. Les deux envois WS se font sur la même connexion
  // FIFO, donc le relai traite session:join avant video:control / rdp:start.
  function connectAndStart(host: HostItem, protocol: "wss" | "webrtc" | "h264" | "h265" | "rdp") {
    if (protocol === "rdp") {
      props.onStartRdp?.(host);
      return;
    }
    if (!host.persistentSessionCode) {
      return;
    }
    // Signale immédiatement l'intention de connexion → overlay parent
    // « Connexion à <host>… ». Le parent l'effacera quand streaming() est
    // vrai (cf. createEffect sur streaming/webrtcStreaming/rdp dans VideoPanel).
    props.onConnecting?.(host, protocol);
    if (props.sessionHost?.persistentSessionCode !== host.persistentSessionCode) {
      connect(host);
    }
    if (protocol === "wss") props.onStart();
    else if (protocol === "webrtc") props.onStartWebRtc?.();
    else if (protocol === "h264") props.onStart("h264-webcodecs");
    else if (protocol === "h265") props.onStart("h265-webcodecs");
  }

  const webrtcSupported = () =>
    typeof RTCPeerConnection !== "undefined" &&
    props.onStartWebRtc != null &&
    props.isPresetSupported(findVideoPreset("h264-webrtc"));
  const h264Supported = () => props.isPresetSupported(findVideoPreset("h264-webcodecs"));
  const h265Supported = () => props.isPresetSupported(findVideoPreset("h265-webcodecs"));

  // RDP est disponible si la feature `rdp` est annoncée côté agent.
  // L'opérateur n'a pas accès au champ `features` par hôte dans HostItem
  // (la liste vient de /host/list, pas du WS) — on tombe donc en repli sur
  // la feature de l'agent actuellement connecté (ws.state.agentFeatures.rdp).
  // Comportement raisonnable : un opérateur déjà connecté à un host voit
  // RDP en grisé si ce host-là ne le supporte pas ; les autres hosts qui
  // pourraient le supporter le verront aussi (faux positif possible).
  const rdpSupported = () => ws.state.agentFeatures?.rdp === true && props.onStartRdp != null;
  // MSRA masqué temporairement : la génération d'invitation fonctionne, mais le
  // visionnage se fait hors navigateur (msra.exe natif côté opérateur) et reste
  // à statuer (UX + CVE-2026-20824). Cf. A-revoir/msra.md.
  const msraSupported = () => false;

  const codecPresets = () => VIDEO_PRESETS.filter((p) => p.transport === "webcodecs");

  // État replié/déplié du bloc "Avancé" (codec + qualité + free params),
  // mémorisé pour qu'un opérateur qui aime tweaker ne le re-déplie pas à
  // chaque ouverture du panel.
  const ADVANCED_KEY = "rws.video.idle.advanced";
  const [advancedOpen, setAdvancedOpen] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem(ADVANCED_KEY) === "1";
      } catch {
        return false;
      }
    })(),
  );
  createEffect(() => {
    try {
      localStorage.setItem(ADVANCED_KEY, advancedOpen() ? "1" : "0");
    } catch {
      /* private mode — silencieux */
    }
  });

  return (
    <div class="idle-view">
      <div class="idle-view-header">
        {/* Ligne 1 : titre + recherche + refresh (les boutons globaux
            Démarrer/WebRTC ont été déplacés sur chaque carte d'hôte). */}
        <div class="idle-view-header-row">
          <span class="idle-view-title">
            {props.sessionHost != null && props.sessionHost.persistentSessionCode
              ? "Connexion à l'hôte courant"
              : hasCurrentEndpoint()
                ? "Connexion à la session courante"
                : "Hôtes disponibles"}
          </span>
        </div>

        {/* Bloc "Avancé" replié — codec, qualité, FreeParams. Caché par
            défaut ; ouverture mémorisée en localStorage. */}
        <Show when={hasScreen()}>
          <details
            class="idle-view-advanced"
            open={advancedOpen()}
            onToggle={(e) => setAdvancedOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary class="idle-view-advanced__summary">Avancé — codec, qualité, paramètres libres</summary>
            <div class="idle-view-advanced__body">
              <div class="idle-view-header-row idle-view-params">
                {/* Codec */}
                <div class="preset-pills-group">
                  <span class="preset-pills-label">Codec</span>
                  <div class="preset-pills">
                    <For each={codecPresets()}>
                      {(preset) => (
                        <button
                          class="preset-pill"
                          classList={{
                            "preset-pill--active": props.selectedPresetId === preset.id,
                            "preset-pill--disabled": !props.isPresetSupported(preset),
                          }}
                          title={preset.description + (preset.experimental ? " (expérimental)" : "")}
                          onClick={() => props.isPresetSupported(preset) && props.onPresetChange(preset.id)}
                        >
                          {preset.label}
                          {preset.experimental ? "*" : ""}
                        </button>
                      )}
                    </For>
                  </div>
                  <Show when={dependencyHint()}>
                    <span class="viewer-codec-warning" title={dependencyHint() || ""}>
                      prérequis
                    </span>
                  </Show>
                </div>

                {/* Qualité */}
                <div class="preset-pills-group">
                  <span class="preset-pills-label">Qualité</span>
                  <div class="preset-pills">
                    <For each={QUALITY_PROFILES}>
                      {(profile) => (
                        <button
                          class="preset-pill"
                          classList={{ "preset-pill--active": props.selectedQualityId === profile.id }}
                          title={profile.description}
                          onClick={() => props.onQualityChange(profile.id)}
                        >
                          {profile.label}
                        </button>
                      )}
                    </For>
                  </div>
                </div>
              </div>

              {/* Ligne 3 (conditionnelle) : mode libre */}
              <Show when={isFreeMode()}>
                <div class="idle-view-header-row idle-view-free-params">
                  <label class="free-param">
                    <span class="free-param-label">FPS</span>
                    <select
                      class="free-param-select"
                      value={String(props.freeParams.fps)}
                      onChange={(e) => props.onFreeParamsChange({ fps: Number(e.currentTarget.value) })}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="24">24</option>
                      <option value="30">30</option>
                    </select>
                  </label>
                  <label class="free-param">
                    <span class="free-param-label">Résolution</span>
                    <select
                      class="free-param-select"
                      value={String(props.freeParams.maxWidth)}
                      onChange={(e) => props.onFreeParamsChange({ maxWidth: Number(e.currentTarget.value) })}
                    >
                      <option value="640">640p</option>
                      <option value="960">960p</option>
                      <option value="1280">1280p</option>
                      <option value="1920">1920p</option>
                      <option value="2560">2560p</option>
                    </select>
                  </label>
                  <label class="free-param free-param--slider">
                    <span class="free-param-label">Débit</span>
                    <input
                      type="range"
                      min="200"
                      max="12000"
                      step="200"
                      value={props.freeParams.bitrate}
                      onInput={(e) => props.onFreeParamsChange({ bitrate: Number(e.currentTarget.value) })}
                    />
                    <span class="free-param-value">{props.freeParams.bitrate} kbps</span>
                  </label>
                  <div class="free-param">
                    <span class="free-param-label">Couleur</span>
                    <div class="preset-pills">
                      <button
                        class="preset-pill"
                        classList={{ "preset-pill--active": props.freeParams.colorMode === "color" }}
                        onClick={() => props.onFreeParamsChange({ colorMode: "color" })}
                      >
                        Couleur
                      </button>
                      <button
                        class="preset-pill"
                        classList={{ "preset-pill--active": props.freeParams.colorMode === "grayscale" }}
                        onClick={() => props.onFreeParamsChange({ colorMode: "grayscale" })}
                      >
                        N&amp;B
                      </button>
                    </div>
                  </div>
                </div>
              </Show>
            </div>
          </details>
        </Show>
      </div>

      <Show
        when={props.sessionHost != null && props.sessionHost.persistentSessionCode}
        fallback={
          <Show
            when={hasCurrentEndpoint()}
            fallback={
              <div class="idle-host-list">
                <div class="idle-empty">Aucun client n'est connecté à cette session.</div>
              </div>
            }
          >
            <div class="idle-host-session">
              <div class="idle-session-title">Connexion rapide — session courante</div>
              <div class="idle-host-card">
                <StatusDot status="online" />
                <div class="idle-host-info">
                  <span class="idle-host-name">Session agentless</span>
                  <span class="idle-host-meta">{ws.state.sessionCode ?? "session courante"}</span>
                </div>
                <PanelBadge intent="ok">client connecté</PanelBadge>
                <div class="idle-host-actions">
                  <button
                    class="btn btn--sm btn--primary"
                    disabled={!hasScreen()}
                    title="Streaming HTTP/WSS (h.264)"
                    onClick={() => props.onStart()}
                  >
                    WSS
                  </button>
                  <Show when={webrtcSupported()}>
                    <button
                      class="btn btn--sm btn--ghost"
                      disabled={!hasScreen()}
                      title="WebRTC P2P via TURN"
                      onClick={() => props.onStartWebRtc?.()}
                    >
                      WebRTC
                    </button>
                  </Show>
                  <Show when={h264Supported()}>
                    <button
                      class="btn btn--sm btn--ghost"
                      disabled={!hasScreen()}
                      title="Démarrage direct en H264 WebCodecs"
                      onClick={() => props.onStart("h264-webcodecs")}
                    >
                      H264
                    </button>
                  </Show>
                </div>
              </div>
            </div>
          </Show>
        }
      >
        <div class="idle-host-session">
          <div class="idle-session-title">Connexion rapide — hôte courant</div>
          <div class="idle-host-card">
            <StatusDot status={props.sessionHost!.online ? "online" : "offline"} />
            <div class="idle-host-info">
              <span class="idle-host-name">{props.sessionHost!.displayName || props.sessionHost!.hostname}</span>
              <span class="idle-host-meta">
                {props.sessionHost!.persistentSessionCode ?? props.sessionHost!.hostId}
                <Show when={props.sessionHost!.platform}> · {props.sessionHost!.platform}</Show>
              </span>
            </div>
            <PanelBadge intent={props.sessionHost!.online ? "ok" : "muted"}>
              {props.sessionHost!.online ? "en ligne" : "hors ligne"}
            </PanelBadge>
            <div class="idle-host-actions">
              <button
                class="btn btn--sm btn--primary"
                disabled={!props.sessionHost!.online || !hasScreen()}
                title="Streaming HTTP/WSS (h.264)"
                onClick={() => connectAndStart(props.sessionHost!, "wss")}
              >
                WSS
              </button>
              <button
                class="btn btn--sm btn--ghost"
                disabled={!props.sessionHost!.online || !hasScreen()}
                title="WebRTC P2P via TURN"
                onClick={() => connectAndStart(props.sessionHost!, "webrtc")}
              >
                WebRTC
              </button>
              <button
                class="btn btn--sm btn--ghost"
                disabled={!props.sessionHost!.online || !hasScreen() || !h264Supported()}
                title="Démarrage direct en H264 WebCodecs"
                onClick={() => connectAndStart(props.sessionHost!, "h264")}
              >
                H264
              </button>
              <button
                class="btn btn--sm btn--ghost"
                disabled={!props.sessionHost!.online || !hasScreen() || !h265Supported()}
                title="Démarrage direct en H265 WebCodecs"
                onClick={() => connectAndStart(props.sessionHost!, "h265")}
              >
                H265
              </button>
              <Show when={rdpSupported()}>
                <button
                  class="btn btn--sm btn--ghost"
                  disabled={!props.sessionHost!.online}
                  title="Bureau Windows natif via RDP"
                  onClick={() => connectAndStart(props.sessionHost!, "rdp")}
                >
                  RDP
                </button>
              </Show>
              <Show when={msraSupported()}>
                <button
                  class="btn btn--sm btn--ghost"
                  disabled={!props.sessionHost!.online}
                  title="Assistance à distance Windows (MSRA)"
                  onClick={() => props.onStartMsra?.(props.sessionHost!)}
                >
                  MSRA
                </button>
              </Show>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};

// ── Native Video Surface (Capacitor) ─────────────────────────────────────────

/**
 * Gère le cycle de vie du plugin VideoBridge natif.
 * Appelez `mount(surfaceEl)` quand la zone vidéo est montée ; le hook
 * positionne la couche native et transmet les évènements pointeur/clavier.
 * Retourne des handlers à brancher sur la div surface.
 */
function useNativeVideoSession(opts: {
  streaming: () => boolean;
  sessionCode: () => string | null;
  getToken: () => Promise<string | null>;
  relayWsUrl: () => string;
}) {
  let surfaceEl: HTMLDivElement | null = null;
  let started = false;

  function buildRelayWssUrl(wsUrl: string): string {
    if (wsUrl.startsWith("ws://") || wsUrl.startsWith("wss://")) return wsUrl;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}${wsUrl}`;
  }

  function pushViewport() {
    if (!surfaceEl || !started) return;
    const r = surfaceEl.getBoundingClientRect();
    const rect: ViewportRect = { x: r.left, y: r.top, width: r.width, height: r.height };
    void VideoBridge.setViewport(rect);
  }

  async function startNative() {
    const code = opts.sessionCode();
    if (!code) return;
    const token = await opts.getToken();
    if (!token) return;
    const relayUrl = buildRelayWssUrl(opts.relayWsUrl());
    await VideoBridge.start({ relayUrl, sessionCode: code, token });
    started = true;
    pushViewport();
  }

  function stopNative() {
    if (!started) return;
    started = false;
    void VideoBridge.stop();
  }

  // Viewport observers — resize + scroll
  let resizeObs: ResizeObserver | null = null;

  function mount(el: HTMLDivElement) {
    surfaceEl = el;
    resizeObs = new ResizeObserver(pushViewport);
    resizeObs.observe(el);
    window.addEventListener("scroll", pushViewport, { passive: true });
  }

  function unmount() {
    resizeObs?.disconnect();
    resizeObs = null;
    window.removeEventListener("scroll", pushViewport);
    surfaceEl = null;
  }

  // Pointer events — coordonnées normalisées 0..1
  function onPointerDown(e: PointerEvent) {
    if (!opts.streaming() || !surfaceEl) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const r = surfaceEl.getBoundingClientRect();
    void VideoBridge.sendPointer({
      phase: "down",
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
      button: e.button,
    });
  }

  function onPointerMove(e: PointerEvent) {
    if (!opts.streaming() || !surfaceEl || !(e.buttons > 0)) return;
    const r = surfaceEl.getBoundingClientRect();
    void VideoBridge.sendPointer({
      phase: "move",
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
    });
  }

  function onPointerUp(e: PointerEvent) {
    if (!opts.streaming() || !surfaceEl) return;
    const r = surfaceEl.getBoundingClientRect();
    void VideoBridge.sendPointer({
      phase: "up",
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
      button: e.button,
    });
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!opts.streaming()) return;
    e.preventDefault();
    void VideoBridge.sendKey({ phase: "down", code: e.code });
  }

  function onKeyUp(e: KeyboardEvent) {
    if (!opts.streaming()) return;
    void VideoBridge.sendKey({ phase: "up", code: e.code });
  }

  return { mount, unmount, startNative, stopNative, onPointerDown, onPointerMove, onPointerUp, onKeyDown, onKeyUp };
}

// ── Video Panel ───────────────────────────────────────────────────────────────

const VideoPanel: Component<{
  onStreamingChange?: (s: boolean) => void;
  sessionHost?: HostItem | null;
  launchRequest?: ControlLaunchRequest | null;
  visible?: boolean;
  vaultDefaults?: Record<string, string>;
}> = (props) => {
  const ws = useWs();

  // ── Native Capacitor path ──────────────────────────────────────────────────
  const native = isNativeApp();

  async function fetchWsToken(): Promise<string | null> {
    try {
      await refreshRuntimeConfig();
      const res = await apiFetch("/api/ws-token", { credentials: "include" });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string };
      return data.token ?? null;
    } catch {
      return null;
    }
  }

  const nativeSession = native
    ? useNativeVideoSession({
        streaming: () => streaming(),
        sessionCode: () => ws.state.sessionCode,
        getToken: fetchWsToken,
        relayWsUrl: () => {
          return wsUrlList()[0];
        },
      })
    : null;

  // State
  const [streaming, setStreaming] = createSignal(false);
  // Mode de la page Prise en main :
  //   "video" → flux WSS/WebRTC + viewer-surface (comportement historique)
  //   "rdp"   → on rend <RdpPanel /> à la place du viewer (Bureau Windows natif)
  // Bascule via les boutons de la liste idle ; retour à "video" via le bouton
  // « ← Liste des hôtes » au-dessus du RdpPanel.
  const [mainMode, setMainMode] = createSignal<"video" | "rdp" | "msra">("video");
  const [activeControlHost, setActiveControlHost] = createSignal<HostItem | null>(null);
  const [rdpHost, setRdpHost] = createSignal<HostItem | null>(null);
  const isRdpMode = () => mainMode() === "rdp";
  const isMsraMode = () => mainMode() === "msra";
  const selectedRdpHost = () => rdpHost() ?? props.sessionHost;
  const remoteControlHost = () => activeControlHost() ?? props.sessionHost ?? null;
  const rdpDefaultsForActiveHost = () => hostDefaultsForRdp(selectedRdpHost() ?? null);
  const rdpSessionCode = () => selectedRdpHost()?.persistentSessionCode ?? targetSessionCode();
  const targetSessionCode = () => props.launchRequest?.sessionCode ?? props.sessionHost?.persistentSessionCode ?? null;
  const [pendingWsLaunch, setPendingWsLaunch] = createSignal<PendingWsLaunch | null>(null);
  const [unattendedDialogOpen, setUnattendedDialogOpen] = createSignal(false);
  const [unattendedResult, setUnattendedResult] = createSignal<CredentialInputResult>({
    mode: "manual",
    ready: false,
  });
  const [unattendedError, setUnattendedError] = createSignal<string | null>(null);
  const [unattendedBusy, setUnattendedBusy] = createSignal(false);
  const [unattendedActiveId, setUnattendedActiveId] = createSignal<string | null>(null);

  function joinTargetSessionIfNeeded(): string | null {
    const code = targetSessionCode();
    if (code && ws.state.sessionCode !== code) {
      ws.send({ type: "session:join", role: "operator", sessionCode: code });
    }
    return code;
  }

  function requestAccessForCurrentSession(forceRequest = false) {
    ws.send({ type: "operator:activate" } as never);
    if (forceRequest || !accessAllows("view")) ws.send({ type: "access:request", level: "view" } as never);
    if (forceRequest || !accessAllows("control")) ws.send({ type: "access:request", level: "control" } as never);
  }

  const shouldAskUnattendedCredentials = () =>
    ws.state.noUserLoggedIn && ws.state.hostPolicy?.unattendedAccessAllowed === true && !unattendedActiveId();

  const activeRdpSessionRequiringLogin = () =>
    (ws.state.rdsSessions ?? []).find((s) => {
      const state = (s.state ?? "").toLowerCase();
      return !s.isConsole && state === "active" && Boolean(s.userName);
    }) ?? null;

  function rdpSessionLabel(session: RdsSession) {
    const user = session.userName
      ? `${session.domainName ? `${session.domainName}\\` : ""}${session.userName}`
      : "utilisateur";
    return `#${session.sessionId} ${user}`;
  }

  // PROPOSITION RDP non-bloquante. Posée quand une session RDP active est
  // détectée au lancement d'une capture WS. Ancien comportement = FORCER la
  // bascule en mode RDP (empêchait d'ouvrir/capturer la session physique
  // console). Nouveau = laisser la capture WS se poursuivre et PROPOSER de
  // rejoindre la session RDP via une bannière ; l'opérateur accepte explicitement.
  const [rdpSuggestion, setRdpSuggestion] = createSignal<RdsSession | null>(null);

  // PROPOSE (ne force pas) : pose la suggestion, sans changer de mode ni stopper le WS.
  function suggestRdpLoginForWs(session: RdsSession) {
    setRdpSuggestion(session);
  }

  // Session RDP à proposer, ré-évaluée en live : masquée en mode RDP et dès que
  // la session RDP active disparaît (évite une bannière périmée).
  const rdpSuggestionVisible = (): RdsSession | null =>
    !isRdpMode() && rdpSuggestion() ? activeRdpSessionRequiringLogin() : null;

  // ACCEPTE la proposition (clic opérateur sur la bannière) — ou détour legacy :
  // bascule réellement en mode RDP. Efface la proposition.
  function switchToRdpLoginForWs(session: RdsSession) {
    setRdpSuggestion(null);
    setPendingWsLaunch(null);
    setRdpHost(props.sessionHost ?? null);
    setActiveControlHost(props.sessionHost ?? null);
    setMainMode("rdp");
    setError(`Session RDP active ${rdpSessionLabel(session)}. Authentifiez-vous en RDP pour prendre cette session.`);
  }

  const noLoginScreenPathAvailable = () =>
    ws.state.noUserLoggedIn &&
    ws.state.hostPolicy?.systemViewLoginScreenAllowed !== true &&
    ws.state.hostPolicy?.unattendedAccessAllowed !== true;

  // Gate consentement : ne demander la clé publique agent QU'APRÈS que le
  // consentement de session est accordé (ou qu'un accès unattended/headless est
  // explicitement autorisé par la host policy). Sans ça, le relay rejette la
  // requête (drop → erreur `agent:credential_key_error`) et l'UI timeoutait sur
  // un "Cle publique agent non recue." trompeur.
  const credentialRequestAllowed = () =>
    ws.state.supportApproved === true ||
    (ws.state.noUserLoggedIn && ws.state.hostPolicy?.unattendedAccessAllowed === true);

  const requestCredentialKeyOnce = (): Promise<AgentCredentialPublicKey> => {
    if (!ws.isOpen()) return Promise.reject(new Error("Connexion WS indisponible."));
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      let off = () => {};
      const timer = window.setTimeout(() => {
        off();
        reject(new Error("Cle publique agent non recue."));
      }, CREDENTIAL_KEY_REQUEST_TIMEOUT_MS);
      off = ws.on(
        (m) => {
          const msg = m as { type?: string; requestId?: string };
          return (
            (msg.type === "agent:credential_key" || msg.type === "agent:credential_key_error") &&
            (!msg.requestId || msg.requestId === requestId)
          );
        },
        (m) => {
          window.clearTimeout(timer);
          off();
          const msg = m as { type?: string; message?: string };
          if (msg.type === "agent:credential_key_error") {
            reject(new Error(msg.message || "Cle publique agent refusee par le relais."));
            return;
          }
          resolve(m as AgentCredentialPublicKey);
        },
      );
      ws.send({ type: "agent:credential_key_request", requestId } as unknown as Parameters<typeof ws.send>[0]);
    });
  };

  const requestCredentialKey = async (): Promise<AgentCredentialPublicKey> => {
    if (!credentialRequestAllowed()) {
      throw new Error("Consentement de session requis avant de demander la cle publique agent.");
    }
    try {
      return await requestCredentialKeyOnce();
    } catch (err) {
      // Retry UNE fois : couvre le flapping transitoire du consentement
      // (arrivée d'un opérateur additionnel) où la 1re requête a pu être
      // rejetée juste avant que `supportApproved` se stabilise.
      if (!credentialRequestAllowed()) throw err;
      return requestCredentialKeyOnce();
    }
  };

  const offUnattendedResult = ws.on(
    (m) => (m as { type?: string }).type === "unattended:result",
    (m) => {
      const r = m as {
        type: "unattended:result";
        ok: boolean;
        shadowId?: string;
        error?: string;
        consoleAction?: string;
      };
      // Résultat d'une action « libérer la console » (logoff/verrouiller) — géré
      // dans le panel Policy, PAS un open de session : ne pas toucher l'état viewer.
      if (r.consoleAction) return;
      setUnattendedBusy(false);
      if (r.ok) {
        setUnattendedActiveId(r.shadowId || "active");
        setUnattendedError(null);
        setUnattendedDialogOpen(false);
        setUnattendedResult({ mode: "manual", ready: false });
        setError("Session Windows ouverte. Demarrage du flux...");
      } else {
        setUnattendedError(r.error || "Erreur inconnue");
        setUnattendedResult({ mode: "manual", ready: false });
      }
    },
  );
  onCleanup(offUnattendedResult);

  const submitUnattended = async () => {
    const result = unattendedResult();
    const ok = result.ready || (result.mode === "volt" && result.credentialId);
    if (!ok) {
      setUnattendedError("Identifiants requis (ou sélectionner un credential VOLT).");
      return;
    }
    setUnattendedError(null);
    setUnattendedBusy(true);
    try {
      let credential: EncryptedCredential | undefined;
      let username = result.username ?? "";
      let domain = result.domain ?? "";

      if (result.mode === "volt") {
        // VOLT : resceller ici si le composant n'a pas fini (fallback)
        if (result.sealed) {
          credential = result.sealed;
        } else if (result.credentialId) {
          const credentialKey = await requestCredentialKey();
          const sealed = await sealCredentialVaultCredentialForAgent(result.credentialId, {
            agentPublicKey: credentialKey,
            purpose: "powershell-runas",
            ttlSeconds: 60,
          });
          credential =
            (sealed as { sealed?: EncryptedCredential; credential?: EncryptedCredential }).sealed ??
            (sealed as { credential?: EncryptedCredential }).credential;
          if (sealed.username) username = sealed.username;
          if (sealed.domain) domain = sealed.domain;
        }
        if (!credential) throw new Error("Le scellement du credential VOLT n'a pas retourné de secret.");
      } else {
        // Manuel
        if (!result.password) {
          setUnattendedError("Mot de passe requis.");
          setUnattendedBusy(false);
          return;
        }
        const credentialKey = await requestCredentialKey();
        credential = await encryptCredentialPassword(credentialKey, result.password);
      }

      setUnattendedResult({ mode: "manual", ready: false });
      ws.send({
        type: "unattended:request_access",
        requestId: crypto.randomUUID(),
        username,
        domain,
        credential,
      } as unknown as Parameters<typeof ws.send>[0]);
    } catch (err) {
      setUnattendedBusy(false);
      setUnattendedResult({ mode: "manual", ready: false });
      setUnattendedError(err instanceof Error ? err.message : "Chiffrement du secret impossible.");
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // TODO(TEST-UNATTENDED) — HOOK DE TEST TEMPORAIRE, À RETIRER AVANT PROD.
  // Le formulaire « Accès non assisté » ne peut pas être piloté par
  // kimi-webbridge : ses events synthétiques (isTrusted=false) ne déclenchent pas
  // la réactivité Solid des inputs, donc les signals user/domain/password restent
  // vides. Ce hook expose le submit (chiffrement RSA-OAEP/AES-GCM réel inclus) à
  // un appel `evaluate`, pour valider end-to-end le login non assisté par
  // (Test hook __rwsTestUnattended supprimé : les credentials sont maintenant
  // gérés par <CredentialInput> qui ne peut pas être piloté impérativement.)

  function cancelUnattended() {
    if (unattendedBusy()) return;
    setUnattendedDialogOpen(false);
    setUnattendedError(null);
    setUnattendedResult({ mode: "manual", ready: false });
    setPendingWsLaunch(null);
    setError(null);
  }

  function startRdpCapture(host?: HostItem) {
    if (webrtcStreaming() || webrtcAttempt()) stopWebRtcCapture();
    if (streaming()) stopCapture();
    setRdpHost(host ?? props.sessionHost ?? null);
    setActiveControlHost(host ?? props.sessionHost ?? null);
    // Pas de setFollowMouseValue(true) ici : le RDP gère son input dans rdp.tsx
    // (mousemove inconditionnel) ; forcer+persister le suivi polluait localStorage
    // et ré-activait le suivi pour les sessions WS suivantes. Suivi OFF par défaut.
    setMainMode("rdp");
  }
  function exitRdpMode() {
    setRdpHost(props.sessionHost ?? null);
    setActiveControlHost(props.sessionHost ?? null);
    setMainMode("video");
  }
  function startMsraCapture(host?: HostItem) {
    setActiveControlHost(host ?? props.sessionHost ?? null);
    setMainMode("msra");
  }
  function exitMsraMode() {
    setActiveControlHost(props.sessionHost ?? null);
    setMainMode("video");
  }

  createEffect(() => {
    if (!streaming() && !webrtcStreaming() && mainMode() === "video" && props.sessionHost) {
      setActiveControlHost(props.sessionHost);
      setRdpHost(props.sessionHost);
    }
    if (!streaming() && !webrtcStreaming() && mainMode() === "video" && !props.sessionHost) {
      setActiveControlHost(null);
      setRdpHost(null);
    }
  });

  createEffect(() => {
    props.onStreamingChange?.(streaming());
  });

  const accessAllows = (level: "view" | "control") => {
    const access = ws.state.access;
    const state = access?.[level];
    if (level === "view" && ws.state.supportApproved) return true;
    return state === "approved" || state === "not_required";
  };
  let handledLaunchNonce = 0;
  createEffect(() => {
    const req = props.launchRequest;
    if (!req || req.nonce === handledLaunchNonce) return;
    handledLaunchNonce = req.nonce;
    setError(null);
    if (req.mode === "rdp") {
      setPendingWsLaunch(null);
      const code = joinTargetSessionIfNeeded();
      requestAccessForCurrentSession(Boolean(code && ws.state.sessionCode !== code));
      if (webrtcStreaming()) stopWebRtcCapture();
      if (streaming()) stopCapture();
      startRdpCapture(props.sessionHost ?? undefined);
      return;
    }
    setMainMode("video");
    setActiveControlHost(props.sessionHost ?? null);
    const code = joinTargetSessionIfNeeded();
    setPendingWsLaunch({ sessionCode: code, accessRequested: false });
  });
  createEffect(() => {
    const pending = pendingWsLaunch();
    if (!pending) return;
    // Dialogue « Accès non assisté » ouvert : la machine de lancement est en
    // attente d'une décision opérateur — ne rien (re)tenter tant qu'il est
    // affiché (sinon boucle start → video:error pendant la saisie). À la
    // fermeture (succès unattended:result ou annulation), l'effet se rejoue :
    // succès → la capture démarre ; annulation → pendingWsLaunch est vidé.
    if (unattendedDialogOpen()) return;
    if (pending.sessionCode && ws.state.sessionCode !== pending.sessionCode) return;
    if (!pending.accessRequested) {
      requestAccessForCurrentSession();
      setPendingWsLaunch({ ...pending, accessRequested: true });
      return;
    }
    // PRIORITÉ : à l'écran de login (noUserLoggedIn), l'accès non assisté
    // PRIME sur le détour RDP-login. Une entrée RDS « active » résiduelle
    // (tentative RDP précédente, état stale) détournait sinon TOUT lancement
    // WSS vers le mode RDP au lieu d'ouvrir la session Windows via le
    // dialogue de credentials puis de (re)démarrer le flux WSS. Le détour
    // RDP ne garde de sens que lorsqu'un utilisateur est réellement connecté
    // en RDP (noUserLoggedIn=false → shouldAskUnattendedCredentials=false).
    if (shouldAskUnattendedCredentials()) {
      setError("Session Windows fermee. Saisissez les credentials admin pour ouvrir une session.");
      setUnattendedDialogOpen(true);
      return;
    }
    // PROPOSER, pas forcer : si une session RDP active existe, on le signale
    // (bannière) MAIS on poursuit l'ouverture/capture WS de la session physique.
    const rdpSession = activeRdpSessionRequiringLogin();
    if (rdpSession) suggestRdpLoginForWs(rdpSession);
    if (noLoginScreenPathAvailable()) {
      setPendingWsLaunch(null);
      setError("Aucun utilisateur connecte et la policy interdit la mire de login comme l'acces non assiste.");
      return;
    }
    const viewState = ws.state.access?.view;
    if (viewState === "denied") {
      setPendingWsLaunch(null);
      setError("Prise en main refusee par la policy ou l'utilisateur.");
      return;
    }
    if (!accessAllows("view")) {
      setError("Demande de prise en main envoyee. Le flux demarrera apres accord utilisateur.");
      return;
    }
    setPendingWsLaunch(null);
    if (!streaming() && !webrtcStreaming() && mainMode() === "video") {
      // Bug #13 (volet front) : ce chemin de (re)lancement AUTO n'arme pas le
      // watchdog `connecting` (contrairement au clic IdleView via connectAndStart).
      // Un timer 15 s d'un essai PRÉCÉDENT pouvait survivre à l'ordre de batch
      // SolidJS (clear-on-error pas encore tiré) et expirer EN PLEIN nouvel essai,
      // affichant un faux « délai dépassé » quelques secondes après le clic. On
      // l'annule explicitement avant de relancer : un nouveau cycle invalide tout
      // watchdog hérité.
      clearConnecting();
      void startCapture();
    }
  });
  const [resolution, setResolution] = createSignal<{ w: number; h: number } | null>(null);
  // Real remote desktop resolution — input coordinates are mapped against this,
  // not the encoder resolution (which may differ).
  const [desktopRes, setDesktopRes] = createSignal<{ w: number; h: number } | null>(null);
  // ── Résolution adaptative + mode taille réelle (1:1) ────────────────────────
  // `framingMode` : "fit" (downscale bureau entier, défaut) ou "real" (1:1 :
  // l'agent crope `sourceRect` du bureau natif). `sourceRect` est la portion du
  // bureau réellement capturée — quand non nul, c'est lui (et pas `desktopRes`)
  // qui sert d'origine/échelle au mapping des coordonnées souris (mapToDesktop).
  // `sessionKind` distingue console physique vs RDP (informatif HUD).
  const [framingMode, setFramingMode] = createSignal<FramingMode>("fit");
  const [sourceRect, setSourceRect] = createSignal<{ x: number; y: number; w: number; h: number } | null>(null);
  const [sessionKind, setSessionKind] = createSignal<"console" | "rdp" | null>(null);
  // Viewport opérateur en pixels PHYSIQUES (round(cssW × dpr)). Mesuré sur la
  // viewer-surface et poussé à l'agent (`video:viewport`) pour que la résolution
  // d'encodage suive la fenêtre. Injecté dans le 1er `video:control start`.
  const [viewport, setViewport] = createSignal<{ w: number; h: number; dpr: number } | null>(null);
  const [fps, setFps] = createSignal<number>(0);
  // WS-B — `effectiveCodec` = codec réellement renvoyé par l'agent dans
  // `video:start` (après cascade encodeur éventuelle). Comparé à requestedCodec.
  const [effectiveCodec, setCodec] = createSignal<string>("");
  const [mode, setMode] = createSignal<VideoMode | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  // ── WS-O : chiffrement E2E applicatif du payload vidéo (OFF par défaut) ──────
  // `e2eSession` : session AES-GCM dérivée après le handshake ECDH (null tant que
  //   `e2e:key` n'a pas été reçu et la dérivation réussie). Tant qu'elle est null,
  //   AUCUN déchiffrement n'a lieu → le flux reste strictement le clair actuel.
  // `e2eComplete` : closure renvoyée par E2eSession.begin() ; à appeler avec la
  //   clé publique + le sel de l'agent (reçus dans `e2e:key`) pour dériver la clé.
  // `e2eDecryptChain` : sérialise les déchiffrements (AES-GCM async) pour préserver
  //   l'ordre des frames malgré l'asynchronisme de WebCrypto.
  // `e2eEnabled()` : ON seulement si le toggle config est ON ET WebCrypto/ECDH P-256
  //   est supporté. Sinon : aucun handshake émis → comportement inchangé.
  let e2eSession: E2eSession | null = null;
  let e2eComplete: ((agentPubB64: string, saltB64: string) => Promise<E2eSession | null>) | null = null;
  let e2eDecryptChain: Promise<void> = Promise.resolve();
  const e2eEnabled = () => e2eEncryptionFlag() && isE2eSupported();
  // WS-O — indicateur d'inspection TLS (MITM/proxy firewall) signalée par l'agent
  // au handshake WSS. Purement informatif (jamais bloquant) → badge d'avertissement.
  const [tlsInspected, setTlsInspected] = createSignal<{ issuer: string; reason: string } | null>(null);

  const [selectedPresetId, setSelectedPresetId] = createSignal(defaultVideoPreset().id);
  const [selectedQualityId, setSelectedQualityId] = createSignal(defaultQualityProfile().id);
  const [freeParams, setFreeParams] = createSignal<FreeParams>({
    fps: 15,
    maxWidth: 1280,
    bitrate: 2000,
    colorMode: "color",
  });
  const bitrate = () =>
    selectedQualityId() === "free" ? freeParams().bitrate : findQualityProfile(selectedQualityId()).bitrate;
  const [browserCodecSupport, setBrowserCodecSupport] = createSignal<Record<string, boolean>>({});
  const [controlFocused, setControlFocused] = createSignal(false);

  // ── WS-B : télémétrie codec (front) ─────────────────────────────────────────
  // `requestedCodec` = codec du preset demandé dans le dernier `video:start`
  // émis ; `_codec` (effectiveCodec) = celui réellement renvoyé par l'agent
  // (cascade encodeur : H265 KO → l'agent retombe sur H264). On expose les deux
  // pour diagnostiquer le taux réel de support/décodage HW. Affiché au HUD et
  // logué (console.info) ; pas d'émission relais (cf. rapport).
  const [requestedCodec, setRequestedCodec] = createSignal<string>("");
  const [h265DefaultApplied, setH265DefaultApplied] = createSignal(false);
  // Verrou : la sélection auto du preset par défaut (WS-B) ne s'applique qu'une
  // fois, et seulement tant que l'opérateur n'a pas choisi manuellement.
  let presetTouchedByUser = false;
  let h265DefaultEvaluated = false;

  // ── Phase 3 : Floating Action Bar — auto-fade ───────────────────────────────
  // La barre de contrôle est superposée en haut de la vidéo ; elle s'estompe
  // après ~2 s sans mouvement de souris au-dessus de la vidéo et réapparaît au
  // `mousemove`. `pointer-events` reste actif (cf. CSS) — la barre demeure
  // cliquable même estompée.
  const BAR_IDLE_MS = 2000;
  const [barVisible, setBarVisible] = createSignal(true);
  let barIdleTimer: ReturnType<typeof setTimeout> | null = null;
  const [floatingBarVisible, setFloatingBarVisible] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem("rws.video.floatingBarVisible") !== "0";
      } catch {
        return true;
      }
    })(),
  );

  function armBarIdleTimer() {
    if (barIdleTimer) clearTimeout(barIdleTimer);
    barIdleTimer = setTimeout(() => setBarVisible(false), BAR_IDLE_MS);
  }

  function wakeBar() {
    if (!barVisible()) setBarVisible(true);
    armBarIdleTimer();
  }

  function toggleFloatingBar() {
    const next = !floatingBarVisible();
    setFloatingBarVisible(next);
    if (next) {
      setBarVisible(true);
      armBarIdleTimer();
    }
  }
  createEffect(() => {
    try {
      localStorage.setItem("rws.video.floatingBarVisible", floatingBarVisible() ? "1" : "0");
    } catch {
      /* noop */
    }
  });

  onCleanup(() => {
    if (barIdleTimer) clearTimeout(barIdleTimer);
  });

  // ── Phase 3 : HUD de télémétrie ─────────────────────────────────────────────
  // Overlay optionnel affichant le framerate (fps) mesuré côté client et un
  // proxy de latence réseau (cadence d'arrivée inter-frames, en ms). Le fps est
  // toujours disponible ; la latence l'est dès qu'au moins deux frames ont été
  // reçues. Activable via un bouton de la barre flottante.
  const [hudVisible, setHudVisible] = createSignal(false);
  const [measuredFps, setMeasuredFps] = createSignal(0);
  // Cadence d'arrivée moyenne entre deux frames décodées (ms) — proxy de la
  // latence réseau perçue : un flux fluide à 30 fps cadence ~33 ms.
  const [frameIntervalMs, setFrameIntervalMs] = createSignal(0);
  // Débit vidéo mesuré côté client (kbps) sur la fenêtre d'1 s.
  const [bitrateKbps, setBitrateKbps] = createSignal(0);
  // CPU du process vidéo côté hôte (Helper), poussé par télémétrie (video:telemetry).
  const [helperCpu, setHelperCpu] = createSignal<number | null>(null);
  // ── WS-D : métriques navigateur fines (decode-time, drops, queue) ───────────
  // Calculées/affichées uniquement quand videoFineMetricsFlag() est ON. Valent
  // pour les DEUX chemins : main (instrumentation locale ci-dessous) et worker
  // (le worker les mesure et poste un message `metrics` qui alimente ces signals).
  const fineMetrics = videoFineMetricsFlag();
  // EMA du temps de décodage (ms) : entre decode() et output (chemin main) ou
  // tel que mesuré par le worker (chemin worker).
  const [decodeMs, setDecodeMs] = createSignal(0);
  // Cumul des frames décodées mais jamais dessinées (mailbox 1-slot écrasée).
  const [droppedFrames, setDroppedFrames] = createSignal(0);
  // VideoDecoder.decodeQueueSize — backlog du décodeur (frames en attente).
  const [decodeQueueSize, setDecodeQueueSize] = createSignal(0);
  const [liveWsBufferedBytes, setLiveWsBufferedBytes] = createSignal(0);
  const [liveMetricsAt, setLiveMetricsAt] = createSignal<number | null>(null);
  const [liveHost, setLiveHost] = createSignal<{ os: string | null; ip: string | null; vlan: string | null }>({
    os: null,
    ip: null,
    vlan: null,
  });
  const liveHostOs = () => liveHost().os;
  const liveHostIp = () => liveHost().ip;
  const liveHostVlan = () => liveHost().vlan;
  // Compteur de frames décodées sur la fenêtre courante d'1 s.
  let fpsFrameCount = 0;
  let fpsWindowStart = 0;
  let lastFrameArrival = 0;
  let frameIntervalEma = 0;
  let bitrateBytesAccum = 0;
  // Anneau d'échantillons kbps (~1/s) — bitrate HUD lissé sur ~30 s.
  let bitrateSamples: number[] = [];
  // WS-D — état decode-time (chemin main) : file FIFO des timestamps d'envoi
  // appariés au 1er output suivant (ordre DTS), + EMA. Drops comptés dans la
  // mailbox (cf. makeDecoderInit). decodeQueueSize lu sur le décodeur main.
  let decodeSentTimestamps: number[] = [];
  let decodeMsEma = 0;
  let droppedFramesAccum = 0;

  /** Appelé à chaque frame vidéo reçue — alimente les métriques du HUD. */
  function recordFrameMetric() {
    const now = performance.now();
    // EMA de l'intervalle inter-frames (lissage léger, alpha 0.2).
    if (lastFrameArrival > 0) {
      const delta = now - lastFrameArrival;
      frameIntervalEma = frameIntervalEma === 0 ? delta : frameIntervalEma * 0.8 + delta * 0.2;
    }
    lastFrameArrival = now;
    // Date.now() partagé avec l'overlay "Hôte hors ligne" (cf. streamStale()
    // plus bas) — performance.now() ne sert qu'aux deltas internes.
    setLastFrameAt(Date.now());
    // Fenêtre glissante d'1 s pour le compteur fps.
    if (fpsWindowStart === 0) fpsWindowStart = now;
    fpsFrameCount++;
    if (now - fpsWindowStart >= 1000) {
      setMeasuredFps(Math.round((fpsFrameCount * 1000) / (now - fpsWindowStart)));
      setFrameIntervalMs(Math.round(frameIntervalEma));
      // kbps instantané (octets × 8 / ms) de cette fenêtre ~1 s, puis moyenne
      // glissante sur ~30 échantillons (~30 s) pour un affichage lissé/stable.
      const instKbps = (bitrateBytesAccum * 8) / (now - fpsWindowStart);
      bitrateSamples.push(instKbps);
      if (bitrateSamples.length > 30) bitrateSamples.shift();
      const avgKbps = bitrateSamples.reduce((a, b) => a + b, 0) / bitrateSamples.length;
      setBitrateKbps(Math.round(avgKbps));
      bitrateBytesAccum = 0;
      fpsFrameCount = 0;
      fpsWindowStart = now;
      // WS-D — publie les métriques fines du CHEMIN MAIN ~1×/s sur la même
      // fenêtre. No-op si OFF. (Le chemin worker passe par onmessage "metrics".)
      if (fineMetrics && !workerActive()) {
        setDecodeMs(Math.round(decodeMsEma * 100) / 100);
        setDroppedFrames(droppedFramesAccum);
        setDecodeQueueSize(videoDecoder ? videoDecoder.decodeQueueSize : 0);
      }
    }
  }

  function resetFrameMetrics() {
    fpsFrameCount = 0;
    fpsWindowStart = 0;
    lastFrameArrival = 0;
    frameIntervalEma = 0;
    bitrateBytesAccum = 0;
    bitrateSamples = [];
    setMeasuredFps(0);
    setLastFrameAt(null);
    setFrameIntervalMs(0);
    setBitrateKbps(0);
    setHelperCpu(null);
    // WS-D — reset des accumulateurs/signals de métriques fines (no-op visuel
    // quand OFF puisque jamais affichés).
    decodeSentTimestamps = [];
    decodeMsEma = 0;
    droppedFramesAccum = 0;
    setDecodeMs(0);
    setDroppedFrames(0);
    setDecodeQueueSize(0);
  }

  // Swap Cmd↔Ctrl : activé par défaut quand l'opérateur est sur Mac.
  const [swapCmdCtrl, setSwapCmdCtrl] = createSignal(isMacPlatform());
  // Profil clavier dérivé de `swapCmdCtrl` (rétrocompat avec l'UI existante) :
  //  - swap ON → profil `mac` : Cmd→Ctrl (Cmd+C = Ctrl+C), liste noire
  //    Cmd+Tab/Space/Q/H restent locales.
  //  - swap OFF → profil `windows` : Cmd = touche Windows, natif.
  // Le profil `mac` généralise l'ancien `swapCmdCtrl` (qui ne faisait que
  // LWIN/RWIN→CONTROL) en ajoutant la liste noire Mac. `eventToVk` consulte
  // ce profil pour décider du swap Cmd→Ctrl.
  // Profil clavier persisté via le hook partagé (lit mac/windows/custom depuis
  // localStorage). Le bouton Cmd→Ctrl existant reste (rétrocompat) ; le bouton
  // "Profil" ouvre l'éditeur complet. swapCmdCtrl est synchronisé avec le profil
  // pour que le bouton legacy reflète l'état réel.
  const kbProfile = useKeyboardProfile();
  const keyboardProfile = kbProfile.profile;
  const [profileEditorOpen, setProfileEditorOpen] = createSignal(false);
  const activeProfileId = kbProfile.activeProfileId;
  const onProfileChange = kbProfile.setActiveProfileId;

  // ── Débug clavier : logs locaux de toutes les touches vues/synthétiques. ───
  const KEYBOARD_DEBUG_KEY = "rws.video.keyboardDebugVisible";
  const [keyboardDebugVisible, _setKeyboardDebugVisible] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem(KEYBOARD_DEBUG_KEY) === "1";
      } catch {
        return false;
      }
    })(),
  );
  const [keyboardDebugEntries, setKeyboardDebugEntries] = createSignal<KeyboardDebugEntry[]>([]);
  let keyboardDebugSeq = 0;
  createEffect(() => {
    try {
      localStorage.setItem(KEYBOARD_DEBUG_KEY, keyboardDebugVisible() ? "1" : "0");
    } catch {
      /* noop */
    }
  });

  function keyboardModifiers(e: KeyboardEvent): string {
    const mods: string[] = [];
    if (e.ctrlKey) mods.push("Ctrl");
    if (e.shiftKey) mods.push("Shift");
    if (e.altKey) mods.push("Alt");
    if (e.metaKey) mods.push("Meta");
    if (e.getModifierState("AltGraph")) mods.push("AltGraph");
    return mods.join("+") || "-";
  }

  function debugVk(vk: number | null): string {
    if (vk === null) return "-";
    return `0x${vk.toString(16).toUpperCase().padStart(2, "0")}`;
  }

  function recordKeyboardDebug(entry: {
    action: string;
    key: string;
    code: string;
    vk: number | null;
    note?: string;
    modifiers?: string;
    event?: KeyboardEvent;
  }) {
    if (!keyboardDebugVisible()) return;
    setKeyboardDebugEntries((rows) => {
      const next: KeyboardDebugEntry = {
        id: ++keyboardDebugSeq,
        action: entry.action,
        key: entry.key,
        code: entry.code,
        vk: debugVk(entry.vk),
        modifiers: entry.modifiers || (entry.event ? keyboardModifiers(entry.event) : "-"),
        note: entry.note,
      };
      return [next, ...rows].slice(0, 12);
    });
  }

  function recordKeyboardTextDebug(action: string, text: string, note?: string) {
    if (!keyboardDebugVisible()) return;
    const value = text.length > 24 ? `${text.slice(0, 24)}…` : text;
    setKeyboardDebugEntries((rows) => {
      const next: KeyboardDebugEntry = {
        id: ++keyboardDebugSeq,
        action,
        key: value,
        code: "text",
        vk: "Unicode",
        modifiers: "-",
        note,
      };
      return [next, ...rows].slice(0, 12);
    });
  }

  function _applyControlPreset(presetId: "desktop" | "mobile" | "mac") {
    if (presetId === "desktop") {
      setFollowMouse(true);
      setTrackpadMode(false);
      setSwapCmdCtrl(isMacPlatform());
      setPointerLocked(false);
      setHudVisible(false);
      showToast("Preset desktop appliqué");
      return;
    }
    if (presetId === "mobile") {
      setTrackpadMode(true);
      setFollowMouse(false);
      setSwapCmdCtrl(isMacPlatform());
      setPointerLocked(false);
      setHudVisible(true);
      showToast("Preset mobile appliqué");
      return;
    }

    setSwapCmdCtrl(true);
    setTrackpadMode(false);
    setFollowMouse(true);
    showToast("Preset Mac→Windows appliqué");
  }

  const specialKeySets: Record<string, SpecialKeyPreset[]> = {
    windows: [
      { label: "Ctrl+Alt+Suppr", run: () => sendSas() },
      { label: "Ctrl+Alt+Tab", run: () => runSpecialCombo([VK.CONTROL, VK.ALT, VK.TAB]) },
      { label: "Alt+Tab", run: () => runSpecialCombo([VK.ALT, VK.TAB]) },
      { label: "Win+L", run: () => runSpecialCombo([VK.LWIN, VK.L]) },
      { label: "Win+D", run: () => runSpecialCombo([VK.LWIN, VK.D]) },
      { label: "Win+E", run: () => runSpecialCombo([VK.LWIN, VK.E]) },
      { label: "Win+R", run: () => runSpecialCombo([VK.LWIN, 0x52]) },
      { label: "Win+X", run: () => runSpecialCombo([VK.LWIN, 0x58]) },
      // Ramène dans l'écran une fenêtre distante débordante ou mal positionnée
      // (barre de menus hors champ). ponytail: si la fenêtre n'est ni
      // maximisable ni ramenable, il faudra un outil computeruse SetWindowPos
      // — au prix d'un rebuild plugin + propagation flotte.
      { label: "Win+↑ (Agrandir)", run: () => runSpecialCombo([VK.LWIN, 0x26]) },
      { label: "Win+↓ (Restaurer)", run: () => runSpecialCombo([VK.LWIN, 0x28]) },
      { label: "Ctrl+Shift+Éch.", run: () => runSpecialCombo([VK.CONTROL, 0xa0, VK.ESCAPE]) },
      { label: "Alt+F4", run: () => runSpecialCombo([VK.ALT, 0x73]) },
      { label: "Échap", run: () => runSpecialCombo([VK.ESCAPE]) },
    ],
    mac: [
      { label: "⌘+Q", run: () => runSpecialCombo([VK.LWIN, VK.Q]) },
      { label: "⌘+W", run: () => runSpecialCombo([VK.LWIN, 0x57]) },
      { label: "⌘+Espace", run: () => runSpecialCombo([VK.LWIN, VK.SPACE]) },
      { label: "⌘+Tab", run: () => runSpecialCombo([VK.LWIN, VK.TAB]) },
      { label: "⌘+H", run: () => runSpecialCombo([VK.LWIN, 0x48]) },
      { label: "⌘+Maj+3", run: () => runSpecialCombo([VK.LWIN, 0xa0, 0x33]) },
      { label: "⌘+Maj+4", run: () => runSpecialCombo([VK.LWIN, 0xa0, 0x34]) },
      { label: "⌘+Alt+Échap", run: () => runSpecialCombo([VK.LWIN, VK.ALT, VK.ESCAPE]) },
      { label: "⌘+Maj+⌫", run: () => runSpecialCombo([VK.LWIN, 0xa0, VK.BACK]) },
      { label: "Ctrl+Alt+Suppr", run: () => sendSas() },
    ],
  };

  // Suivi continu de la souris — OFF par défaut. Quand OFF, les `mousemove`
  // simples ne sont PAS streamés (le curseur distant ne « suit » plus) ; seuls
  // les clics (qui portent x,y), le drag (mousemove bouton enfoncé), la molette
  // et le clavier agissent. Quand ON, le curseur distant suit la souris.
  // Le pointer-lock (prise en main verrouillée) reste indépendant de ce réglage.
  const FOLLOW_MOUSE_KEY = "rws.followMouse";
  function readFollowMousePref(): boolean {
    try {
      const value = localStorage.getItem(FOLLOW_MOUSE_KEY);
      if (value === null) return false;
      return value === "1";
    } catch {
      return false;
    }
  }
  const [followMouse, setFollowMouse] = createSignal(readFollowMousePref());
  const textInputDown = new Set<string>();

  function setFollowMouseValue(enabled: boolean) {
    try {
      localStorage.setItem(FOLLOW_MOUSE_KEY, enabled ? "1" : "0");
    } catch {
      // localStorage indisponible — réglage non persisté
    }
    setFollowMouse(enabled);
  }
  function toggleFollowMouse() {
    setFollowMouseValue(!followMouse());
  }
  // Menu déroulant des touches spéciales.
  const [specialKeysOpen, setSpecialKeysOpen] = createSignal(false);
  const [clipboardMenuOpen, setClipboardMenuOpen] = createSignal(false);
  // Moteur clipboard partagé pour l'historique + mode. Le transport WS est un
  // stub : les vraies actions passent par pushClipboard/requestClipboard (qui
  // émettent les messages WS) puis alimentent l'historique via pushHistory.
  const videoClipboardTransport: ClipboardTransport = {
    supportsRichText: () => false, // ClipboardBridge agent = texte only.
    supportsFiles: () => true, // Proto file:* existe.
    async pushToRemote() {
      return true; // Le vrai push se fait via pushClipboard.
    },
    async requestFromRemote() {
      return null; // Le pull arrive via clipboard:sync handler.
    },
  };
  const videoClipboardEngine = createClipboardEngine(videoClipboardTransport);
  const [controlSettingsOpen, setControlSettingsOpen] = createSignal(false);
  const activeSpecialKeys = () =>
    isWindowsHost(remoteControlHost() ?? null) ? specialKeySets.windows : specialKeySets.mac;
  function closeFloatingSubMenus() {
    setQualityPickerOpen(false);
    setSpecialKeysOpen(false);
    setClipboardMenuOpen(false);
    setControlSettingsOpen(false);
  }
  function toggleSpecialKeysMenu() {
    const next = !specialKeysOpen();
    closeFloatingSubMenus();
    setSpecialKeysOpen(next);
  }
  function toggleClipboardMenu() {
    const next = !clipboardMenuOpen();
    closeFloatingSubMenus();
    setClipboardMenuOpen(next);
  }
  function toggleControlSettingsMenu() {
    const next = !controlSettingsOpen();
    closeFloatingSubMenus();
    setControlSettingsOpen(next);
  }

  // ── Mobile : Trackpad mode + curseur virtuel ────────────────────────────────
  // Sur mobile, taper directement avec le doigt sur un écran 1920×1080
  // affiché en 390×844 est illisible. Le mode "trackpad" affiche un curseur
  // virtuel que l'opérateur fait glisser (en coords RELATIVES) — comme un
  // pavé tactile macOS. Tap court = clic à la position courante du curseur.
  // Comportement calqué sur TeamViewer mobile.
  const TRACKPAD_KEY = "rws.video.trackpadMode";
  const [trackpadMode, setTrackpadMode] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem(TRACKPAD_KEY) === "1";
      } catch {
        return false;
      }
    })(),
  );
  createEffect(() => {
    try {
      localStorage.setItem(TRACKPAD_KEY, trackpadMode() ? "1" : "0");
    } catch {
      /* private mode */
    }
  });
  // Position normalisée du curseur virtuel (0..1 dans la surface). Le curseur
  // démarre au centre quand le mode est activé.
  const [virtualCursor, setVirtualCursor] = createSignal<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  // État du geste tactile en cours pour distinguer tap (clic) vs drag (move).
  let touchAnchor: { clientX: number; clientY: number; t: number } | null = null;
  let touchMoved = false;
  const TAP_THRESHOLD_PX = 8;
  const TAP_MAX_MS = 250;

  // ── Pinch-zoom + pan (touch, 2 doigts) ──────────────────────────────────
  // Zoom : 2 doigts qui s'écartent/rapprochent. Pan : 2 doigts qui glissent
  // ensemble. Reset : double-tap sur la surface. Le scale s'applique en CSS
  // sur .viewer-surface (transform: translate + scale).
  const [zoomScale, setZoomScale] = createSignal(1);
  const [zoomOffset, setZoomOffset] = createSignal<{ x: number; y: number }>({ x: 0, y: 0 });
  const isZoomed = () => zoomScale() > 1.001;
  // Pinch en cours (entre TouchStart 2-doigts et TouchEnd) — utilisé pour
  // afficher l'indicateur transient « 1.5× » au-dessus de la surface.
  const [pinching, setPinching] = createSignal(false);
  // Mémorise un timer pour faire fondre l'indicateur 800 ms après la fin
  // du geste, le temps que l'opérateur lise la valeur finale.
  let zoomFadeTimer: ReturnType<typeof setTimeout> | null = null;
  const [zoomIndicatorVisible, setZoomIndicatorVisible] = createSignal(false);
  function showZoomIndicator() {
    setZoomIndicatorVisible(true);
    if (zoomFadeTimer) clearTimeout(zoomFadeTimer);
  }
  function fadeZoomIndicator() {
    if (zoomFadeTimer) clearTimeout(zoomFadeTimer);
    zoomFadeTimer = setTimeout(() => setZoomIndicatorVisible(false), 800);
  }
  onCleanup(() => {
    if (zoomFadeTimer) clearTimeout(zoomFadeTimer);
  });

  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  let pinchAnchor: { dist: number; scale: number; midX: number; midY: number; offX: number; offY: number } | null =
    null;
  let pinchMoved = false; // true dès qu'un 2-finger move dépasse le seuil → ignore le right-click trackpad
  const PINCH_THRESHOLD_PX = 6;
  function touchDistance(a: Touch, b: Touch): number {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }
  // Clamp les offsets de pan pour éviter que la surface scrollée disparaisse
  // hors écran. À scale S, l'excès de chaque côté est `naturel * (S-1) / 2`.
  function clampOffsetForScale(off: { x: number; y: number }, scale: number): { x: number; y: number } {
    if (scale <= 1) return { x: 0, y: 0 };
    const el = getSurface();
    if (!el) return off;
    const rect = el.getBoundingClientRect();
    const currentScale = zoomScale();
    const naturalW = currentScale > 0 ? rect.width / currentScale : rect.width;
    const naturalH = currentScale > 0 ? rect.height / currentScale : rect.height;
    const maxX = (naturalW * (scale - 1)) / 2;
    const maxY = (naturalH * (scale - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, off.x)),
      y: Math.max(-maxY, Math.min(maxY, off.y)),
    };
  }
  function resetZoom() {
    setZoomScale(1);
    setZoomOffset({ x: 0, y: 0 });
    showZoomIndicator();
    fadeZoomIndicator();
  }
  // Double-tap → reset du zoom.
  let lastTapAt = 0;
  function checkDoubleTapForZoomReset() {
    const now = Date.now();
    if (isZoomed() && now - lastTapAt < 300) {
      resetZoom();
      lastTapAt = 0;
      return true;
    }
    lastTapAt = now;
    return false;
  }

  function sendVirtualMove() {
    if (!ensureCurrentOperatorActive()) return;
    // Le curseur virtuel est une fraction 0..1 de la surface → même espace que
    // (fx,fy) : mapToDesktop applique sourceRect (1:1) ou la résolution bureau.
    if (!sourceRect() && !desktopRes() && !resolution()) return;
    const c = virtualCursor();
    const { x, y } = mapToDesktop(c.x, c.y);
    queueMove({ kind: "move", x, y, absolute: true });
  }

  function sendVirtualClick(button: "left" | "right" = "left") {
    if (!ensureCurrentOperatorActive()) return;
    if (!sourceRect() && !desktopRes() && !resolution()) return;
    const c = virtualCursor();
    const { x, y } = mapToDesktop(c.x, c.y);
    flushMove();
    ws.send({ type: "input", input: { kind: "button", button, down: true, x, y } });
    ws.send({ type: "input", input: { kind: "button", button, down: false, x, y } });
  }

  // ── M3 : Overlay "Connexion à <host>..." ───────────────────────────────────
  // Couvre la fenêtre entre le clic protocole et la 1ʳᵉ frame / 1ʳᵉ surface
  // RDP. Watchdog 15 s qui affiche une erreur claire si rien ne vient.
  const [connecting, setConnecting] = createSignal<{
    host: HostItem;
    protocol: "wss" | "webrtc" | "h264" | "h265" | "rdp";
  } | null>(null);
  let connectingTimer: ReturnType<typeof setTimeout> | null = null;
  function isCurrentHostLive(host: HostItem, protocol: "wss" | "webrtc" | "h264" | "h265" | "rdp") {
    const hostCode = host.persistentSessionCode;
    const sameSession = !hostCode || ws.state.sessionCode === hostCode;
    if (!sameSession) return false;
    if (protocol === "rdp") return mainMode() === "rdp";
    if (protocol === "webrtc") return webrtcStreaming();
    return streaming() && transport() === "wss";
  }
  function startConnecting(host: HostItem, protocol: "wss" | "webrtc" | "h264" | "h265" | "rdp") {
    setActiveControlHost(host);
    if (isCurrentHostLive(host, protocol)) {
      clearConnecting();
      return;
    }
    if (connectingTimer) clearTimeout(connectingTimer);
    setConnecting({ host, protocol });
    connectingTimer = setTimeout(() => {
      if (connecting()) {
        // Ne pas écraser une erreur distante déjà affichée (ex. « Encodage
        // vidéo indisponible ») par un faux « délai dépassé » : si error() est
        // non nul, on se contente de retirer l'overlay sans masquer la cause
        // réelle. (Cas normalement déjà neutralisé par le clear-on-error de
        // l'effet ci-dessous, mais défense en profondeur sur l'ordre des batchs.)
        if (!error()) setError(`Connexion impossible vers ${host.hostname} — délai dépassé (15 s).`);
        setConnecting(null);
      }
    }, 15_000);
  }
  function clearConnecting() {
    if (connectingTimer) {
      clearTimeout(connectingTimer);
      connectingTimer = null;
    }
    setConnecting(null);
  }
  // Quand un canal commence à pousser des données, on enlève l'overlay.
  // Défense en profondeur : une erreur affichée (error() non nul) est aussi un
  // état terminal du « connecting » — si aucun canal n'est devenu actif, on
  // retire l'overlay pour ne pas bloquer les clics sur l'IdleView. Sans risque
  // pour un nouvel essai : error() est remis à null dans startCapture
  // (setError(null)) et dans l'effet pendingWsLaunch avant tout re-lancement.
  createEffect(() => {
    if (streaming() || webrtcStreaming() || mainMode() === "rdp") clearConnecting();
    else if (error()) clearConnecting();
  });
  onCleanup(() => {
    if (connectingTimer) clearTimeout(connectingTimer);
  });

  // ── M5 : détection de flux figé (host offline) ─────────────────────────────
  // `lastFrameAt` est bumpé par recordFrameMetric à chaque frame décodée.
  // On considère le flux "stale" si > 5 s sans nouvelle frame en streaming.
  const [lastFrameAt, setLastFrameAt] = createSignal<number | null>(null);

  // Horodatage du début du streaming — affiché dans la session-info sheet.
  // Bumped au passage streaming(false → true) ou webrtcStreaming(false → true).
  const [streamStartedAt, setStreamStartedAt] = createSignal<number | null>(null);
  createEffect(() => {
    const live = streaming() || webrtcStreaming();
    if (live && streamStartedAt() == null) setStreamStartedAt(Date.now());
    if (!live) setStreamStartedAt(null);
    window.dispatchEvent(new CustomEvent("remotews:video-active", { detail: { active: live } }));
  });
  onCleanup(() => window.dispatchEvent(new CustomEvent("remotews:video-active", { detail: { active: false } })));

  // ── Session info sheet (overlay plein écran type TeamViewer) ────────────────
  const [sessionInfoOpen, setSessionInfoOpen] = createSignal(false);
  // Tick pour mettre à jour la durée affichée en temps réel.
  const [infoTick, setInfoTick] = createSignal(0);
  const infoTickInterval = setInterval(() => {
    if (sessionInfoOpen()) setInfoTick((t) => t + 1);
  }, 1000);
  onCleanup(() => clearInterval(infoTickInterval));
  const formatDuration = (ms: number): string => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
  };
  const sessionDuration = () => {
    infoTick();
    const start = streamStartedAt();
    return start ? formatDuration(Date.now() - start) : "—";
  };

  // ── Sélecteur qualité in-session (popover) ──────────────────────────────────
  // Changer la qualité = stop + start avec le nouveau preset. La transition
  // est masquée par l'overlay « Connexion… » (M3) qui se rallume.
  const [qualityPickerOpen, setQualityPickerOpen] = createSignal(false);
  function toggleVideoSettingsMenu() {
    const next = !qualityPickerOpen();
    closeFloatingSubMenus();
    setQualityPickerOpen(next);
  }
  async function switchQuality(profileId: string) {
    if (profileId === selectedQualityId()) {
      setQualityPickerOpen(false);
      return;
    }
    setSelectedQualityId(profileId);
    setQualityPickerOpen(false);
    // Stop puis start dans la foulée — si on streame en WSS, on relance WSS ;
    // si on est en WebRTC, on relance WebRTC. RDP n'est pas concerné.
    const wasWebrtc = webrtcStreaming();
    if (streaming()) {
      stopCapture();
      // Petite latence pour laisser l'agent traiter le stop avant le start.
      setTimeout(() => void startCapture(), 250);
    } else if (wasWebrtc) {
      stopWebRtcCapture();
      setTimeout(() => startWebRtcCapture(), 250);
    }
  }
  // Tick chaque seconde pour réévaluer streamStale() sans dépendance externe.
  const [staleTick, setStaleTick] = createSignal(0);
  const staleInterval = setInterval(() => setStaleTick((t) => t + 1), 1000);
  onCleanup(() => clearInterval(staleInterval));
  const STREAM_STALE_MS = 5_000;
  const streamStale = () => {
    staleTick();
    if (!streaming() && !webrtcStreaming()) return false;
    const ts = lastFrameAt();
    if (!ts) return false;
    return Date.now() - ts > STREAM_STALE_MS;
  };

  // ── M4 : helpers multi-opérateur (badge dans la barre flottante) ──────────
  const operators = () => ws.state.peers.filter((p) => p.role === "operator");
  const isMultiOp = () => operators().length > 1;
  const activeOp = () => operators().find((p) => p.active);
  const isMeActive = () => {
    const me = ws.state.myPeerId;
    const active = activeOp();
    return me != null && active != null && active.id === me;
  };
  // Garde input : un AUTRE opérateur détient le contrôle actif. On bloque
  // l'envoi côté UI pour éviter une erreur réseau (le serveur refuse l'input
  // des opérateurs non-actifs). Conservateur : si personne n'est actif, on
  // n'empêche rien (rétro-compat mono-opérateur).
  const anotherOpIsActive = () => {
    const active = activeOp();
    return active != null && !isMeActive();
  };
  function ensureCurrentOperatorActive() {
    if (!anotherOpIsActive()) return true;
    ws.send({ type: "operator:activate" } as never);
    showToast("Controle repris pour cet onglet. Refaites l'action.");
    return false;
  }

  // ── M1 : clavier mobile (input invisible qu'on focus pour pop le clavier OS) ─
  const [keyboardOpen, setKeyboardOpen] = createSignal(false);
  let mobileKbInput: HTMLInputElement | undefined;
  function toggleMobileKeyboard() {
    if (keyboardOpen()) {
      setKeyboardOpen(false);
      mobileKbInput?.blur();
    } else {
      setKeyboardOpen(true);
      // Focus différé : le navigateur n'ouvre le clavier que sur un focus
      // explicite issu d'une interaction utilisateur. Le click handler EST
      // l'interaction, donc focus synchrone fonctionne sur iOS/Android.
      mobileKbInput?.focus();
    }
  }
  function onMobileKbInput(e: InputEvent) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    const t = e.currentTarget as HTMLInputElement;
    const val = t.value;
    t.value = "";
    if (!val) return;
    // Envoie le texte comme "keyText" (chemin Unicode existant utilisé par
    // pasteAsTyping). L'agent gère l'IME composé en un seul message.
    ws.send({ type: "input", input: { kind: "keyText", text: val } });
    recordKeyboardTextDebug("mobile:input", val);
  }
  function onMobileKbKeydown(e: KeyboardEvent) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    // Touches spéciales que `input` ne capture pas (Enter, Backspace, Tab,
    // arrows, Escape) : on les intercepte ici et on envoie le VK direct.
    // VK codes Windows officiels — l'export shared/keymap n'en a qu'un
    // sous-ensemble, on complète localement les flèches.
    const mapped: Record<string, number> = {
      Enter: VK.ENTER,
      Backspace: VK.BACK,
      Tab: VK.TAB,
      Escape: VK.ESCAPE,
      Delete: VK.DELETE,
      ArrowLeft: 0x25,
      ArrowUp: 0x26,
      ArrowRight: 0x27,
      ArrowDown: 0x28,
    };
    const vk = mapped[e.key];
    if (vk == null) return;
    e.preventDefault();
    flushMove();
    ws.send({ type: "input", input: { kind: "key", vk, down: true } });
    ws.send({ type: "input", input: { kind: "key", vk, down: false } });
    recordKeyboardDebug({ action: "mobile:key", key: e.key, code: e.code || "-", vk, event: e });
  }
  // Notification transitoire (collage de mot de passe, presse-papier...).
  const [toast, setToast] = createSignal<string | null>(null);
  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  function showToast(text: string, ms = 3000) {
    setToast(text);
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => setToast(null), ms);
  }

  // WebRTC state
  let rtcPeer: RTCPeerConnection | null = null;
  let rtcOperatorId: string | null = null;
  let lastWebRtcOfferSDP: string | null = null;
  const [webrtcStreaming, setWebrtcStreaming] = createSignal(false);
  let rtcVideoEl: HTMLVideoElement | undefined;

  // ── Plein écran + Focus in-app + Document PiP ───────────────────────────────
  const [isFullscreen, setIsFullscreen] = createSignal(false);
  const [focusMode, setFocusMode] = createSignal(false);
  const pipAvailable = () => typeof window !== "undefined" && "documentPictureInPicture" in window;
  let pipWindow: Window | null = null;
  let pipVideoEl: HTMLVideoElement | null = null;
  // pipActive : la vidéo est déportée dans la fenêtre PiP. On masque alors le
  // média de la page principale pour ne pas l'afficher en double.
  const [pipActive, setPipActive] = createSignal(false);
  // popoutOpen : une fenêtre séparée (?popout=1) reconnectée au même code est
  // ouverte. On masque la vidéo de la page d'origine pour ne pas l'avoir en
  // double (la fenêtre popout porte alors le flux).
  let popoutWindow: Window | null = null;
  let popoutPoll: ReturnType<typeof setInterval> | null = null;
  const [popoutOpen, setPopoutOpen] = createSignal(false);
  function openPopout() {
    if (popoutWindow && !popoutWindow.closed) {
      popoutWindow.focus();
      return;
    }
    const s = ws.state.sessionCode || "";
    const url = `${window.location.origin}${window.location.pathname}?popout=1${s ? `&s=${encodeURIComponent(s)}` : ""}`;
    const w = window.open(url, "rws-popout", "width=1280,height=800,popup=1");
    if (!w) {
      showToast("Le navigateur a bloqué la fenêtre séparée");
      return;
    }
    popoutWindow = w;
    setPopoutOpen(true);
    if (popoutPoll) clearInterval(popoutPoll);
    popoutPoll = setInterval(() => {
      if (!popoutWindow || popoutWindow.closed) closePopout();
    }, 800);
  }
  function closePopout() {
    if (popoutPoll) {
      clearInterval(popoutPoll);
      popoutPoll = null;
    }
    const w = popoutWindow;
    popoutWindow = null;
    setPopoutOpen(false);
    if (w && !w.closed) {
      try {
        w.close();
      } catch {
        /* ignore */
      }
    }
  }
  onCleanup(() => {
    if (popoutPoll) clearInterval(popoutPoll);
    // On NE ferme PAS la fenêtre popout au démontage du panel : elle est
    // autonome (reconnectée au même code). On nettoie juste le polling.
  });
  let viewerWrapRef: HTMLDivElement | undefined;

  // ── Multi-opérateur : curseurs distants (off par défaut) ────────────────────
  // Mode collaboratif "clic direct" : tous les opérateurs envoient leurs entrées
  // (l'arbitrage exclusif optionnel se fait via operator:activate, géré par le
  // relai). En complément, chaque opérateur peut afficher les curseurs des AUTRES
  // opérateurs en temps réel. Désactivé par défaut (préférence locale).
  const REMOTE_CURSORS_KEY = "rws.video.showRemoteCursors";
  const [showRemoteCursors, setShowRemoteCursors] = createSignal<boolean>(
    (() => {
      try {
        return localStorage.getItem(REMOTE_CURSORS_KEY) === "1";
      } catch {
        return false;
      }
    })(),
  );
  function toggleRemoteCursors() {
    const next = !showRemoteCursors();
    setShowRemoteCursors(next);
    try {
      localStorage.setItem(REMOTE_CURSORS_KEY, next ? "1" : "0");
    } catch {
      /* private mode */
    }
  }
  type RemoteCursor = { x: number; y: number; label: string; t: number };
  const [remoteCursors, setRemoteCursors] = createSignal<Record<string, RemoteCursor>>({});
  // Couleur stable par opérateur (hash → teinte HSL).
  function cursorColor(id: string): string {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
    return `hsl(${h}, 85%, 55%)`;
  }
  const remoteCursorList = () => {
    const map = remoteCursors();
    return Object.entries(map).map(([id, c]) => ({ id, ...c, color: cursorColor(id) }));
  };
  // Émission throttlée de notre position curseur (normalisée 0..1) aux autres
  // opérateurs via le relai. Léger : ~30 Hz max, uniquement si quelqu'un peut
  // l'afficher (multi-op).
  let lastCursorSentAt = 0;
  const CURSOR_SEND_MS = 33;
  function sendCursorPosition(e: MouseEvent) {
    if (!isMultiOp()) return;
    const now = Date.now();
    if (now - lastCursorSentAt < CURSOR_SEND_MS) return;
    lastCursorSentAt = now;
    const el = getSurface();
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    ws.send({ type: "operator:cursor", x, y } as never);
  }

  // ── Zone C : transport actif + crédentials TURN + ABR ──────────────────────
  // `transport` : indicateur HUD — "webrtc" tant que le chemin WebRTC porte la
  // vidéo, "wss" une fois basculé sur le flux historique. Pas d'oscillation :
  // une fois en `wss` pour la session, on y reste.
  type TransportMode = "webrtc" | "wss";
  const [transport, setTransport] = createSignal<TransportMode>("wss");
  // Vrai quand WebRTC a été tenté en premier et qu'on attend `connected`.
  const [webrtcAttempt, setWebrtcAttempt] = createSignal(false);
  // Vrai une fois le fallback WSS engagé — verrou anti-oscillation.
  let fallbackEngaged = false;
  // Timer du watchdog de connexion WebRTC (6-8 s).
  let webrtcWatchdog: ReturnType<typeof setTimeout> | null = null;
  // Cache des ICE servers + horodatage du fetch (TTL ~300 s).
  let turnIceServers: RTCIceServer[] | null = null;
  let turnFetchedAt = 0;
  const TURN_TTL_MS = 280_000; // marge sous le TTL serveur (~300 s)

  // ABR — adaptation client. `abrEnabled` : toggle UI (défaut activé).
  const ABR_KEY = "rws.abrAuto";
  function readAbrPref(): boolean {
    try {
      return localStorage.getItem(ABR_KEY) !== "0";
    } catch {
      return true;
    }
  }
  const [abrEnabled, setAbrEnabled] = createSignal(readAbrPref());
  function toggleAbr() {
    setAbrEnabled((v) => {
      const next = !v;
      try {
        localStorage.setItem(ABR_KEY, next ? "1" : "0");
      } catch {
        // localStorage indisponible
      }
      return next;
    });
  }
  // Palier ABR courant (index dans ABR_LADDER, 0 = meilleur).
  const [abrRung, setAbrRung] = createSignal(0);
  let abrStatsTimer: ReturnType<typeof setInterval> | null = null;
  let abrLastChangeAt = 0; // cooldown entre changements
  let abrBadTicks = 0; // ticks consécutifs de réseau dégradé (descente rapide)
  let abrGoodSince = 0; // début de la fenêtre stable (remontée lente)
  // getStats — état précédent pour calculer les deltas de perte de paquets.
  let prevPacketsLost = 0;
  let prevPacketsReceived = 0;
  // Détecteur de stall média : si WebRTC est `connected` mais que plus aucune
  // frame ne se décode, la PeerConnection ne passe pas `failed` → le watchdog
  // ne se déclenche pas et l'opérateur reste sur une image gelée. On compte
  // les ticks getStats() sans progression de `framesDecoded` ; au-delà du
  // seuil → fallback WSS.
  let prevFramesDecoded = -1;
  let webrtcStallTicks = 0;
  const WEBRTC_STALL_TICKS_MAX = 3; // ~3 ticks getStats (~6 s) sans frame
  const ABR_COOLDOWN_MS = 3000;
  const ABR_UP_WINDOW_MS = 12_000; // fenêtre stable avant remontée

  // DOM refs
  let videoEl: HTMLVideoElement | undefined;
  let canvasEl: HTMLCanvasElement | undefined;
  let surfaceEl: HTMLDivElement | undefined;

  // MediaSource state
  let mediaSource: MediaSource | null = null;
  let sourceBuffer: SourceBuffer | null = null;
  let pendingChunks: Uint8Array[] = [];
  // Borne anti-fuite : si le SourceBuffer (append asynchrone) ne suit pas le
  // débit d'arrivée, on jette les chunks les plus anciens (drop-oldest) plutôt
  // que de laisser la file croître sans limite.
  const MAX_PENDING = 120;
  let sbBusy = false;

  // WebCodecs state
  let videoDecoder: VideoDecoder | null = null;
  let waitingForKeyframe = true;
  let decoderCodec = "avc1.4D4029";
  let decoderRes = { w: 1280, h: 720 };
  let decoderHasDescription = false;
  let cachedH264Sps: Uint8Array | null = null;
  let cachedH264Pps: Uint8Array | null = null;

  // VSync-aligned render mailbox: the decoder may emit frames off the display
  // refresh cadence. Instead of drawing synchronously in the decode callback
  // (tearing, wasted draws), we keep only the most recent decoded frame and
  // paint it once per requestAnimationFrame tick. A 1-slot mailbox — no jitter
  // delay added, stale frames are dropped, drawing is aligned to the refresh.
  let latestFrame: VideoFrame | null = null;
  let renderRafHandle = 0;
  let browserFrameSeq = 0;
  let browserFrameDrawnSeq = 0;
  // Contexte 2D mis en cache — `getContext` par frame est superflu.
  // `desynchronized` réduit la latence d'affichage (bypass de la sync de
  // composition, pertinent pour un bureau distant interactif) ; `alpha:false`
  // évite une composition alpha inutile (la vidéo est opaque).
  let canvasCtx: CanvasRenderingContext2D | null = null;

  // WS-C — décodage + rendu déportés dans un Web Worker (OffscreenCanvas).
  // Décidé au start du flux annexb selon le flag `videoWorkerDecode` et le
  // support. Quand actif : le canvas est transféré au worker (plus dessinable
  // côté main → exclusivité garantie), les bytes reçus sont routés au worker au
  // lieu de processVideoFrame local, et le renderLoop main-thread ne tourne pas.
  let decodeWorker: Worker | null = null;
  // WS-C — signature (codec|résolution) du dernier (re)init envoyé au worker.
  // Évite de reset le décodeur quand un `video:start` redondant (même codec/
  // résolution) arrive — fréquent lors d'une rafale de (re)abonnement.
  let workerInitSig = "";

  function renderLoop() {
    renderRafHandle = requestAnimationFrame(renderLoop);
    const frame = latestFrame;
    if (!frame) return;
    latestFrame = null;
    if (canvasEl) {
      if (!canvasCtx) {
        canvasCtx = canvasEl.getContext("2d", { desynchronized: true, alpha: false });
      }
      if (canvasCtx) {
        if (canvasEl.width !== frame.displayWidth) canvasEl.width = frame.displayWidth;
        if (canvasEl.height !== frame.displayHeight) canvasEl.height = frame.displayHeight;
        canvasCtx.drawImage(frame, 0, 0);
      }
    }
    frame.close();
  }

  function stopRenderLoop() {
    if (renderRafHandle) {
      cancelAnimationFrame(renderRafHandle);
      renderRafHandle = 0;
    }
    if (latestFrame) {
      latestFrame.close();
      latestFrame = null;
    }
    canvasCtx = null;
  }

  // WS-C — vrai si le worker de décodage est l'actif (le main ne dessine plus).
  function workerActive(): boolean {
    return decodeWorker !== null;
  }

  // Démarre le worker de décodage si flag ON + support + canvas transférable.
  // Retourne true si le worker prend la main (canvas transféré) ; false →
  // l'appelant continue sur le chemin thread-principal (fallback strict).
  function tryStartDecodeWorker(codecStr?: string, res?: { w: number; h: number } | null): boolean {
    if (!videoWorkerDecodeFlag()) return false;
    if (!isOffscreenWorkerDecodeSupported()) return false;
    if (!canvasEl || typeof canvasEl.transferControlToOffscreen !== "function") return false;

    let worker: Worker;
    try {
      // Vite bundle ce worker en module (cf. `new URL(..., import.meta.url)`).
      worker = new Worker(new URL("./video-decode.worker.ts", import.meta.url), { type: "module" });
    } catch (e) {
      // Échec de création (CSP, ancien navigateur) → fallback thread-principal.
      console.warn("[video] worker de décodage indisponible, repli thread-principal", e);
      return false;
    }

    let offscreen: OffscreenCanvas;
    try {
      offscreen = canvasEl.transferControlToOffscreen();
    } catch (e) {
      // transferControlToOffscreen peut échouer si le canvas a déjà un contexte
      // 2D (chemin main joué juste avant). On garde le canvas pour le main.
      console.warn("[video] transferControlToOffscreen impossible, repli thread-principal", e);
      worker.terminate();
      return false;
    }

    decodeWorker = worker;
    worker.onmessage = (ev: MessageEvent<VideoWorkerOutbound>) => {
      const msg = ev.data;
      if (!msg) return;
      if (msg.type === "frame") {
        // Parité métriques : recordFrameMetric() est normalement appelé par
        // frame reçue côté main. Le worker poste 1 message par frame reçue ; on
        // alimente ici les mêmes signals (fps / intervalle / bitrate HUD).
        recordFrameMetric();
        bitrateBytesAccum += msg.bytes;
      } else if (msg.type === "metrics") {
        // WS-D — métriques fines mesurées DANS le worker (decode-time, drops,
        // queue). N'arrive que si fineMetrics ON (le worker ne poste qu'alors).
        setDecodeMs(msg.decodeMs);
        setDroppedFrames(msg.droppedFrames);
        setDecodeQueueSize(msg.decodeQueueSize);
      } else if (msg.type === "error") {
        setError(msg.message);
      }
    };

    // OffscreenCanvas transféré (transferable) au worker avec la config initiale.
    // WS-D — on propage le flag fineMetrics : le worker n'instrumente/poste que
    // si ON (sinon aucun calcul ni message `metrics`).
    worker.postMessage(
      {
        type: "init",
        canvas: offscreen,
        codec: codecStr,
        resolution: res ?? null,
        fineMetrics,
        webgpu: videoWebGpuRenderFlag(),
      },
      [offscreen],
    );
    workerInitSig = `${codecStr ?? ""}|${res ? `${res.w}x${res.h}` : ""}`;
    return true;
  }

  function teardownDecodeWorker() {
    if (decodeWorker) {
      try {
        decodeWorker.postMessage({ type: "dispose" });
      } catch {
        // ignore
      }
      decodeWorker.terminate();
      decodeWorker = null;
    }
  }

  function isH264Codec(): boolean {
    return decoderCodec.startsWith("avc1") || decoderCodec.startsWith("avc3");
  }

  function selectedPreset(): VideoPreset {
    return findVideoPreset(selectedPresetId());
  }

  function isPresetSupported(preset: VideoPreset): boolean {
    if (!isPresetAgentSupported(preset, ws.state.agentFeatures)) return false;
    if (preset.transport === "webrtc") return typeof RTCPeerConnection !== "undefined";
    if (preset.codec === "h264") return isWebCodecsSupported();
    return browserCodecSupport()[preset.codec] === true;
  }

  createEffect(() => {
    if (!isWebCodecsSupported()) {
      setBrowserCodecSupport({});
      return;
    }

    let cancelled = false;
    void Promise.all([
      VideoDecoder.isConfigSupported({
        codec: "hev1.1.6.L150.90",
        codedWidth: 1280,
        codedHeight: 720,
      })
        .then((r) => ["h265", r.supported] as const)
        .catch(() => ["h265", false] as const),
      VideoDecoder.isConfigSupported({
        codec: "av01.0.08M.08",
        codedWidth: 1280,
        codedHeight: 720,
      })
        .then((r) => ["av1", r.supported] as const)
        .catch(() => ["av1", false] as const),
      // WS-N — 4:4:4 (avc1 High 4:4:4 Predictive, profil 0xF4=244). prefer-hardware :
      // privilégie le décodage matériel. NB : WebCodecs n'expose pas de garantie HW
      // stricte ("require-hardware" n'existe pas dans le standard) → ce gate filtre
      // le SUPPORT du 4:4:4 ; il reste OFF par défaut (POC : risque decode CPU si le
      // navigateur ne décode le 4:4:4 qu'en software).
      VideoDecoder.isConfigSupported({
        codec: "avc1.F40028",
        codedWidth: 1280,
        codedHeight: 720,
        hardwareAcceleration: "prefer-hardware",
      })
        .then((r) => ["chroma444", r.supported] as const)
        .catch(() => ["chroma444", false] as const),
    ]).then((entries) => {
      if (cancelled) return;
      setBrowserCodecSupport(
        entries.reduce<Record<string, boolean>>((acc, [codecId, supported]) => {
          acc[codecId] = supported === true;
          return acc;
        }, {}),
      );
    });

    onCleanup(() => {
      cancelled = true;
    });
  });

  // WS-B / WS-I — sélection initiale du preset par défaut.
  //
  // WS-B : quand `videoH265Default` est ON ET que l'agent annonce `codecH265` ET
  // que le navigateur décode HEVC, on bascule le preset par défaut sur H265.
  //
  // WS-I : quand `videoCodecPolicy` est ON (prioritaire sur WS-B), on choisit le
  // codec OPTIMAL selon le profil réseau+contenu via `pickCodecForProfile`, qui
  // cascade vers le 1er codec supporté (agent + navigateur) et, en dernier
  // ressort, h264. Le profil de transport est dérivé du preset sélectionné
  // (webrtc → "webrtc", sinon "wss" car chemin relayé) ; le contenu est "text"
  // (cas d'usage bureautique dominant ; le profil "mixed" reste réservé à une
  // détection ultérieure).
  //
  // Dans les DEUX cas : évalué UNE seule fois, et seulement si l'opérateur n'a
  // pas déjà choisi manuellement. Les deux flags OFF (défaut) ⇒ on retombe sur
  // `preferredDefaultVideoPreset(false, …)` ⇒ defaultVideoPreset() ⇒ aucun
  // changement vs aujourd'hui (h264).
  createEffect(() => {
    const codecPolicyFlag = videoCodecPolicyFlag();
    const h265Flag = videoH265DefaultFlag();
    const features = ws.state.agentFeatures;
    const browserH265 = browserCodecSupport().h265 === true;
    const browserAv1 = browserCodecSupport().av1 === true;
    // Réveille l'effet sur ces dépendances. Si les deux flags sont OFF, on ne
    // fait rien (et on n'évalue qu'une fois pour ne pas verrouiller
    // prématurément si les capacités arrivent plus tard).
    if (!codecPolicyFlag && !h265Flag) return;
    if (h265DefaultEvaluated || presetTouchedByUser) return;
    // On attend que les capacités agent ET navigateur soient connues avant de
    // décider (sinon on figerait h264 sur un état transitoire).
    if (!features) return;
    let preferred: VideoPreset;
    if (codecPolicyFlag) {
      const profile: VideoCodecProfile = {
        transport: untrack(selectedPreset).transport === "webrtc" ? "webrtc" : "wss",
        content: untrack(() => findQualityProfile(selectedQualityId()).content) ?? "text",
      };
      preferred = pickCodecForProfile(profile, features, { h265: browserH265, av1: browserAv1 });
    } else {
      preferred = preferredDefaultVideoPreset(h265Flag, features, browserH265);
    }
    h265DefaultEvaluated = true;
    if (preferred.id !== defaultVideoPreset().id && preferred.id !== untrack(selectedPresetId)) {
      setSelectedPresetId(preferred.id);
      setH265DefaultApplied(true);
      console.info(
        `[video][${codecPolicyFlag ? "ws-i" : "ws-b"}] codec par défaut sélectionné (agent+navigateur):`,
        preferred.id,
      );
    }
  });

  function flushPending() {
    if (sbBusy || !sourceBuffer || pendingChunks.length === 0) return;
    if (sourceBuffer.updating) return;
    const chunk = pendingChunks.shift()!;
    sbBusy = true;
    try {
      sourceBuffer.appendBuffer(chunk.buffer as ArrayBuffer);
    } catch (e) {
      sbBusy = false;
      setError(`appendBuffer failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  function initMediaSource(codecStr: string) {
    if (!videoEl) return;
    if (!isMseSupported()) {
      setError("MediaSource API non disponible dans ce navigateur.");
      return;
    }

    teardownMediaSource();

    mediaSource = new MediaSource();
    videoEl.src = URL.createObjectURL(mediaSource);

    mediaSource.addEventListener("sourceopen", () => {
      if (!mediaSource) return;
      const mimeCodec = `video/mp4; codecs="${codecStr || "avc1.640028"}"`;
      if (!MediaSource.isTypeSupported(mimeCodec)) {
        setError(`Codec non supporté: ${mimeCodec}`);
        return;
      }
      try {
        sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
        sourceBuffer.mode = "segments";
        sourceBuffer.addEventListener("updateend", () => {
          sbBusy = false;
          flushPending();
        });
        sourceBuffer.addEventListener("error", () => {
          setError("SourceBuffer error");
        });
        flushPending();
      } catch (e) {
        setError(`addSourceBuffer failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    });

    mediaSource.addEventListener("sourceended", () => {
      setStreaming(false);
    });

    videoEl.play().catch(() => {
      // Autoplay may be blocked
    });
  }

  function teardownMediaSource() {
    pendingChunks = [];
    sbBusy = false;
    if (sourceBuffer) {
      try {
        sourceBuffer.abort();
      } catch {
        // ignore
      }
      sourceBuffer = null;
    }
    if (mediaSource && mediaSource.readyState === "open") {
      try {
        mediaSource.endOfStream();
      } catch {
        // ignore
      }
    }
    if (videoEl && videoEl.src.startsWith("blob:")) {
      URL.revokeObjectURL(videoEl.src);
      videoEl.src = "";
    }
    mediaSource = null;
  }

  function makeDecoderInit(): VideoDecoderInit {
    return {
      output(frame) {
        if (fineMetrics) {
          // WS-D — decode-time : apparie cet output au plus ancien decode()
          // (ordre DTS = ordre d'arrivée), puis EMA légère (alpha 0.2).
          const sentAt = decodeSentTimestamps.shift();
          if (sentAt !== undefined) {
            const dt = performance.now() - sentAt;
            decodeMsEma = decodeMsEma === 0 ? dt : decodeMsEma * 0.8 + dt * 0.2;
          }
        }
        // Hand the frame to the VSync render mailbox. Drop (close) any prior
        // undrawn frame so only the freshest one is ever painted.
        if (latestFrame) {
          if (fineMetrics) droppedFramesAccum++;
          latestFrame.close();
        }
        latestFrame = frame;
      },
      error(e) {
        // Reset fully so the next keyframe re-creates and re-configures the decoder.
        waitingForKeyframe = true;
        decoderHasDescription = false;
        videoDecoder = null;
        setError(`VideoDecoder: ${e.message}`);
      },
    };
  }

  function configureDecoder(description?: Uint8Array) {
    if (!videoDecoder || videoDecoder.state === "closed") return;
    const cfg: VideoDecoderConfig = {
      codec: decoderCodec,
      codedWidth: decoderRes.w,
      codedHeight: decoderRes.h,
      optimizeForLatency: true,
      // Préfère le décodage matériel (moins de CPU opérateur) ; "prefer" et
      // non "require" → repli logiciel automatique si indisponible.
      hardwareAcceleration: "prefer-hardware",
    };
    if (description) {
      cfg.description = description;
      decoderHasDescription = true;
    }
    videoDecoder.configure(cfg);
  }

  function initWebCodecs(codecStr?: string, res?: { w: number; h: number } | null) {
    if (!isWebCodecsSupported()) {
      setError("WebCodecs API non disponible dans ce navigateur.");
      return;
    }
    // WS-C — worker déjà actif : son OffscreenCanvas a été transféré et
    // `transferControlToOffscreen()` est IRRÉVERSIBLE. Le recréer (teardown +
    // 2e transfert) lève une exception → repli main-thread sur un canvas
    // détaché → ÉCRAN NOIR. On reconfigure donc le décodeur DANS le worker
    // existant (sans le terminer). Les `video:start` redondants (même codec/
    // résolution, typiques d'une rafale de réabonnement) sont ignorés pour ne
    // pas reset le flux à chaque message.
    if (workerActive()) {
      const sig = `${codecStr ?? ""}|${res ? `${res.w}x${res.h}` : ""}`;
      if (sig !== workerInitSig) {
        workerInitSig = sig;
        decodeWorker!.postMessage({ type: "reinit", codec: codecStr, resolution: res ?? null });
      }
      return;
    }
    teardownWebCodecs();

    // WS-C — si le flag est ON et le support présent, on déporte décodage +
    // rendu dans le worker (OffscreenCanvas transféré). Le worker possède son
    // propre VideoDecoder et son propre renderLoop ; on n'instancie alors NI le
    // décodeur main NI le renderLoop main, et processVideoFrame route les bytes
    // vers le worker. Décision figée au start (pas de bascule en vol) pour
    // respecter l'exclusivité du canvas transféré.
    if (tryStartDecodeWorker(codecStr, res)) return;

    waitingForKeyframe = true;
    decoderHasDescription = false;
    cachedH264Sps = null;
    cachedH264Pps = null;

    if (!codecStr || codecStr === "h264") {
      // Use AVCC (`avc1` + description) because Chrome/macOS VideoToolbox is
      // unreliable with Annex-B `avc3` despite accepting the config.
      decoderCodec = "avc1.4D4029";
    } else if (codecStr === "h265" || codecStr === "hevc") {
      // HEVC Main Profile Level 5.0 — hev1.* = in-band params (Annex B), no description needed
      decoderCodec = "hev1.1.6.L150.90";
    } else if (codecStr === "av1") {
      decoderCodec = "av01.0.08M.08";
    } else {
      decoderCodec = codecStr;
    }
    decoderRes = res ? { w: res.w, h: res.h } : { w: 1280, h: 720 };

    videoDecoder = new VideoDecoder(makeDecoderInit());
    if (!isH264Codec()) configureDecoder();

    // Start the VSync-aligned render loop (idempotent — teardown cancels it).
    if (!renderRafHandle) renderRafHandle = requestAnimationFrame(renderLoop);
  }

  function teardownWebCodecs() {
    // WS-C — NE PAS terminer le worker ici. `transferControlToOffscreen()` est
    // irréversible : le canvas DOM ne peut être peint QUE par ce worker tant
    // qu'il vit. Le terminer le rendrait définitivement noir (ni worker ni main
    // ne pourraient re-dessiner). Le worker survit aux re-init de flux
    // (initWebCodecs lui envoie `reinit`) ; il n'est terminé qu'au démontage du
    // composant (onCleanup). Sur le chemin main-thread, aucun worker n'existe.
    waitingForKeyframe = true;
    stopRenderLoop();
    cachedH264Sps = null;
    cachedH264Pps = null;
    if (videoDecoder && videoDecoder.state !== "closed") {
      try {
        videoDecoder.close();
      } catch {
        // ignore
      }
    }
    videoDecoder = null;
  }

  function handleStart(rawMsg: unknown) {
    // relay wraps: {type:"video:start", data:{codec,mode,resolution,fps,...}}
    const msg = unwrapVideoPayload(rawMsg) as Partial<VideoStartMsg>;
    setError(null);
    if (streaming()) {
      surfaceEl?.focus();
      setControlFocused(true);
      setFollowMouseValue(followMouse());
    }
    setResolution(msg.resolution ?? null);
    setDesktopRes(msg.desktopResolution ?? msg.resolution ?? null);
    // Cadrage confirmé par l'agent : sourceRect = portion réellement capturée
    // (mode 1:1/crop). framingMode explicite prime ; à défaut, la présence d'un
    // sourceRect implique "real". L'agent fait foi (la bascule peut échouer).
    const sr =
      msg.sourceRect && msg.sourceRect.w > 0 && msg.sourceRect.h > 0
        ? { x: msg.sourceRect.x, y: msg.sourceRect.y, w: msg.sourceRect.w, h: msg.sourceRect.h }
        : null;
    setSourceRect(sr);
    setFramingMode(msg.framingMode ?? (sr ? "real" : "fit"));
    if (msg.sessionKind === "console" || msg.sessionKind === "rdp") setSessionKind(msg.sessionKind);
    setFps(msg.fps ?? 0);
    setCodec(msg.codec ?? "");
    // WS-B — télémétrie codec : compare le codec demandé (requestedCodec) au
    // codec effectif renvoyé par l'agent (effectiveCodec). Une divergence
    // signale la cascade encodeur (ex. H265 KO → H264) ; le client reconfigure
    // déjà son décodeur sur `msg.codec` via initWebCodecs ci-dessous (inchangé).
    {
      const requested = untrack(requestedCodec);
      const effective = msg.codec ?? "";
      if (requested && effective && requested !== effective) {
        console.info(
          `[video][ws-b] codec demandé=${requested} → effectif=${effective} (cascade agent). isConfigSupported:`,
          { h265: browserCodecSupport().h265 === true, av1: browserCodecSupport().av1 === true },
        );
      } else if (effective) {
        console.info(`[video][ws-b] codec effectif=${effective} (demandé=${requested || "?"})`);
      }
    }
    const m: VideoMode = msg.mode === "fmp4" ? "fmp4" : "annexb";
    setMode(m);
    setStreaming(true);
    resetFrameMetrics();
    setBarVisible(true);
    armBarIdleTimer();

    if (m === "annexb") {
      initWebCodecs(msg.codec, msg.resolution);
    } else {
      initMediaSource(msg.codec ?? "avc1.640028");
    }

    // Pas d'ABR automatique en WSS : les changements de palier sont trop
    // coûteux sur ce transport (reconfiguration encodeur côté Helper → pics
    // CPU / pertes de frames aux passages). En WSS la qualité reste celle
    // choisie par l'opérateur. L'ABR automatique n'opère qu'en WebRTC
    // (bitrate seul, hot-change), démarré à l'état `connected`.
  }

  // Shared video frame processing for both JSON and binary paths.
  function processVideoFrame(ts: number, isKey: boolean, bytes: Uint8Array) {
    // WS-C — chemin worker : décodage + rendu déportés. On transfère les bytes
    // (ArrayBuffer dédié, zéro-copie côté main) et on RETOURNE : ni décodage ni
    // recordFrameMetric ici — le worker poste les métriques par frame (cf.
    // onmessage "frame"). Garde mode annexb : le worker n'existe qu'en annexb.
    if (workerActive() && mode() === "annexb") {
      // Copie du segment dans un buffer propre pour le rendre transférable (la
      // vue `bytes` partage le buffer WS, non transférable tel quel).
      const buf = bytes.slice().buffer;
      decodeWorker!.postMessage({ type: "chunk", ts, isKey, bytes: buf }, [buf]);
      return;
    }

    recordFrameMetric();
    bitrateBytesAccum += bytes.length;
    if (mode() === "fmp4") {
      if (pendingChunks.length >= MAX_PENDING) pendingChunks.shift();
      pendingChunks.push(bytes);
      flushPending();
      return;
    }

    // After a decoder error the error callback nulls out videoDecoder and resets
    // decoderHasDescription. Recreate on the next keyframe so the stream self-heals.
    if (isKey && !videoDecoder) {
      decoderHasDescription = false;
      videoDecoder = new VideoDecoder(makeDecoderInit());
    }

    let decodeBytes = bytes;

    if (!videoDecoder || videoDecoder.state === "closed") return;
    if (waitingForKeyframe && !isKey) return;

    // Scan NALU O(n) + allocs réservé aux keyframes : SPS/PPS n'apparaissent
    // qu'en tête de keyframe, inutile de scanner chaque delta. Placé APRÈS les
    // early-returns pour ne rien exécuter quand on jette la frame.
    if (isH264Codec() && isKey) {
      const sets = extractH264ParameterSets(bytes);
      if (sets.sps) cachedH264Sps = sets.sps;
      if (sets.pps) cachedH264Pps = sets.pps;
    }

    if (isH264Codec()) {
      if (isKey && !decoderHasDescription) {
        const avcConfig =
          extractAvcDecoderConfig(bytes) ??
          (cachedH264Sps && cachedH264Pps ? buildAvcDecoderConfig(cachedH264Sps, cachedH264Pps) : null);
        if (!avcConfig) {
          waitingForKeyframe = true;
          setError("Keyframe H264 sans SPS/PPS: attente des paramètres vidéo.");
          return;
        }
        decoderCodec = avcConfig.codec;
        configureDecoder(avcConfig.description);
        setError(null);
      }
      if (!decoderHasDescription) return;
      decodeBytes = annexBToAvcc(bytes);
      if (decodeBytes.length === 0) return;
    }

    if (isKey) waitingForKeyframe = false;

    // Decode in arrival order (= DTS order). Using performance.now() as timestamp
    // guarantees monotonically increasing values required by WebCodecs, regardless of
    // PTS ordering from the encoder (B-frames have lower PTS than the preceding P-frame).
    try {
      if (fineMetrics) {
        // WS-D — borne la file (un output droppé/perdu ne renverra jamais son ts).
        if (decodeSentTimestamps.length > 240) decodeSentTimestamps.shift();
        decodeSentTimestamps.push(performance.now());
      }
      videoDecoder.decode(
        new EncodedVideoChunk({
          type: isKey ? "key" : "delta",
          timestamp: performance.now() * 1000,
          data: decodeBytes,
        }),
      );
    } catch {
      // Decoder in bad state — reset fully so next keyframe starts fresh.
      waitingForKeyframe = true;
      decoderHasDescription = false;
      if (fineMetrics) decodeSentTimestamps = [];
      try {
        videoDecoder?.close();
      } catch {
        // ignore close errors
      }
      videoDecoder = null;
    }
  }

  function handleChunk(rawMsg: unknown) {
    if (!streaming()) return;
    // relay wraps: {type:"video:chunk", data:{data:"b64", ts:..., keyframe:...}}
    const inner = unwrapVideoPayload(rawMsg) as Record<string, unknown>;
    const b64: string = typeof inner.data === "string" ? inner.data : typeof inner.Data === "string" ? inner.Data : "";
    if (!b64) return;
    const bytes = base64ToUint8Array(b64);
    const rawTs = inner.ts ?? inner.Ts;
    const ts = typeof rawTs === "number" ? rawTs : 0;
    const isKey = !!(inner.keyframe ?? inner.Keyframe);
    processVideoFrame(ts, isKey, bytes);
  }

  function handleBrowserFrame(rawMsg: unknown) {
    const msg = unwrapVideoPayload(rawMsg) as BrowserFrameMsg;
    if (typeof msg.data !== "string" || !msg.data.startsWith("data:image/")) return;

    const encodedWidth = Math.max(1, Math.round(Number(msg.encodedWidth || msg.width || 0)));
    const encodedHeight = Math.max(1, Math.round(Number(msg.encodedHeight || msg.height || 0)));
    const sourceWidth = Math.max(encodedWidth, Math.round(Number(msg.width || encodedWidth)));
    const sourceHeight = Math.max(encodedHeight, Math.round(Number(msg.height || encodedHeight)));

    if (!streaming() || mode() !== "annexb") {
      teardownMediaSource();
      teardownWebCodecs();
      resetFrameMetrics();
      setMode("annexb");
      setStreaming(true);
      setBarVisible(true);
      armBarIdleTimer();
      surfaceEl?.focus();
      setControlFocused(true);
    }

    setResolution({ w: encodedWidth, h: encodedHeight });
    setDesktopRes({ w: sourceWidth, h: sourceHeight });
    setCodec("browser-jpeg");
    bitrateBytesAccum += Math.ceil((msg.data.length * 3) / 4);

    const seq = ++browserFrameSeq;
    const img = new Image();
    img.onload = () => {
      if (seq < browserFrameDrawnSeq) return;
      // WS-C — si le worker détient le canvas (transferControlToOffscreen est
      // irréversible), on ne peut plus dessiner côté main : on décode en
      // ImageBitmap et on le transfère au worker pour qu'il peigne.
      if (workerActive()) {
        browserFrameDrawnSeq = seq;
        void createImageBitmap(img).then((bmp) => {
          if (!decodeWorker) {
            bmp.close();
            return;
          }
          decodeWorker.postMessage({ type: "bitmap", bitmap: bmp, width: encodedWidth, height: encodedHeight }, [bmp]);
          recordFrameMetric();
        });
        return;
      }
      if (!canvasEl) return;
      browserFrameDrawnSeq = seq;
      if (!canvasCtx) canvasCtx = canvasEl.getContext("2d", { desynchronized: true, alpha: false });
      if (!canvasCtx) return;
      if (canvasEl.width !== encodedWidth) canvasEl.width = encodedWidth;
      if (canvasEl.height !== encodedHeight) canvasEl.height = encodedHeight;
      canvasCtx.drawImage(img, 0, 0, encodedWidth, encodedHeight);
      recordFrameMetric();
    };
    img.src = msg.data;
  }

  function handleNaluChunk(msg: VideoNaluChunkMsg) {
    if (!streaming() || mode() !== "annexb") return;
    // WS-C — quand le worker est actif, route via processVideoFrame (qui gère le
    // routage worker + l'AVCC/SPS H264). Ces chunks sont traités en keyframes.
    if (workerActive()) {
      const bytes = base64ToUint8Array(msg.data);
      processVideoFrame(0, true, bytes);
      return;
    }
    if (!videoDecoder) return;
    if (videoDecoder.state === "closed") return;
    recordFrameMetric();
    const bytes = base64ToUint8Array(msg.data);
    const chunk = new EncodedVideoChunk({
      type: "key",
      timestamp: performance.now() * 1000,
      data: bytes,
    });
    try {
      videoDecoder.decode(chunk);
    } catch {
      // ignore decode errors on individual frames
    }
  }

  function handleEnd(_msg: VideoEndMsg) {
    setStreaming(false);
    stopAbrStats();
    resetFramingState();
    if (mode() === "fmp4") {
      if (sourceBuffer && !sourceBuffer.updating && mediaSource?.readyState === "open") {
        try {
          mediaSource!.endOfStream();
        } catch {
          // ignore
        }
      }
    } else if (mode() === "annexb") {
      teardownWebCodecs();
    }
  }

  function handleModeActive(msg: VideoModeActiveMsg) {
    setMode(msg.mode);
  }

  function liveMetricText(value: unknown): string | null {
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
    return trimOrNull(value);
  }

  function handleLiveVideoMetrics(msg: VideoMetricsLiveMsg) {
    const metrics = msg.metrics ?? {};
    if (typeof metrics.bufferedAmount === "number" && Number.isFinite(metrics.bufferedAmount)) {
      setLiveWsBufferedBytes(Math.max(0, metrics.bufferedAmount));
    }
    if (typeof metrics.decodeMs === "number" && Number.isFinite(metrics.decodeMs)) {
      setDecodeMs(Math.round(metrics.decodeMs * 10) / 10);
    }
    if (typeof metrics.droppedFrames === "number" && Number.isFinite(metrics.droppedFrames)) {
      setDroppedFrames(Math.max(0, Math.floor(metrics.droppedFrames)));
    }
    if (typeof metrics.decodeQueueSize === "number" && Number.isFinite(metrics.decodeQueueSize)) {
      setDecodeQueueSize(Math.max(0, Math.floor(metrics.decodeQueueSize)));
    }
    setLiveMetricsAt(Date.now());

    const host = msg.host ?? {};
    const os = liveMetricText(host.os);
    const ip = liveMetricText(host.ip);
    const vlan = liveMetricText(host.vlan);
    if (os || ip || vlan) {
      setLiveHost((prev) => ({
        os: os ?? prev.os,
        ip: ip ?? prev.ip,
        vlan: vlan ?? prev.vlan,
      }));
    }
  }

  // Subscribe to WS messages
  const offStart = ws.on(
    (m) => m.type === "video:start",
    (m) => untrack(() => handleStart(m)),
  );
  const offChunk = ws.on(
    (m) => m.type === "video:chunk",
    (m) => untrack(() => handleChunk(m)),
  );
  const offBrowserFrame = ws.on(
    (m) => m.type === "frame",
    (m) => untrack(() => handleBrowserFrame(m)),
  );
  const offEnd = ws.on(
    (m) => m.type === "video:end",
    (m) => untrack(() => handleEnd(m as unknown as VideoEndMsg)),
  );
  const offNaluStart = ws.on(
    (m) => m.type === "video:nalu:start",
    () => {
      /* handled via nalu:chunk */
    },
  );
  const offNaluChunk = ws.on(
    (m) => m.type === "video:nalu:chunk",
    (m) => untrack(() => handleNaluChunk(m as unknown as VideoNaluChunkMsg)),
  );
  const offNaluEnd = ws.on(
    (m) => m.type === "video:nalu:end",
    () => {
      if (untrack(() => streaming() && mode() === "annexb")) {
        setStreaming(false);
        teardownWebCodecs();
      }
    },
  );
  const offModeActive = ws.on(
    (m) => m.type === "video:mode_active",
    (m) => handleModeActive(m as unknown as VideoModeActiveMsg),
  );
  // Télémétrie hôte légère (push 5s depuis le Helper) : CPU du process vidéo +
  // résolution source. Sert d'info HUD pour diagnostiquer qualité/étirement.
  const offTelemetry = ws.on(
    (m) => m.type === "video:telemetry",
    // eslint-disable-next-line solid/reactivity -- handler ws.on
    (m) => {
      const d = unwrapVideoPayload(m) as { helperCpuPct?: number; captureW?: number; captureH?: number };
      if (typeof d.helperCpuPct === "number") setHelperCpu(Math.round(d.helperCpuPct * 10) / 10);
      if (d.captureW && d.captureH && !desktopRes()) setDesktopRes({ w: d.captureW, h: d.captureH });
    },
  );
  const offLiveMetrics = ws.on(
    (m) => m.type === "video:metrics:live",
    (m) => handleLiveVideoMetrics(m as unknown as VideoMetricsLiveMsg),
  );
  const offVideoError = ws.on(
    (m) => m.type === "video:error",
    // eslint-disable-next-line solid/reactivity -- handler ws.on
    (m) => {
      const d = m as unknown as { code?: string; message?: string };
      const message =
        d.message ||
        (d.code === "interactive_login_required"
          ? "Session interactive Windows requise avant de demarrer le flux."
          : "Erreur video distante.");
      setStreaming(false);
      setWebrtcStreaming(false);
      // L'erreur est un chemin de sortie terminal du « connecting » : sans ce
      // clear, l'overlay .viewer-connecting-overlay (z-index:30, inset:0,
      // opaque) reste posé et intercepte tous les clics sur l'IdleView en
      // dessous (badges WSS / « Connecter » inopérants) jusqu'au timeout 15 s.
      // À placer AVANT toute branche de retour anticipé (interactive_login_required
      // ouvre un dialogue par-dessus : l'overlay ne doit pas lui survivre derrière).
      clearConnecting();
      // Chemin RÉACTIF du login non assisté : l'agent est LA source de vérité
      // de l'état de session Windows. Le chemin proactif (flag noUserLoggedIn
      // du session:state, évalué AVANT l'envoi du start) peut être stale ou
      // perdu en multi-op — sans ce handler, l'erreur était avalée en toast et
      // l'opérateur revenait silencieusement à l'écran de choix au lieu de
      // voir le dialogue de credentials.
      if (d.code === "interactive_login_required") {
        if (ws.state.hostPolicy?.unattendedAccessAllowed === true && !unattendedActiveId()) {
          // ORDRE IMPORTANT : ouvrir le dialogue AVANT de ré-armer la machine
          // de lancement — l'effet pendingWsLaunch se déclenche dès le set et
          // s'early-return sur `unattendedDialogOpen()` ; dans l'autre sens il
          // relancerait un startCapture (2e video:error parasite) avant que le
          // dialogue ne soit ouvert. Après `unattended:result` ok, le dialogue
          // se ferme et l'effet reprend → startCapture.
          setError("Session Windows fermee. Saisissez les credentials admin pour ouvrir une session.");
          setUnattendedDialogOpen(true);
          if (!pendingWsLaunch()) setPendingWsLaunch({ sessionCode: ws.state.sessionCode, accessRequested: true });
          return;
        }
        // PROPOSER, pas forcer : login requis et accès non assisté indisponible.
        // On NE bascule PAS d'office en RDP — on propose (bannière) + message.
        const rdpSession = activeRdpSessionRequiringLogin();
        if (rdpSession) {
          suggestRdpLoginForWs(rdpSession);
          setError(
            `Session Windows verrouillee — login requis. Session RDP active ${rdpSessionLabel(rdpSession)} disponible : vous pouvez la rejoindre en RDP.`,
          );
          return;
        }
      }
      setError(message);
      showToast(message, 6000);
    },
  );
  const offSasResult = ws.on(
    (m) => m.type === "sas:result",
    (m) => {
      const d = m as unknown as { ok?: boolean; sent?: boolean; ackReceived?: boolean; error?: string };
      if (d.ok || d.sent) {
        showToast(d.ackReceived === false ? "Ctrl+Alt+Suppr transmis, accuse non recu" : "Ctrl+Alt+Suppr transmis");
        return;
      }
      showToast(`Ctrl+Alt+Suppr non transmis${d.error ? ` : ${d.error}` : ""}`, 6000);
    },
  );

  // WS-O — réponse de l'agent au handshake E2E : sa clé publique ECDH (SPKI b64)
  // + le sel HKDF. On dérive la session AES-GCM ; à partir de là les frames
  // chiffrées (flag bit1) sont déchiffrées dans onBinary. Suite divergente ou
  // dérivation échouée (e2eComplete → null) → on reste en clair.
  const offE2eKey = ws.on(
    (m) => m.type === "e2e:key",
    (m) =>
      // eslint-disable-next-line solid/reactivity -- handler ws.on avec untrack interne
      untrack(async () => {
        const d = m as unknown as { suite: string; pub: string; salt: string };
        if (!e2eComplete || d.suite !== E2E_SUITE) return;
        e2eSession = await e2eComplete(d.pub, d.salt);
      }),
  );

  // WS-O — l'agent signale une inspection TLS (proxy MITM en firewall strict)
  // détectée au handshake WSS. Purement informatif → badge d'avertissement.
  const offInspect = ws.on(
    (m) => m.type === "e2e:inspection-detected",
    (m) => {
      const d = m as unknown as { issuer: string; reason: string };
      setTlsInspected({ issuer: d.issuer, reason: d.reason });
    },
  );

  const offWebRtcOffer = ws.on(
    (m) => m.type === "webrtc:offer",
    (m) => handleWebRtcOffer(m as unknown as WebRtcOfferMsg),
  );
  const offWebRtcIce = ws.on(
    (m) => m.type === "webrtc:ice",
    (m) => handleWebRtcIce(m as unknown as WebRtcIceMsg),
  );

  // Curseurs distants des autres opérateurs (re-broadcast par le relai).
  const offOperatorCursor = ws.on(
    (m) => m.type === "operator:cursor",
    (m) => {
      const d = m as unknown as { operatorId?: string; label?: string; x?: number; y?: number };
      if (!d.operatorId || typeof d.x !== "number" || typeof d.y !== "number") return;
      if (d.operatorId === ws.state.myPeerId) return;
      setRemoteCursors((prev) => ({
        ...prev,
        [d.operatorId as string]: { x: d.x as number, y: d.y as number, label: d.label || "opérateur", t: Date.now() },
      }));
    },
  );
  // Purge les curseurs des opérateurs qui ont quitté la session.
  createEffect(() => {
    const present = new Set(operators().map((p) => p.id));
    setRemoteCursors((prev) => {
      let changed = false;
      const next: Record<string, RemoteCursor> = {};
      for (const [id, c] of Object.entries(prev)) {
        if (present.has(id)) next[id] = c;
        else changed = true;
      }
      return changed ? next : prev;
    });
  });
  // Purge les curseurs périmés (opérateur immobile / déconnexion silencieuse).
  const cursorStaleTimer = window.setInterval(() => {
    const now = Date.now();
    setRemoteCursors((prev) => {
      let changed = false;
      const next: Record<string, RemoteCursor> = {};
      for (const [id, c] of Object.entries(prev)) {
        if (now - c.t < 4000) next[id] = c;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, 2000);
  onCleanup(() => {
    offOperatorCursor();
    window.clearInterval(cursorStaleTimer);
  });

  // Binary frames — en-tête VB 10 o : magic(2) "VB" + FRAME_TYPE(1) + FLAGS(1)
  //   + ts_µs(6 BE). FRAME_TYPE : 0x01 = video (payload @10), 0x02 = tiles (WS-L,
  //   cadre complet header 16 o + payload). FLAGS : bit0 keyframe, bit1 chiffré (WS-O).
  //
  // WS-O / WS-L — chemin combiné :
  //   • tiles (0x02) : postées telles quelles au worker (recompo côté worker).
  //   • video (0x01) clair : processVideoFrame direct (comportement historique).
  //   • video (0x01) chiffré : déchiffré via e2eSession, sérialisé sur e2eDecryptChain.
  // LIMITE assumée : seules les frames video 0x01 sont déchiffrées. Les tiles 0x02
  // sont transférées telles quelles (chiffrement des tuiles = extension future).
  // Les deux features (E2E + mixed-mode tuiles) sont OFF par défaut ; leur
  // combinaison est un cas avancé non couvert ici.
  const offBinary = ws.onBinary((data: ArrayBuffer) => {
    untrack(() => {
      if (!streaming()) return;
      if (data.byteLength < 10) return;
      const view = new DataView(data);
      if (view.getUint8(0) !== 0x56 || view.getUint8(1) !== 0x42) return;
      const frameType = view.getUint8(2);
      const flags = view.getUint8(3);
      const isKey = (flags & 0x01) !== 0;
      const encrypted = (flags & 0x02) !== 0;
      const ts = view.getUint32(4, false) * 65536 + view.getUint16(8, false);

      // WS-L — cadre « tiles » mixed-mode : transféré ENTIER (header 16 o + payload)
      // au worker, qui décode les tuiles RLE et les superpose au dernier H264.
      // N'arrive que si le mixed-mode est négocié (sinon le flux n'en contient pas).
      if (frameType === 0x02) {
        if (data.byteLength < 16) return;
        if (workerActive() && mode() === "annexb") {
          const buf = data.slice(0);
          decodeWorker!.postMessage({ type: "tiles", bytes: buf }, [buf]);
        }
        return;
      }

      // FRAME_TYPE 0x01 — frame vidéo. Payload @ octet 10.
      const payload = new Uint8Array(data, 10);
      if (!encrypted) {
        processVideoFrame(ts, isKey, payload);
        return;
      }
      // Frame chiffrée (WS-O) : sans session E2E établie, on ne peut pas déchiffrer
      // → on jette (ne devrait pas arriver, l'agent ne chiffre qu'après handshake OK).
      if (!e2eSession) return;
      // Sérialise les déchiffrements AES-GCM (async) pour préserver l'ordre DTS des
      // frames malgré l'asynchronisme de WebCrypto. Capture la session courante.
      const sess = e2eSession;
      // eslint-disable-next-line solid/reactivity -- promise chain, pas un scope réactif
      e2eDecryptChain = e2eDecryptChain.then(async () => {
        const clear = await sess.decryptFrame(payload);
        if (clear) processVideoFrame(ts, isKey, clear);
      });
    });
  });

  onCleanup(() => {
    offStart();
    offChunk();
    offBrowserFrame();
    offEnd();
    offNaluStart();
    offNaluChunk();
    offNaluEnd();
    offModeActive();
    offTelemetry();
    offLiveMetrics();
    offVideoError();
    offSasResult();
    offWebRtcOffer();
    offWebRtcIce();
    offBinary();
    // WS-O — détache les handlers E2E et libère la session (la clé AES-GCM dérivée).
    offE2eKey();
    offInspect();
    e2eSession = null;
    if (native && nativeSession) {
      nativeSession.stopNative();
      nativeSession.unmount();
    } else {
      if (streaming()) {
        ws.send({ type: "video:control", action: "stop" });
      }
      teardownMediaSource();
      teardownWebCodecs();
      // WS-C — démontage du composant : le canvas DOM disparaît, on peut/doit
      // terminer le worker (sinon fuite worker + décodeur + OffscreenCanvas).
      teardownDecodeWorker();
      stopWebRtcCapture();
      releasePointerLock();
    }
  });

  // ── Input forwarding ────────────────────────────────────────────────────────

  function getSurface(): HTMLElement | null {
    // En transport WebRTC la vidéo est rendue dans `rtcVideoEl` ; `videoEl` et
    // `canvasEl` sont alors `display:none` (clientWidth/Height = 0) — il faut
    // donc cibler `rtcVideoEl` pour la normalisation des coordonnées souris,
    // sinon tout l'input mappe sur (0,0) en mode WebRTC.
    if (webrtcStreaming()) return rtcVideoEl ?? null;
    return mode() === "annexb" ? (canvasEl ?? null) : (videoEl ?? null);
  }

  // ── T1 : viewport opérateur → résolution d'encodage adaptative ──────────────
  // Mesure la surface vidéo en pixels PHYSIQUES (round(cssW × dpr)) et pousse
  // `video:viewport {w,h,dpr}` à l'agent (via relais). Le relais arbitre
  // (multi-op = MAX plafonné au CAP) et reconfigure l'encodeur. DEBOUNCE 300 ms
  // + écoute du changement de devicePixelRatio. Web uniquement : le chemin
  // Capacitor natif passe par VideoBridge.setViewport (cf. useNativeVideoSession).
  const VIEWPORT_DEBOUNCE_MS = 300;
  let viewportDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSentViewport: { w: number; h: number } | null = null;

  function measureViewport(): { w: number; h: number; dpr: number } | null {
    // On mesure la viewer-surface (cadre vidéo réel) plutôt que getSurface() :
    // le <canvas>/<video> peut être display:none au tout début. La surface a
    // toujours sa taille de layout dès le mount.
    const el = surfaceEl ?? getSurface();
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const dpr = window.devicePixelRatio || 1;
    return { w: Math.round(rect.width * dpr), h: Math.round(rect.height * dpr), dpr };
  }

  function pushViewportNow() {
    if (native) return; // chemin natif géré par VideoBridge.setViewport
    const v = measureViewport();
    if (!v) return;
    setViewport(v);
    // Anti-spam : ne ré-émettre que si la sortie a bougé d'≥1 px physique.
    if (lastSentViewport && lastSentViewport.w === v.w && lastSentViewport.h === v.h) return;
    lastSentViewport = { w: v.w, h: v.h };
    // Le relais throttle/hystérèse encore côté serveur ; ici on ne fait que
    // refléter la taille mesurée. Émis même hors streaming pour préparer le start.
    ws.send({ type: "video:viewport", w: v.w, h: v.h, dpr: v.dpr } as never);
  }

  function pushViewportDebounced() {
    // Mesure immédiate (pour que viewport() soit frais si on démarre tout de
    // suite), envoi WS différé pour absorber les rafales de resize.
    const v = measureViewport();
    if (v) setViewport(v);
    if (viewportDebounceTimer) clearTimeout(viewportDebounceTimer);
    viewportDebounceTimer = setTimeout(() => {
      viewportDebounceTimer = null;
      pushViewportNow();
    }, VIEWPORT_DEBOUNCE_MS);
  }

  // ResizeObserver sur la surface + écoute du changement de DPR (zoom navigateur
  // / déplacement entre écrans de densités différentes). matchMedia(resolution)
  // doit être ré-armé à chaque changement car l'événement n'est tiré qu'une fois.
  let viewportResizeObs: ResizeObserver | null = null;
  let dprMql: MediaQueryList | null = null;
  function onDprChange() {
    pushViewportDebounced();
    armDprListener();
  }
  function armDprListener() {
    if (dprMql) {
      dprMql.removeEventListener?.("change", onDprChange);
      dprMql = null;
    }
    const dpr = window.devicePixelRatio || 1;
    try {
      dprMql = window.matchMedia(`(resolution: ${dpr}dppx)`);
      dprMql.addEventListener?.("change", onDprChange);
    } catch {
      dprMql = null;
    }
  }
  function attachViewportObservers(el: HTMLElement) {
    if (native) return;
    viewportResizeObs = new ResizeObserver(() => pushViewportDebounced());
    viewportResizeObs.observe(el);
    armDprListener();
    // Mesure initiale (sans attendre un resize) pour que viewport() soit prêt.
    pushViewportDebounced();
  }
  onCleanup(() => {
    if (viewportDebounceTimer) clearTimeout(viewportDebounceTimer);
    if (viewportResizeObs) {
      viewportResizeObs.disconnect();
      viewportResizeObs = null;
    }
    if (dprMql) {
      dprMql.removeEventListener?.("change", onDprChange);
      dprMql = null;
    }
  });

  // ── T2 : mapping coordonnées surface → bureau distant ───────────────────────
  // En mode 1:1 (sourceRect actif), l'agent ne capture qu'une portion du bureau.
  // Une fraction (fx,fy) ∈ [0,1] de la surface affichée correspond donc à
  //   x = sourceRect.x + fx * sourceRect.w   (et idem en y)
  // au lieu de fx * desktop.w. En mode fit, sourceRect est nul → on retombe sur
  // la résolution bureau entière. Ceci est le VRAI 1:1 (pixels réellement
  // demandés à l'agent), à distinguer du zoom CSS (transform scale) qui upscale
  // une image déjà encodée (donc floue) sans changer l'espace de coordonnées.
  function mapToDesktop(fx: number, fy: number): { x: number; y: number } {
    const sr = sourceRect();
    if (sr && sr.w > 0 && sr.h > 0) {
      return {
        x: Math.round(sr.x + fx * sr.w),
        y: Math.round(sr.y + fy * sr.h),
      };
    }
    const target = desktopRes() ?? resolution();
    if (!target) return { x: 0, y: 0 };
    return { x: Math.round(fx * target.w), y: Math.round(fy * target.h) };
  }

  // ── T2 : pan en mode taille réelle (1:1) ────────────────────────────────────
  // En "real", `sourceRect` est une fenêtre {x,y,w,h} dans le bureau natif.
  // Le pan déplace x,y (w,h constants → l'agent reconfigure le crop GPU à chaud,
  // PAS de restart encodeur). On émet `video:viewrect` throttlé ~16 ms. Pour
  // masquer la latence (émission → nouvelle frame cropée), on translate aussi
  // la surface en CSS dans une MARGE (~12% de la fenêtre) : tant qu'on reste
  // sous la marge, le déplacement est purement local et on n'émet rien ; au-delà
  // on « commit » la position au serveur et on résorbe l'offset CSS.
  const PAN_MARGIN_RATIO = 0.12; // +12% de marge avant re-crop serveur
  const VIEWRECT_THROTTLE_MS = 16; // ~60 Hz, hot-path
  // Offset visuel temporaire en pixels CSS de la surface (pan local non encore
  // committé au serveur). Résorbé à chaque commit / nouvelle frame.
  const [panOffset, setPanOffset] = createSignal<{ x: number; y: number }>({ x: 0, y: 0 });
  let viewrectThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingViewrect: { x: number; y: number; w: number; h: number } | null = null;
  // Drag de pan via bouton MILIEU (n'entre pas en conflit avec le drag gauche =
  // sélection/déplacement distant). Maintenu : déplace la fenêtre 1:1 « grab ».
  let panDragging = false;
  // Commit différé du résidu de pan à la molette (pas d'événement « fin »).
  let wheelPanCommitTimer: ReturnType<typeof setTimeout> | null = null;

  const isRealMode = () => framingMode() === "real" && !!sourceRect();

  function fullDesktop(): { w: number; h: number } | null {
    return desktopRes() ?? resolution();
  }

  function flushViewrect() {
    if (viewrectThrottleTimer) {
      clearTimeout(viewrectThrottleTimer);
      viewrectThrottleTimer = null;
    }
    if (!pendingViewrect) return;
    ws.send({ type: "video:viewrect", ...pendingViewrect } as never);
    pendingViewrect = null;
  }

  function emitViewrect(rect: { x: number; y: number; w: number; h: number }) {
    pendingViewrect = rect;
    if (viewrectThrottleTimer === null) {
      viewrectThrottleTimer = setTimeout(() => {
        viewrectThrottleTimer = null;
        flushViewrect();
      }, VIEWRECT_THROTTLE_MS);
    }
  }

  // Combien de pixels CSS de la surface représentent 1 px bureau, sur l'axe
  // contraignant (la vidéo est en object-fit: contain). Sert à convertir un
  // déplacement bureau en offset CSS pour le pan local.
  function cssPerDesktopPx(): number {
    const el = getSurface();
    const sr = sourceRect();
    if (!el || !sr || sr.w <= 0 || sr.h <= 0) return 1;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 1;
    const elAR = rect.width / rect.height;
    const srcAR = sr.w / sr.h;
    // dimension affichée de la vidéo (la plus contrainte)
    const dispW = srcAR > elAR ? rect.width : rect.height * srcAR;
    return dispW / sr.w;
  }

  // Déplacement bureau accumulé NON ENCORE committé au serveur (reflété en pan
  // CSS uniquement). `sourceRect` (la source de vérité pour le mapping coords)
  // ne bouge qu'au commit, car tant qu'on est sous la marge le serveur crope
  // toujours la même fenêtre — la frame affichée correspond à l'ancien rect +
  // un translate CSS, et le mapping doit rester cohérent avec CE qui est à
  // l'écran. Au franchissement de la marge on commit (avance sourceRect + émet
  // viewrect) et on résorbe l'offset CSS.
  let pendingPanDelta = { x: 0, y: 0 };

  function commitPan() {
    if (pendingPanDelta.x === 0 && pendingPanDelta.y === 0) return;
    const sr = sourceRect();
    const full = fullDesktop();
    if (!sr || !full) {
      pendingPanDelta = { x: 0, y: 0 };
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    const maxX = Math.max(0, full.w - sr.w);
    const maxY = Math.max(0, full.h - sr.h);
    const nx = Math.min(maxX, Math.max(0, sr.x + pendingPanDelta.x));
    const ny = Math.min(maxY, Math.max(0, sr.y + pendingPanDelta.y));
    pendingPanDelta = { x: 0, y: 0 };
    setPanOffset({ x: 0, y: 0 });
    if (nx === sr.x && ny === sr.y) return;
    // Source de vérité : la nouvelle fenêtre cropée que le serveur va appliquer.
    setSourceRect({ x: nx, y: ny, w: sr.w, h: sr.h });
    emitViewrect({ x: nx, y: ny, w: sr.w, h: sr.h });
  }

  // Déplace la fenêtre 1:1 de (dx,dy) px BUREAU. `dx,dy` > 0 = on regarde plus
  // à droite/bas. Sous la marge : pan local CSS, aucune émission serveur, pas de
  // modif sourceRect. Au-delà de la marge : commit (re-crop serveur, pas de
  // restart encodeur — l'agent reconfigure le crop GPU à chaud).
  function panBy(dxDesktop: number, dyDesktop: number) {
    if (!isRealMode()) return;
    const sr = sourceRect();
    const full = fullDesktop();
    if (!sr || !full) return;
    const maxX = Math.max(0, full.w - sr.w);
    const maxY = Math.max(0, full.h - sr.h);
    // Borne le delta accumulé pour que la position visée reste dans le bureau.
    const wantX = sr.x + pendingPanDelta.x + dxDesktop;
    const wantY = sr.y + pendingPanDelta.y + dyDesktop;
    const clampedX = Math.min(maxX, Math.max(0, wantX));
    const clampedY = Math.min(maxY, Math.max(0, wantY));
    pendingPanDelta = { x: clampedX - sr.x, y: clampedY - sr.y };
    if (pendingPanDelta.x === 0 && pendingPanDelta.y === 0) {
      setPanOffset({ x: 0, y: 0 });
      return;
    }
    // Pan local CSS : translate inverse du déplacement bureau (regarder à droite
    // = contenu glisse vers la gauche), proportionnel à l'échelle d'affichage.
    const k = cssPerDesktopPx();
    setPanOffset({ x: -pendingPanDelta.x * k, y: -pendingPanDelta.y * k });
    // Hors marge (~12% de la fenêtre) → commit serveur ; sinon purement local.
    const marginX = Math.max(8, PAN_MARGIN_RATIO * sr.w * k);
    const marginY = Math.max(8, PAN_MARGIN_RATIO * sr.h * k);
    if (Math.abs(pendingPanDelta.x * k) >= marginX || Math.abs(pendingPanDelta.y * k) >= marginY) {
      commitPan();
    }
  }

  // Auto-pan aux bords : quand la souris approche d'un bord de la surface en
  // mode 1:1, on fait défiler la fenêtre dans cette direction (vitesse ∝
  // proximité du bord). Tick rAF tant qu'un bord est actif.
  let autoPanRaf: number | null = null;
  let autoPanVec: { x: number; y: number } = { x: 0, y: 0 };
  const AUTOPAN_EDGE_PX = 40; // largeur de la zone sensible au bord
  const AUTOPAN_MAX_SPEED = 22; // px bureau / tick au contact du bord
  function autoPanTick() {
    if ((autoPanVec.x === 0 && autoPanVec.y === 0) || !isRealMode()) {
      autoPanRaf = null;
      return;
    }
    panBy(autoPanVec.x, autoPanVec.y);
    autoPanRaf = requestAnimationFrame(autoPanTick);
  }
  function updateAutoPanFromPointer(e: MouseEvent) {
    if (!isRealMode()) {
      autoPanVec = { x: 0, y: 0 };
      return;
    }
    const el = getSurface();
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    let vx = 0;
    let vy = 0;
    if (px < AUTOPAN_EDGE_PX) vx = -((AUTOPAN_EDGE_PX - px) / AUTOPAN_EDGE_PX) * AUTOPAN_MAX_SPEED;
    else if (px > rect.width - AUTOPAN_EDGE_PX)
      vx = ((px - (rect.width - AUTOPAN_EDGE_PX)) / AUTOPAN_EDGE_PX) * AUTOPAN_MAX_SPEED;
    if (py < AUTOPAN_EDGE_PX) vy = -((AUTOPAN_EDGE_PX - py) / AUTOPAN_EDGE_PX) * AUTOPAN_MAX_SPEED;
    else if (py > rect.height - AUTOPAN_EDGE_PX)
      vy = ((py - (rect.height - AUTOPAN_EDGE_PX)) / AUTOPAN_EDGE_PX) * AUTOPAN_MAX_SPEED;
    autoPanVec = { x: Math.round(vx), y: Math.round(vy) };
    if (autoPanVec.x !== 0 || autoPanVec.y !== 0) {
      if (autoPanRaf === null) autoPanRaf = requestAnimationFrame(autoPanTick);
    } else if (!panDragging && (pendingPanDelta.x !== 0 || pendingPanDelta.y !== 0)) {
      // La souris a quitté la zone de bord : on fige la position courante
      // (commit du résidu CSS non encore envoyé au serveur).
      commitPan();
    }
  }
  function stopAutoPan() {
    autoPanVec = { x: 0, y: 0 };
    if (autoPanRaf !== null) {
      cancelAnimationFrame(autoPanRaf);
      autoPanRaf = null;
    }
  }
  onCleanup(() => {
    stopAutoPan();
    if (viewrectThrottleTimer) clearTimeout(viewrectThrottleTimer);
    if (wheelPanCommitTimer) clearTimeout(wheelPanCommitTimer);
  });

  // ── Ascenseurs (mode 1:1) ───────────────────────────────────────────────────
  // Le pan existait déjà (bouton milieu, molette, bords) mais restait invisible :
  // rien à l'écran n'indiquait qu'il y avait du bureau au-delà du cadre. Ces
  // ascenseurs rendent la position visible et manipulable, en réutilisant
  // panBy/commitPan — mêmes bornes, même commit serveur que le pan souris.

  // Fenêtre RÉELLEMENT affichée : `sourceRect` avancé du pan local pas encore
  // committé. Ce résidu est dérivé du signal `panOffset` plutôt que de la
  // variable `pendingPanDelta` : une variable ne déclenche pas de re-render, le
  // curseur avancerait donc par à-coups de 12 % (PAN_MARGIN_RATIO) au lieu de
  // suivre le geste.
  function visibleRect(): { x: number; y: number; w: number; h: number } | null {
    const sr = sourceRect();
    if (!sr) return null;
    const k = cssPerDesktopPx() || 1;
    const off = panOffset();
    return { x: sr.x - off.x / k, y: sr.y - off.y / k, w: sr.w, h: sr.h };
  }

  function scrollThumb(axis: "x" | "y") {
    if (!isRealMode()) return null;
    const view = visibleRect();
    const full = fullDesktop();
    if (!view || !full) return null;
    return thumbGeometry(view, full, axis);
  }

  let scrollDragAxis: "x" | "y" | null = null;
  let scrollDragLast = 0;

  function onScrollThumbDown(axis: "x" | "y", e: PointerEvent) {
    e.preventDefault();
    // Sans ça, le pointerdown remonte à la piste et déclenche un saut de page.
    e.stopPropagation();
    scrollDragAxis = axis;
    scrollDragLast = axis === "x" ? e.clientX : e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  function onScrollThumbMove(e: PointerEvent) {
    if (!scrollDragAxis) return;
    const track = (e.currentTarget as HTMLElement).parentElement;
    const full = fullDesktop();
    if (!track || !full) return;
    const rect = track.getBoundingClientRect();
    const horizontal = scrollDragAxis === "x";
    const now = horizontal ? e.clientX : e.clientY;
    const delta = trackDeltaToDesktop(
      now - scrollDragLast,
      horizontal ? rect.width : rect.height,
      horizontal ? full.w : full.h,
    );
    // Sous le pixel bureau : on garde le résidu pour le move suivant plutôt que
    // de le perdre (sinon les petits déplacements ne bougent jamais rien).
    if (delta === 0) return;
    scrollDragLast = now;
    panBy(horizontal ? delta : 0, horizontal ? 0 : delta);
  }

  function onScrollThumbUp(e: PointerEvent) {
    if (!scrollDragAxis) return;
    scrollDragAxis = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    } catch {
      /* pointer déjà relâché — ignoré */
    }
    commitPan();
  }

  /** Clic sur la piste (hors curseur) : saut d'une page vers le point cliqué. */
  function onScrollTrackDown(axis: "x" | "y", e: PointerEvent) {
    const view = visibleRect();
    const full = fullDesktop();
    if (!view || !full) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const horizontal = axis === "x";
    const frac = horizontal ? (e.clientX - rect.left) / rect.width : (e.clientY - rect.top) / rect.height;
    const cur = horizontal ? view.x / full.w : view.y / full.h;
    const page = Math.round((horizontal ? view.w : view.h) * 0.9);
    const step = frac > cur ? page : -page;
    panBy(horizontal ? step : 0, horizontal ? 0 : step);
    commitPan();
  }

  // Bascule fit ↔ taille réelle (1:1). Changement de cadrage = changement de
  // sortie d'encodage → RESTART encodeur (rare, assumé). On relance la capture
  // avec le framingMode demandé ; l'agent renvoie un `video:start` confirmant
  // sourceRect/framingMode (cf. handleStart). Optimiste : on pose le mode tout
  // de suite pour l'UI, l'agent corrige si la bascule échoue.
  function toggleFraming() {
    if (!streaming() && !webrtcStreaming()) return;
    const next: FramingMode = framingMode() === "real" ? "fit" : "real";
    setFramingMode(next);
    setPanOffset({ x: 0, y: 0 });
    if (next === "fit") setSourceRect(null);
    else showToast("1:1 — déplacement : ascenseurs, molette, bouton du milieu ou bords");
    const preset = transport() === "webrtc" ? findVideoPreset("h264-webcodecs") : selectedPreset();
    const profile = findQualityProfile(selectedQualityId());
    const v = measureViewport();
    if (v) setViewport(v);
    const base = buildVideoStartParams(preset, profile, freeParams(), v ? { w: v.w, h: v.h } : undefined);
    const params = { ...base, framingMode: next };
    if (transport() === "webrtc") {
      ws.send({ type: "video:control", action: "start", operatorId: rtcOperatorId, params } as never);
    } else {
      ws.send({ type: "video:control", action: "start", params });
    }
  }

  // Aspect ratio de la vidéo réellement affichée : en mode 1:1 (sourceRect
  // actif) la sortie encodée a le ratio de la portion cropée, pas du bureau
  // entier — sinon le letterboxing serait mal calculé et les coords décalées.
  function displayedSourceAr(): number | null {
    const sr = sourceRect();
    if (sr && sr.w > 0 && sr.h > 0) return sr.w / sr.h;
    const target = desktopRes() ?? resolution();
    if (!target || !target.h) return null;
    return target.w / target.h;
  }

  function normalizeCoords(e: MouseEvent): { x: number; y: number } {
    const el = getSurface();
    if (!el) return { x: 0, y: 0 };
    // Map to the real remote desktop resolution — that is the coordinate space
    // SendInput expects. `mapToDesktop` applique l'origine/échelle de sourceRect
    // en mode 1:1, sinon la résolution bureau entière.
    const contentAR = displayedSourceAr();
    if (contentAR == null) return { x: 0, y: 0 };

    const elW = el.clientWidth;
    const elH = el.clientHeight;
    if (elW <= 0 || elH <= 0) return { x: 0, y: 0 };

    // The video content is letterboxed inside the element via `object-fit: contain`,
    // so `e.offsetX/offsetY` (relative to the full element box, black bars included)
    // must first be mapped into the actual displayed video rectangle.
    const elAR = elW / elH;
    let dispW = elW;
    let dispH = elH;
    let offX = 0;
    let offY = 0;
    if (contentAR > elAR) {
      // Width-limited → pillarbox top/bottom.
      dispH = elW / contentAR;
      offY = (elH - dispH) / 2;
    } else {
      // Height-limited → letterbox left/right.
      dispW = elH * contentAR;
      offX = (elW - dispW) / 2;
    }

    const fx = Math.min(1, Math.max(0, (e.offsetX - offX) / dispW));
    const fy = Math.min(1, Math.max(0, (e.offsetY - offY) / dispH));
    return mapToDesktop(fx, fy);
  }

  // ── Input batching ──────────────────────────────────────────────────────
  // Coalesce mousemove à 125 Hz (8 ms). Les souris modernes peuvent émettre
  // 500-1000 events/s ; non coalescé, cela inonde la WS et l'IPC C#. Les
  // events significatifs (button/key/scroll/touch/paste) déclenchent un
  // flush synchrone juste avant pour préserver l'ordre causal.
  type PendingMove =
    | { kind: "move"; x: number; y: number; absolute: true }
    | { kind: "move"; x: number; y: number; absolute: false };
  const INPUT_FLUSH_MS = 8;
  let pendingMove: PendingMove | null = null;
  let inputFlushTimer: number | null = null;

  function flushMove() {
    if (inputFlushTimer !== null) {
      window.clearTimeout(inputFlushTimer);
      inputFlushTimer = null;
    }
    if (!pendingMove) return;
    ws.send({ type: "input", input: pendingMove });
    pendingMove = null;
  }

  function queueMove(move: PendingMove) {
    if (pendingMove && !move.absolute && !pendingMove.absolute) {
      // Mode relatif : additionner les deltas (préserve le mouvement total)
      pendingMove = { ...move, x: pendingMove.x + move.x, y: pendingMove.y + move.y };
    } else {
      // Mode absolu : garder la dernière position (les précédentes sont obsolètes)
      pendingMove = move;
    }
    if (inputFlushTimer === null) {
      inputFlushTimer = window.setTimeout(flushMove, INPUT_FLUSH_MS);
    }
  }

  function onSurfaceMouseMove(e: MouseEvent) {
    if (!streaming()) return;
    wakeBar();
    sendCursorPosition(e);
    // Drag de pan (bouton milieu, mode 1:1) : déplace la fenêtre en sens « grab »
    // (souris à droite → on découvre la gauche). Aucun input distant pendant ce
    // drag. Converti des px CSS souris en px bureau via cssPerDesktopPx().
    if (panDragging && isRealMode()) {
      const k = cssPerDesktopPx() || 1;
      panBy(-Math.round(e.movementX / k), -Math.round(e.movementY / k));
      return;
    }
    // Auto-pan aux bords (mode 1:1) : actif dès que la souris approche d'un bord.
    if (isRealMode()) updateAutoPanFromPointer(e);
    if (pointerLocked()) {
      if (!ensureCurrentOperatorActive()) return;
      const dx = Math.round(e.movementX);
      const dy = Math.round(e.movementY);
      if (dx !== 0 || dy !== 0) queueMove({ kind: "move", x: dx, y: dy, absolute: false });
    } else {
      // Suivi continu OFF par défaut : on n'envoie un `move` absolu que si le
      // suivi est activé OU si un bouton est enfoncé (drag — nécessaire pour
      // glisser/sélectionner). Les clics, la molette et le clavier portent déjà
      // leurs propres coordonnées et restent inchangés.
      if (!followMouse() && e.buttons === 0) return;
      // Auto-claim de la « main » APRÈS le filtre passif : un simple survol de
      // la surface (sans bouton, suivi OFF) ne doit JAMAIS voler le contrôle.
      // Avec plusieurs opérateurs, chaque survol envoyait operator:activate →
      // ping-pong de bascules en boucle (toasts « a pris la main ») + rafale
      // de session:state. Le claim ne part que sur action réelle (drag/suivi).
      if (!ensureCurrentOperatorActive()) return;
      const { x, y } = normalizeCoords(e);
      queueMove({ kind: "move", x, y, absolute: true });
    }
  }

  function onSurfaceMouseDown(e: MouseEvent) {
    if (!streaming()) return;
    if (surfaceEl && document.activeElement !== surfaceEl) {
      surfaceEl.focus();
      setControlFocused(true);
    }
    // Mode 1:1 : le bouton MILIEU démarre un drag de pan local (pas de clic
    // milieu distant). Le drag gauche/droit reste un input distant normal.
    if (isRealMode() && e.button === 1) {
      e.preventDefault();
      panDragging = true;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.((e as PointerEvent).pointerId ?? 0);
      } catch {
        /* setPointerCapture non dispo sur MouseEvent pur — ignoré */
      }
      return;
    }
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    e.preventDefault();
    const btn = e.button === 1 ? "middle" : e.button === 2 ? "right" : "left";
    const { x, y } = normalizeCoords(e);
    // Clic direct (suivi de souris OFF) : le curseur distant n'a pas suivi la
    // souris, il faut donc le positionner AVANT le press. On envoie un `move`
    // absolu explicite — fiable quelle que soit la version du helper distant —
    // puis le `button` (qui reporte aussi x,y pour les helpers récents).
    if (!pointerLocked()) {
      ws.send({ type: "input", input: { kind: "move", x, y, absolute: true } });
    }
    ws.send({ type: "input", input: { kind: "button", button: btn, down: true, x, y } });
  }

  function onSurfaceMouseUp(e: MouseEvent) {
    if (!streaming()) return;
    // Fin d'un drag de pan (bouton milieu, mode 1:1) : ne pas émettre d'input.
    if (panDragging && e.button === 1) {
      panDragging = false;
      commitPan();
      return;
    }
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    const btn = e.button === 1 ? "middle" : e.button === 2 ? "right" : "left";
    const { x, y } = normalizeCoords(e);
    ws.send({ type: "input", input: { kind: "button", button: btn, down: false, x, y } });
  }

  // Sortie de la surface : stoppe l'auto-pan aux bords et tout drag de pan en
  // cours (sinon le pan continuerait après que la souris a quitté la zone).
  function onSurfaceMouseLeave() {
    stopAutoPan();
    if (panDragging) {
      panDragging = false;
      commitPan();
    }
  }

  function onSurfaceWheel(e: WheelEvent) {
    if (!streaming()) return;
    // En mode taille réelle (1:1), la molette PAN la fenêtre 1:1 au lieu de
    // scroller la machine distante : on déplace sourceRect des deltas molette
    // (px bureau), sans restart encodeur. Le scroll distant reste accessible en
    // mode fit. Aucun envoi `video:viewrect` ne touche le scroll distant.
    if (isRealMode()) {
      e.preventDefault();
      panBy(Math.round(e.deltaX), Math.round(e.deltaY));
      // La molette n'a pas d'événement « fin » : on commit le résidu (offset CSS
      // sous la marge) peu après le dernier cran pour figer la position serveur.
      if (wheelPanCommitTimer) clearTimeout(wheelPanCommitTimer);
      wheelPanCommitTimer = setTimeout(() => {
        wheelPanCommitTimer = null;
        commitPan();
      }, 120);
      return;
    }
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    e.preventDefault();
    ws.send({ type: "input", input: { kind: "scroll", dx: Math.round(e.deltaX), dy: Math.round(e.deltaY) } });
  }

  // ── Touch handlers (mobile) ─────────────────────────────────────────────────
  // Quand controlFocused() : touch → souris distante (touch-action: none via CSS).
  // Quand non contrôlé : touch-action: pan-x pan-y → scroll natif du conteneur.

  function normalizeCoordsFromTouch(touch: Touch): { x: number; y: number } {
    const el = getSurface();
    if (!el) return { x: 0, y: 0 };
    const contentAR = displayedSourceAr();
    if (contentAR == null) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const elW = rect.width;
    const elH = rect.height;
    if (elW <= 0 || elH <= 0) return { x: 0, y: 0 };
    const elAR = elW / elH;
    let dispW = elW,
      dispH = elH,
      offX = 0,
      offY = 0;
    if (contentAR > elAR) {
      dispH = elW / contentAR;
      offY = (elH - dispH) / 2;
    } else {
      dispW = elH * contentAR;
      offX = (elW - dispW) / 2;
    }
    const ox = touch.clientX - rect.left;
    const oy = touch.clientY - rect.top;
    const fx = Math.min(1, Math.max(0, (ox - offX) / dispW));
    const fy = Math.min(1, Math.max(0, (oy - offY) / dispH));
    return mapToDesktop(fx, fy);
  }

  // Touch handlers : route vers trackpad-mode si activé, sinon comportement
  // historique (tap-to-click absolu). 2 doigts → pinch-zoom / pan (commun
  // aux deux modes).

  function onSurfaceTouchStart(e: TouchEvent) {
    if (!streaming() || !controlFocused()) return;
    // 2 doigts : initialise pinch/pan. Cas commun (trackpad ou non).
    if (e.touches.length === 2) {
      e.preventDefault();
      const a = e.touches[0];
      const b = e.touches[1];
      pinchAnchor = {
        dist: touchDistance(a, b),
        scale: zoomScale(),
        midX: (a.clientX + b.clientX) / 2,
        midY: (a.clientY + b.clientY) / 2,
        offX: zoomOffset().x,
        offY: zoomOffset().y,
      };
      pinchMoved = false;
      touchAnchor = null;
      setPinching(true);
      showZoomIndicator();
      return;
    }
    // Double-tap (1 doigt sur 1 doigt en < 300 ms) : reset zoom si zoomé.
    if (checkDoubleTapForZoomReset()) {
      e.preventDefault();
      return;
    }
    if (trackpadMode()) return onSurfaceTouchStartTrackpad(e);
    if (!ensureCurrentOperatorActive()) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const { x, y } = normalizeCoordsFromTouch(touch);
    flushMove();
    ws.send({ type: "input", input: { kind: "move", x, y, absolute: true } });
    ws.send({ type: "input", input: { kind: "button", button: "left", down: true, x, y } });
  }

  function onSurfaceTouchMove(e: TouchEvent) {
    if (!streaming() || !controlFocused()) return;
    // Pinch-zoom + pan (2 doigts).
    if (e.touches.length === 2 && pinchAnchor) {
      e.preventDefault();
      const a = e.touches[0];
      const b = e.touches[1];
      const d = touchDistance(a, b);
      const mx = (a.clientX + b.clientX) / 2;
      const my = (a.clientY + b.clientY) / 2;
      // Si le doigt bouge nettement → on est dans un gesture zoom/pan, plus
      // un simple "2-finger tap" pour le clic droit du trackpad.
      const distDelta = Math.abs(d - pinchAnchor.dist);
      const midDelta = Math.abs(mx - pinchAnchor.midX) + Math.abs(my - pinchAnchor.midY);
      if (distDelta > PINCH_THRESHOLD_PX || midDelta > PINCH_THRESHOLD_PX) pinchMoved = true;
      // Zoom = ratio des distances.
      const ratio = d / pinchAnchor.dist;
      const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, pinchAnchor.scale * ratio));
      // Pan = midpoint qui se déplace, en pixels écran. Clampé pour ne pas
      // sortir la surface complètement hors viewport.
      const rawOffset = {
        x: pinchAnchor.offX + (mx - pinchAnchor.midX),
        y: pinchAnchor.offY + (my - pinchAnchor.midY),
      };
      setZoomScale(newScale);
      setZoomOffset(clampOffsetForScale(rawOffset, newScale));
      return;
    }
    if (trackpadMode()) return onSurfaceTouchMoveTrackpad(e);
    if (!ensureCurrentOperatorActive()) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const { x, y } = normalizeCoordsFromTouch(touch);
    queueMove({ kind: "move", x, y, absolute: true });
  }

  function onSurfaceTouchEnd(e: TouchEvent) {
    if (!streaming() || !controlFocused()) return;
    // Fin du pinch. Si on a vraiment bougé → c'était un zoom/pan, ne rien
    // émettre de plus. Sinon, et si trackpad mode est ON, on émule le clic
    // droit (le bouton "2-finger tap" historique du trackpad). Le mode
    // historique (trackpadMode OFF) ignore — il n'envoyait rien sur 2 doigts.
    if (pinchAnchor) {
      const stillTwo = e.touches.length >= 2;
      if (!stillTwo) {
        const movedReal = pinchMoved;
        const wasTrackpad = trackpadMode();
        pinchAnchor = null;
        pinchMoved = false;
        setPinching(false);
        fadeZoomIndicator();
        if (!movedReal && wasTrackpad) sendVirtualClick("right");
      }
      return;
    }
    if (trackpadMode()) return onSurfaceTouchEndTrackpad(e);
    if (!ensureCurrentOperatorActive()) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const { x, y } = normalizeCoordsFromTouch(touch);
    flushMove();
    ws.send({ type: "input", input: { kind: "button", button: "left", down: false, x, y } });
  }

  // ── Trackpad-mode handlers (curseur virtuel relatif) ───────────────────────

  function onSurfaceTouchStartTrackpad(e: TouchEvent) {
    // Note : le cas 2-doigts est intercepté par le handler parent (pinch/
    // pan). Le clic droit du mode trackpad se déclenche sur TouchEnd si
    // le geste 2-doigts n'a pas vraiment bougé.
    e.preventDefault();
    const t = e.touches[0];
    if (!t) return;
    touchAnchor = { clientX: t.clientX, clientY: t.clientY, t: Date.now() };
    touchMoved = false;
  }

  function onSurfaceTouchMoveTrackpad(e: TouchEvent) {
    e.preventDefault();
    if (!touchAnchor) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - touchAnchor.clientX;
    const dy = t.clientY - touchAnchor.clientY;
    if (Math.abs(dx) + Math.abs(dy) > TAP_THRESHOLD_PX) touchMoved = true;
    const el = getSurface();
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    setVirtualCursor((prev) => ({
      x: Math.max(0, Math.min(1, prev.x + dx / rect.width)),
      y: Math.max(0, Math.min(1, prev.y + dy / rect.height)),
    }));
    touchAnchor = { clientX: t.clientX, clientY: t.clientY, t: touchAnchor.t };
    sendVirtualMove();
  }

  function onSurfaceTouchEndTrackpad(e: TouchEvent) {
    if (!touchAnchor) return;
    const dt = Date.now() - touchAnchor.t;
    const wasTap = !touchMoved && dt < TAP_MAX_MS;
    touchAnchor = null;
    touchMoved = false;
    if (wasTap) sendVirtualClick("left");
    // (e.changedTouches utilisé implicitement par le navigateur, pas besoin ici)
    void e;
  }

  function onSurfaceContextMenu(e: Event) {
    if (streaming()) e.preventDefault();
  }

  // Convertit un événement clavier en VK Windows. Priorité à `event.code`
  // (position physique, indépendant du layout AZERTY/QWERTY/Mac), fallback
  // sur `event.key` si le code est inconnu.
  //
  // Le swap Cmd→Ctrl est désormais décidé par le profil clavier (`macProfile`
  // quand `swapCmdCtrl` est ON), qui ajoute aussi la liste noire Mac
  // (Cmd+Tab/Space/Q/H = locaux). On consulte `evaluateKey` : si l'action est
  // `local`, on retourne `null` pour que `onSurfaceKeyDown` saute l'envoi
  // (la touche reste navigueur). Sinon, on calcule le VK avec le swap profilé.
  function eventToVk(e: KeyboardEvent): number | null {
    const profile = keyboardProfile();
    const decision = evaluateKey(profile, e);
    // `local` : Cmd+Tab/Space/Q/H — on laisse le navigateur gérer (switch app, etc.).
    if (decision?.action.kind === "local") return null;
    let vk = codeToVk(e.code);
    if (vk === null) vk = keyToVk(e.key);
    if (vk === null) return null;
    // Swap Cmd→Ctrl : si le profil est `mac` (swapCmdCtrl ON) ET la touche est
    // Meta (LWIN/RWIN), on traduit en CONTROL. Le profil `mac` gère ça via
    // `swap-cmd-to-ctrl`, mais on l'applique ici au VK calculé pour préserver le
    // fallback `keyToVk` qui ne passe pas par `evaluateKey`.
    if (swapCmdCtrl() && (vk === VK.LWIN || vk === VK.RWIN)) {
      vk = VK.CONTROL;
    }
    return vk;
  }

  function onSurfaceKeyDown(e: KeyboardEvent) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    const textInput = toTextInputValue(e);
    if (textInput !== null) {
      e.preventDefault();
      flushMove();
      textInputDown.add(e.code);
      ws.send({ type: "input", input: { kind: "keyText", text: textInput } });
      recordKeyboardDebug({
        action: "down:text",
        key: textInput,
        code: e.code || "-",
        vk: null,
        event: e,
        note: "texte",
      });
      return;
    }

    flushMove();
    // Prevent browser shortcuts while controlling
    const browserKeys = ["F5", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "F1", "F3", "F4", "F11"];
    if (browserKeys.includes(e.key)) {
      e.preventDefault();
      recordKeyboardDebug({ action: "down:block", key: e.key, code: e.code || "-", vk: null, event: e });
    }
    // Empêcher le navigateur de capturer les raccourcis avec modificateur
    // (Cmd+W, Ctrl+T...) pour qu'ils soient transmis à la machine distante.
    if (e.metaKey || e.ctrlKey || e.altKey) {
      e.preventDefault();
      recordKeyboardDebug({ action: "down:block-mod", key: e.key, code: e.code || "-", vk: null, event: e });
    }

    const vk = eventToVk(e);
    if (vk !== null) {
      ws.send({ type: "input", input: { kind: "key", vk, down: true } });
      recordKeyboardDebug({ action: "down", key: e.key, code: e.code || "-", vk, event: e });
      return;
    }

    recordKeyboardDebug({
      action: "down:unmapped",
      key: e.key,
      code: e.code || "-",
      vk: null,
      event: e,
    });
  }

  function onSurfaceKeyUp(e: KeyboardEvent) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    if (textInputDown.has(e.code)) {
      textInputDown.delete(e.code);
      recordKeyboardDebug({
        action: "up:text-skip",
        key: e.key,
        code: e.code || "-",
        vk: null,
        event: e,
        note: "déj envoyé en keyText",
      });
      return;
    }

    flushMove();
    const vk = eventToVk(e);
    if (vk !== null) {
      ws.send({ type: "input", input: { kind: "key", vk, down: false } });
      recordKeyboardDebug({ action: "up", key: e.key, code: e.code || "-", vk, event: e });
      return;
    }

    recordKeyboardDebug({ action: "up:unmapped", key: e.key, code: e.code || "-", vk: null, event: e });
  }

  function toTextInputValue(e: KeyboardEvent): string | null {
    if (e.isComposing) return null;
    if (e.key.length !== 1) return null;
    if (e.ctrlKey || e.metaKey || e.altKey || e.getModifierState("AltGraph")) return null;
    const ch = e.key;
    // Ignore les touches de contrôle non-imprimables.
    if (ch < " ") return null;
    return ch;
  }

  function onSurfacePaste(e: ClipboardEvent) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    const text = e.clipboardData?.getData("text");
    if (text) {
      e.preventDefault();
      ws.send({ type: "input", input: { kind: "keyText", text } });
      recordKeyboardTextDebug("paste:event", text, `chars=${text.length}`);
    }
  }

  // ── Touches spéciales (séquences) ───────────────────────────────────────────

  /** Envoie une combinaison : down dans l'ordre, up dans l'ordre inverse. */
  function sendCombo(vks: number[]) {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    for (const vk of vks) {
      ws.send({ type: "input", input: { kind: "key", vk, down: true } });
    }
    for (let i = vks.length - 1; i >= 0; i--) {
      ws.send({ type: "input", input: { kind: "key", vk: vks[i], down: false } });
    }
  }

  function sendSas() {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    flushMove();
    // Windows ignore l'injection logicielle de Ctrl+Alt+Suppr : il faut passer
    // par le canal dédié géré par l'agent (Secure Attention Sequence).
    ws.send({ type: "sas:request" });
    showToast("Demande Ctrl+Alt+Suppr envoyee...");
    setSpecialKeysOpen(false);
  }

  function runSpecialCombo(vks: number[]) {
    sendCombo(vks);
    setSpecialKeysOpen(false);
  }

  // ── Presse-papier ───────────────────────────────────────────────────────────

  /** Lit le presse-papier local et envoie le texte en frappe Unicode. */
  async function pasteAsTyping() {
    if (!streaming()) return;
    if (!ensureCurrentOperatorActive()) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        showToast("Presse-papier vide");
        setClipboardMenuOpen(false);
        return;
      }
      ws.send({ type: "input", input: { kind: "keyText", text } });
      recordKeyboardTextDebug("paste:typing", text, `chars=${text.length}`);
      showToast(`Collé en frappe (${text.length} caractères)`);
      setClipboardMenuOpen(false);
    } catch {
      showToast("Accès au presse-papier refusé");
    }
  }

  /** Envoie le presse-papier local vers la machine distante. */
  async function pushClipboard(silent = false) {
    if (!ensureCurrentOperatorActive()) return;
    try {
      const text = await navigator.clipboard.readText();
      ws.send({ type: "clipboard:set", text });
      setClipboardMenuOpen(false);
      // Alimente l'historique du moteur partagé (local→remote).
      if (text) {
        videoClipboardEngine.pushHistory(entryFromSnapshot({ text }, "local", Date.now(), `push-${Date.now()}`));
      }
      if (!silent) showToast("Presse-papier envoyé");
      recordKeyboardTextDebug("clipboard:set", text, `chars=${text.length}`);
    } catch {
      if (!silent) showToast("Accès au presse-papier refusé");
    }
  }

  /** Demande le presse-papier de la machine distante. */
  function requestClipboard() {
    ws.send({ type: "clipboard:request" });
    setClipboardMenuOpen(false);
    showToast("Récupération du presse-papier distant...");
  }

  // Réception du presse-papier distant.
  const offClipboardSync = ws.on(
    (m) => m.type === "clipboard:sync",
    (m) => {
      const msg = m as { type: "clipboard:sync"; text?: string; kind?: string; name?: string; size?: number };
      if (msg.kind === "file") {
        const sizeKb = msg.size ? ` (${Math.round(msg.size / 1024)} Ko)` : "";
        showToast(`L'agent veut transférer un fichier : ${msg.name ?? "fichier"}${sizeKb}`, 6000);
        // Alimente l'historique (métadonnées fichier seulement).
        videoClipboardEngine.pushHistory({
          id: `file-${Date.now()}`,
          origin: "remote",
          formats: ["files"],
          textPreview: msg.name ?? "fichier",
          fileRefs: msg.name ? [{ name: msg.name, size: msg.size ?? 0 }] : undefined,
          timestamp: Date.now(),
        });
        return;
      }
      if (typeof msg.text === "string") {
        // Alimente l'historique du moteur partagé (remote→local).
        const snapshot: ClipboardSnapshot = { text: msg.text };
        videoClipboardEngine.onRemoteChanged(snapshot, async (s) => {
          if (s.text) {
            try {
              await navigator.clipboard.writeText(s.text);
              showToast("Presse-papier distant copié localement");
            } catch {
              showToast("Impossible d'écrire dans le presse-papier local");
            }
          }
        });
      }
    },
  );
  onCleanup(() => offClipboardSync());

  // Sync auto : quand la fenêtre de prise en main reçoit le focus, on pousse le
  // presse-papier local vers la machine distante.
  function onWindowFocus() {
    if (streaming()) void pushClipboard(true);
  }
  window.addEventListener("focus", onWindowFocus);
  onCleanup(() => window.removeEventListener("focus", onWindowFocus));

  function onSurfaceFocus() {
    setControlFocused(true);
  }

  function onSurfaceBlur() {
    setControlFocused(false);
    releasePointerLock();
    textInputDown.clear();
  }

  function onSurfaceClick() {
    if (streaming() && surfaceEl) {
      surfaceEl.focus();
    }
  }

  // ── Fullscreen + Focus + PiP handlers ───────────────────────────────────────
  function toggleFullscreen() {
    if (!viewerWrapRef) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void viewerWrapRef.requestFullscreen();
    }
  }
  function onFullscreenChange() {
    setIsFullscreen(Boolean(document.fullscreenElement));
  }
  document.addEventListener("fullscreenchange", onFullscreenChange);
  onCleanup(() => document.removeEventListener("fullscreenchange", onFullscreenChange));

  function toggleFocusMode() {
    const next = !focusMode();
    setFocusMode(next);
    window.dispatchEvent(new CustomEvent("video:focus-mode", { detail: { active: next } }));
  }

  // Flux d'affichage courant pour le PiP : la MediaStream WebRTC si elle porte
  // la vidéo, sinon une capture temps réel du canvas WSS/WebCodecs. On NE déplace
  // PAS le canvas vivant (un contexte 2D `desynchronized` devient blanc hors de sa
  // fenêtre d'origine) : on partage le même flux via un <video> dans le PiP.
  function currentDisplayStream(): MediaStream | null {
    if (webrtcStreaming() && rtcVideoEl?.srcObject instanceof MediaStream) {
      return rtcVideoEl.srcObject;
    }
    if (canvasEl && typeof canvasEl.captureStream === "function") {
      try {
        return canvasEl.captureStream(30);
      } catch {
        return null;
      }
    }
    return null;
  }

  function closePiP() {
    if (pipVideoEl) {
      try {
        pipVideoEl.srcObject = null;
      } catch {
        /* ignore */
      }
      pipVideoEl = null;
    }
    const w = pipWindow;
    pipWindow = null;
    setPipActive(false);
    if (w && !w.closed) {
      try {
        w.close();
      } catch {
        /* ignore */
      }
    }
  }

  async function togglePiP() {
    if (pipWindow && !pipWindow.closed) {
      closePiP();
      return;
    }
    if (!("documentPictureInPicture" in window)) return;
    if (!streaming() && !webrtcStreaming()) {
      showToast("Démarre la vidéo avant d'ouvrir le PiP");
      return;
    }
    const stream = currentDisplayStream();
    if (!stream) {
      showToast("Flux vidéo indisponible pour le PiP");
      return;
    }
    try {
      const srcW = canvasEl?.width || viewerWrapRef?.clientWidth || 1280;
      const srcH = canvasEl?.height || viewerWrapRef?.clientHeight || 720;
      const pip = await (
        window as unknown as { documentPictureInPicture: { requestWindow: (opts?: unknown) => Promise<Window> } }
      ).documentPictureInPicture.requestWindow({ width: srcW, height: srcH });
      pipWindow = pip;
      // Fenêtre PiP minimale : fond noir, vidéo plein cadre (contain).
      pip.document.body.style.margin = "0";
      pip.document.body.style.background = "#000";
      pip.document.body.style.overflow = "hidden";
      const video = pip.document.createElement("video");
      video.autoplay = true;
      video.muted = true;
      (video as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
      video.setAttribute("playsinline", "");
      video.style.width = "100%";
      video.style.height = "100vh";
      video.style.objectFit = "contain";
      video.style.background = "#000";
      video.srcObject = stream;
      pip.document.body.appendChild(video);
      void video.play().catch(() => {
        /* autoplay best-effort */
      });
      pipVideoEl = video;
      // Masque le média de la page principale (pas de double affichage).
      setPipActive(true);
      pip.addEventListener("pagehide", () => closePiP(), { once: true });
    } catch {
      closePiP();
      showToast("Picture-in-Picture indisponible");
    }
  }
  onCleanup(() => closePiP());

  // Pointer lock for "locked" remote control mode
  const [pointerLocked, setPointerLocked] = createSignal(false);

  function togglePointerLock() {
    if (!streaming() || !surfaceEl) return;
    if (document.pointerLockElement === surfaceEl) {
      document.exitPointerLock();
    } else {
      surfaceEl.requestPointerLock();
    }
  }

  function releasePointerLock() {
    if (document.pointerLockElement) {
      try {
        document.exitPointerLock();
      } catch {
        // ignore
      }
    }
  }

  // Track pointer lock state
  function onPointerLockChange() {
    setPointerLocked(document.pointerLockElement === surfaceEl);
  }

  document.addEventListener("pointerlockchange", onPointerLockChange);
  onCleanup(() => {
    document.removeEventListener("pointerlockchange", onPointerLockChange);
  });

  // Construit les params de `video:control start` en y injectant le viewport
  // opérateur mesuré (le 1er encode suit déjà la fenêtre — plus de flou
  // d'upscale) et le framingMode courant. Le preset/profil reste un PLAFOND :
  // buildVideoStartParams pose maxWidth = min(profil, viewport.w).
  function buildStartParams(preset: VideoPreset, profile: QualityProfile) {
    const v = measureViewport();
    if (v) setViewport(v);
    const base = buildVideoStartParams(preset, profile, freeParams(), v ? { w: v.w, h: v.h } : undefined);
    // WS-B — mémorise le codec demandé (télémétrie : comparé à effectiveCodec
    // reçu dans handleStart). Le verdict isConfigSupported par codec est déjà
    // dans browserCodecSupport().
    setRequestedCodec(base.codec);
    // WS-N / WS-K — activation NÉGOCIÉE (OFF si l'agent ne l'annonce pas) :
    //  • 4:4:4 : agent video444 + gate dur navigateur (chroma444 décodable HW) + H264.
    //  • mixed-mode : agent videoMixedMode + worker de décode actif (recompo WS-L).
    const af = ws.state.agentFeatures as
      | { video444?: boolean; videoMixedMode?: boolean; videoGlyphCache?: boolean }
      | undefined;
    const want444 = af?.video444 === true && browserCodecSupport().chroma444 === true && base.codec === "h264";
    const wantMixed = af?.videoMixedMode === true && videoWorkerDecodeFlag();
    // WS-M — cache de glyphes : SUR-COUCHE de mixed-mode. On ne le demande QUE si
    // mixed-mode est lui-même demandé (le REF référence des tuiles RLE de WS-K) ET que
    // l'agent annonce la capability videoGlyphCache (négociation bilatérale, toggle ON).
    const wantGlyph = wantMixed && af?.videoGlyphCache === true;
    return {
      ...base,
      framingMode: framingMode(),
      ...(want444 ? { chroma444: true } : {}),
      ...(wantMixed ? { mixedMode: true } : {}),
      ...(wantGlyph ? { glyphCache: true } : {}),
    };
  }

  // Control actions
  // « WS dans RDP » — bascule la vue en capture WS (WGC) ciblant une session TS
  // précise (sélectionnée dans la liste des sessions du panel RDP). Court-circuite
  // le flux « login requis » : la session existe déjà, on la capture directement.
  function startCaptureWithSession(sessionId: number, label: string) {
    setError(null);
    setMainMode("video");
    void startCapture(undefined, sessionId, label);
  }

  async function startCapture(presetId?: string, targetSessionId?: number, targetLabel?: string) {
    setError(null);
    // En mode ciblage explicite (WS dans RDP), on ne repasse PAS par le flux de
    // login : la session est déjà ouverte, l'agent re-cible le Helper dessus.
    if (targetSessionId == null) {
      // PROPOSER, pas forcer : on signale la session RDP active (bannière) mais
      // on poursuit la capture WS de la session physique console.
      const rdpSession = activeRdpSessionRequiringLogin();
      if (rdpSession) suggestRdpLoginForWs(rdpSession);
    }
    if (native && nativeSession) {
      setStreaming(true);
      await nativeSession.startNative();
      return;
    }
    const requestedPreset = presetId != null ? findVideoPreset(presetId) : selectedPreset();
    // Démarrage WSS explicite/implicite : on force un transport webcodecs.
    // Un preset WebRTC par défaut ne doit pas déclencher d'auto-choix WebRTC.
    const preset =
      presetId == null && requestedPreset.transport === "webrtc"
        ? ((): VideoPreset => {
            const h265 = browserCodecSupport().h265 === true;
            const av1 = browserCodecSupport().av1 === true;
            const features = ws.state.agentFeatures;
            if (videoCodecPolicyFlag()) {
              const preferred = pickCodecForProfile(
                { transport: "wss", content: findQualityProfile(selectedQualityId()).content ?? "text" },
                features,
                { h265, av1 },
              );
              return isPresetSupported(preferred) ? preferred : defaultVideoPreset();
            }
            return preferredDefaultVideoPreset(videoH265DefaultFlag(), features, h265);
          })()
        : requestedPreset;
    if (!preset) {
      setError("Preset invalide.");
      return;
    }
    if (!isPresetSupported(preset) || preset.transport !== "webcodecs") {
      setError(`${preset.label} non supporté par cet agent/navigateur.`);
      return;
    }
    const profile = findQualityProfile(selectedQualityId());
    // WS-O — handshake E2E avant le premier démarrage du flux : on génère une paire
    // ECDH éphémère et on envoie notre clé publique à l'agent. Sans toggle ON (ou
    // WebCrypto absent), aucun handshake n'est émis → flux strictement en clair.
    // L'agent répond `e2e:key` (clé pub + sel) → e2eComplete dérive la session.
    if (e2eEnabled() && !e2eSession) {
      const hs = await E2eSession.begin();
      e2eComplete = hs.complete;
      ws.send({ type: "e2e:hello", suite: E2E_SUITE, pub: hs.publicKeyB64 } as never);
    }
    void targetLabel; // libellé informatif côté UI uniquement (log agent porte la session)
    ws.send({
      type: "video:control",
      action: "start",
      ...(targetSessionId != null ? { targetSessionId } : {}),
      params: buildStartParams(preset, profile),
    });
  }

  function stopCapture() {
    if (native && nativeSession) {
      nativeSession.stopNative();
      setStreaming(false);
      setControlFocused(false);
      return;
    }
    ws.send({ type: "video:control", action: "stop" });
    setStreaming(false);
    setControlFocused(false);
    releasePointerLock();
    teardownMediaSource();
    teardownWebCodecs();
    resetFrameMetrics();
    stopAbrStats();
    resetFramingState();
  }

  // Remise à l'état "fit" du cadrage 1:1 (fin de session / arrêt). Évite qu'un
  // sourceRect périmé fausse le mapping coords à la prochaine prise en main.
  function resetFramingState() {
    stopAutoPan();
    panDragging = false;
    pendingPanDelta = { x: 0, y: 0 };
    if (viewrectThrottleTimer) {
      clearTimeout(viewrectThrottleTimer);
      viewrectThrottleTimer = null;
    }
    if (wheelPanCommitTimer) {
      clearTimeout(wheelPanCommitTimer);
      wheelPanCommitTimer = null;
    }
    pendingViewrect = null;
    setPanOffset({ x: 0, y: 0 });
    setSourceRect(null);
    setFramingMode("fit");
    setSessionKind(null);
  }

  // ── C1 : crédentials TURN ───────────────────────────────────────────────────
  // Fetch authentifié de `GET /turn/credential`. Retourne les `iceServers` ou
  // `null` si indisponible (401/429/503/réseau) — l'appelant gère le fallback.
  // Réutilise le cache tant que le TTL n'est pas dépassé.
  async function fetchTurnCredentials(forceRefresh = false): Promise<RTCIceServer[] | null> {
    if (!forceRefresh && turnIceServers && Date.now() - turnFetchedAt < TURN_TTL_MS) {
      return turnIceServers;
    }
    try {
      const res = await apiFetch("/turn/credential", { credentials: "include" });
      if (!res.ok) {
        // 401 = session expirée, 429 = rate-limit, 503 = TURN non configuré.
        console.warn(`[webrtc] /turn/credential a renvoyé ${res.status} — fallback WSS probable.`);
        return null;
      }
      const data = (await res.json()) as { iceServers?: RTCIceServer[] };
      if (!Array.isArray(data.iceServers) || data.iceServers.length === 0) {
        console.warn("[webrtc] /turn/credential sans iceServers — fallback WSS probable.");
        return null;
      }
      turnIceServers = data.iceServers;
      turnFetchedAt = Date.now();
      return turnIceServers;
    } catch (e) {
      console.warn("[webrtc] échec fetch /turn/credential:", e);
      return null;
    }
  }

  // Journalise et signale au relais la raison d'un repli WSS, puis bascule.
  function fallbackToWss(reason: string) {
    if (fallbackEngaged) return; // anti-oscillation : un seul basculement par session
    fallbackEngaged = true;
    console.warn(`[webrtc] fallback WSS — raison: ${reason}`);
    if (webrtcWatchdog) {
      clearTimeout(webrtcWatchdog);
      webrtcWatchdog = null;
    }
    setWebrtcAttempt(false);
    // Audit : remonter la raison au relais.
    ws.send({ type: "webrtc:error", operatorId: rtcOperatorId, reason });
    // Démanteler la PC WebRTC éventuellement à moitié établie.
    if (rtcPeer) {
      try {
        rtcPeer.close();
      } catch {
        // ignore
      }
      rtcPeer = null;
    }
    setWebrtcStreaming(false);
    stopAbrStats();
    setTransport("wss");
    // Démarrer le flux WSS historique si pas déjà actif.
    if (!streaming()) {
      void startCapture();
    }
  }

  // ── C2 : démarrage vidéo — WebRTC préféré, fallback WSS ─────────────────────
  function startWebRtcCapture() {
    setError(null);
    // PROPOSER, pas forcer : la capture WebRTC de la session physique continue ;
    // la bannière propose de rejoindre la session RDP active.
    const rdpSession = activeRdpSessionRequiringLogin();
    if (rdpSession) suggestRdpLoginForWs(rdpSession);
    const preset = findVideoPreset("h264-webrtc");
    if (!isPresetSupported(preset)) {
      // WebRTC indisponible côté agent/navigateur → fallback direct WSS.
      fallbackToWss("webrtc-unsupported");
      return;
    }
    lastWebRtcOfferSDP = null;
    fallbackEngaged = false;
    setWebrtcAttempt(true);
    setTransport("webrtc");
    // Réinitialise le détecteur de stall pour cette nouvelle session.
    prevFramesDecoded = -1;
    webrtcStallTicks = 0;
    rtcOperatorId = crypto.randomUUID();
    // Reconnexion fiable : avant toute nouvelle offer, on demande à l'agent
    // d'invalider toute session WebRTC stale précédente (ancien operatorId resté
    // côté agent après un refresh/déconnexion). Sans ça, l'agent peut renvoyer
    // une offer portant l'ancien id, rejetée par le guard ci-dessous → la
    // reconnexion échoue silencieusement. Inoffensif si l'agent l'ignore.
    ws.send({
      type: "webrtc:reset",
      operatorId: rtcOperatorId,
      sessionCode: ws.state.sessionCode ?? "",
    } as never);
    // Le bridge WebRTC est alimenté par les NALU H.264 que l'Agent reçoit du
    // Helper. SANS démarrer la capture, la PeerConnection s'établit mais la
    // piste reste `muted` (aucune image). On démarre donc la capture en
    // H.264 Annex-B (mode attendu par le bridge), comme le chemin WSS.
    const capturePreset = findVideoPreset("h264-webcodecs");
    const profile = findQualityProfile(selectedQualityId());
    ws.send({
      type: "video:control",
      action: "start",
      operatorId: rtcOperatorId,
      params: buildStartParams(capturePreset, profile),
    });
    ws.send({
      type: "webrtc:request",
      operatorId: rtcOperatorId,
      codec: "h264",
    });
    // Watchdog : si WebRTC n'atteint pas `connected` dans le délai, fallback.
    // 20 s — l'établissement relay-only TURN-over-TLS/TCP (allocations TURN des
    // deux côtés + handshakes TLS + permissions + DTLS-SRTP) dépasse souvent
    // 7 s à la 1ʳᵉ connexion. Un délai trop court fait basculer en WSS avant
    // que WebRTC n'ait eu le temps d'aboutir. Le passage à l'état `failed` de
    // la PeerConnection déclenche de toute façon le fallback immédiatement.
    if (webrtcWatchdog) clearTimeout(webrtcWatchdog);
    webrtcWatchdog = setTimeout(() => {
      if (rtcPeer?.connectionState !== "connected") {
        fallbackToWss("webrtc-connect-timeout");
      }
    }, 20000);
  }

  function stopWebRtcCapture() {
    const hadRtcSession = Boolean(rtcPeer || rtcOperatorId || webrtcAttempt() || webrtcStreaming());
    if (webrtcWatchdog) {
      clearTimeout(webrtcWatchdog);
      webrtcWatchdog = null;
    }
    stopAbrStats();
    if (rtcPeer) {
      ws.send({ type: "webrtc:close", operatorId: rtcOperatorId });
      rtcPeer.close();
      rtcPeer = null;
    }
    lastWebRtcOfferSDP = null;
    // Arrêter aussi la capture démarrée par startWebRtcCapture (symétrie avec
    // stopCapture du chemin WSS). Si un fallback WSS l'a déjà reprise, le
    // chemin WSS gère son propre stop.
    if (hadRtcSession && !fallbackEngaged)
      ws.send({ type: "video:control", action: "stop", operatorId: rtcOperatorId });
    rtcOperatorId = null;
    setWebrtcStreaming(false);
    setWebrtcAttempt(false);
    if (rtcVideoEl) rtcVideoEl.srcObject = null;
    resetFramingState();
  }

  async function handleWebRtcOffer(msg: WebRtcOfferMsg) {
    try {
      if (!rtcOperatorId || (msg.operatorId && msg.operatorId !== rtcOperatorId)) {
        // Offer obsolète (ancien operatorId d'une session précédente non nettoyée
        // côté agent). On la rejette mais on le trace pour le diagnostic des
        // reconnexions impossibles. Le webrtc:reset envoyé au start devrait
        // empêcher ce cas avec un agent à jour.
        console.warn(`[webrtc] offer ignorée operatorId=${msg.operatorId ?? "∅"} (courant=${rtcOperatorId ?? "∅"})`);
        return;
      }
      const offerSdp = (msg.sdp ?? "").trim();
      if (!offerSdp) return;
      if (msg.operatorId === rtcOperatorId && lastWebRtcOfferSDP === offerSdp) return;
      if (rtcPeer) rtcPeer.close();
      rtcOperatorId = msg.operatorId ?? rtcOperatorId;
      lastWebRtcOfferSDP = offerSdp;

      // C1 : récupérer les crédentials TURN éphémères. Re-fetch si le TTL est
      // dépassé (renégociation après expiration). Échec → ne bloque pas, on
      // construit quand même la PC ; si la connexion échoue le watchdog C2
      // déclenche le fallback WSS.
      const iceServers = await fetchTurnCredentials();
      const pcConfig: RTCConfiguration = iceServers ? { iceServers, iceTransportPolicy: "relay" } : {};
      if (!iceServers) {
        console.warn("[webrtc] PC construite sans TURN — le watchdog fera le fallback si besoin.");
      }
      rtcPeer = new RTCPeerConnection(pcConfig);

      rtcPeer.ontrack = (evt) => {
        // Élargir le jitter buffer du récepteur. Sur le chemin relay-only (saut
        // TURN supplémentaire → jitter accru), les frames arrivent en rafales ;
        // un buffer trop court les compte "dropped" alors qu'elles ne sont
        // qu'en léger retard. Cible 150 ms : absorbe le jitter relais sans
        // dégrader notablement la réactivité de la prise en main.
        try {
          const rcv = evt.receiver as unknown as {
            jitterBufferTarget?: number | null;
            playoutDelayHint?: number | null;
          };
          if (rcv && "jitterBufferTarget" in rcv) rcv.jitterBufferTarget = 150;
          else if (rcv && "playoutDelayHint" in rcv) rcv.playoutDelayHint = 0.15;
        } catch {
          /* propriété non supportée par ce navigateur — ignoré */
        }
        if (evt.streams[0] && rtcVideoEl) {
          if (webrtcWatchdog) {
            clearTimeout(webrtcWatchdog);
            webrtcWatchdog = null;
          }
          rtcVideoEl.srcObject = evt.streams[0];
          rtcVideoEl.play().catch(() => {});
          setWebrtcAttempt(false);
          setTransport("webrtc");
          setWebrtcStreaming(true);
          startAbrStats();
        }
      };
      rtcPeer.onicecandidate = (evt) => {
        if (evt.candidate) {
          // Warning défensif : avec iceTransportPolicy "relay" on ne devrait
          // voir que des candidats `relay`. Un srflx/host signale une mauvaise
          // config TURN ou une fuite réseau.
          if (evt.candidate.type && evt.candidate.type !== "relay") {
            console.warn(`[webrtc] candidat ICE non-relay détecté (${evt.candidate.type}) — TURN relay-only attendu.`);
          }
          ws.send({
            type: "webrtc:ice",
            operatorId: msg.operatorId,
            candidate: evt.candidate.toJSON(),
          });
        }
      };
      rtcPeer.onconnectionstatechange = () => {
        const st = rtcPeer?.connectionState;
        if (st === "connected") {
          // C2 : WebRTC a réussi — annuler le watchdog, démarrer l'ABR.
          if (webrtcWatchdog) {
            clearTimeout(webrtcWatchdog);
            webrtcWatchdog = null;
          }
          setWebrtcAttempt(false);
          setTransport("webrtc");
          startAbrStats();
        } else if (st === "failed" || st === "disconnected" || st === "closed") {
          setWebrtcStreaming(false);
          // Bascule sur WSS si on n'a pas déjà replié (anti-oscillation interne).
          if (st !== "closed" || webrtcAttempt()) {
            fallbackToWss(`webrtc-state-${st}`);
          }
        }
      };
      await rtcPeer.setRemoteDescription({ type: "offer", sdp: offerSdp });
      const answer = await rtcPeer.createAnswer();
      await rtcPeer.setLocalDescription(answer);
      ws.send({ type: "webrtc:answer", operatorId: msg.operatorId, sdp: answer.sdp });
    } catch (e) {
      console.warn("[webrtc] échec négociation offer:", e);
      fallbackToWss(`webrtc-offer-error: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleWebRtcIce(msg: WebRtcIceMsg) {
    if (!rtcPeer) return;
    if (rtcOperatorId && msg.operatorId && msg.operatorId !== rtcOperatorId) return;
    try {
      await rtcPeer.addIceCandidate(msg.candidate);
    } catch {
      /* ignore */
    }
  }

  // ── C3 : ABR client — getStats() + décision palier ──────────────────────────
  // Applique un palier de l'échelle ABR_LADDER via `video:control update`.
  function applyAbrRung(index: number, why: string) {
    const rung = ABR_LADDER[index];
    if (!rung) return;
    setAbrRung(index);
    abrLastChangeAt = Date.now();
    console.info(`[abr] palier ${index} (${rung.maxWidth}p/${rung.fps}fps/${rung.bitrate}kbps) — ${why}`);
    // Refléter sur l'UI : aligner le slider/qualité manuelle.
    setFreeParams((p) => ({ ...p, fps: rung.fps, maxWidth: rung.maxWidth, bitrate: rung.bitrate }));
    // ABR automatique : bitrate uniquement. Les paliers conservent fps/maxWidth
    // pour l'affichage et les réglages manuels, mais l'automatisme ne les envoie
    // pas : côté Helper, fps/maxWidth redémarrent l'encodeur et provoquent des
    // pics CPU/pertes FPS en WSS quand le backend H.264 est software.
    ws.send({ type: "video:control", action: "update", params: buildAbrUpdateParams(rung) });
  }

  // Évalue l'état réseau et décide d'un éventuel changement de palier.
  // `degraded` : réseau mauvais (RTT/perte/jitter élevés sur ce tick).
  function abrEvaluate(degraded: boolean) {
    if (!abrEnabled()) return;
    const now = Date.now();
    if (now - abrLastChangeAt < ABR_COOLDOWN_MS) return; // cooldown

    if (degraded) {
      abrBadTicks++;
      abrGoodSince = 0;
      // Descente RAPIDE : ~2 ticks dégradés consécutifs → un palier plus bas.
      if (abrBadTicks >= 2 && abrRung() < ABR_LADDER.length - 1) {
        abrBadTicks = 0;
        applyAbrRung(abrStepDown(abrRung()), "réseau dégradé");
      }
    } else {
      abrBadTicks = 0;
      // Remontée LENTE : fenêtre stable ~12 s → un seul palier vers le haut.
      if (abrGoodSince === 0) abrGoodSince = now;
      if (now - abrGoodSince >= ABR_UP_WINDOW_MS && abrRung() > 0) {
        abrGoodSince = now;
        applyAbrRung(abrStepUp(abrRung()), "réseau stable");
      }
    }
  }

  // Collecte getStats() en mode WebRTC. RTT, perte, jitter, framesDropped.
  // OPT-07 #5 : on passe un sélecteur de track vidéo à getStats() pour
  // restreindre le RTCStatsReport aux stats inbound-rtp du flux vidéo, au lieu
  // de collecter et parcourir TOUS les rapports (inbound/outbound RTP,
  // candidate-pairs, codecs, certificates, transports…) toutes les 2 s. Pertinent
  // surtout sur mobile (app Capacitor). Fallback global préservé (cf. plus bas).
  async function collectWebrtcStats() {
    if (!rtcPeer || rtcPeer.connectionState !== "connected") return;
    try {
      // Sélecteur de track vidéo si disponible. getReceivers() peut ne pas
      // exister sur d'anciens runtimes ; on le résout défensivement.
      let videoTrack: MediaStreamTrack | undefined;
      try {
        videoTrack = rtcPeer.getReceivers?.().find((r) => r.track?.kind === "video")?.track;
      } catch {
        videoTrack = undefined;
      }

      // Report filtré sur le track vidéo si on a un track, sinon report global.
      // Si getStats(track) n'est pas supporté, fallback sur le report global.
      let stats: RTCStatsReport;
      try {
        stats = videoTrack ? await rtcPeer.getStats(videoTrack) : await rtcPeer.getStats();
      } catch {
        stats = await rtcPeer.getStats();
        videoTrack = undefined; // on a déjà le report global, pas de re-fetch
      }

      let rtt = 0;
      let jitter = 0;
      let packetsLost = 0;
      let packetsReceived = 0;
      let framesDropped = 0;
      let framesReceived = 0;
      let framesDecoded = 0;
      // Indique si le report parcouru contenait une source de RTT (candidate-pair
      // ou remote-inbound-rtp). Le report filtré par track peut omettre ces
      // entrées selon le navigateur → on re-fetch alors le report global.
      let sawRttSource = false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTCPeerConnection stats API est non-typée
      const parseReport = (report: any) => {
        if (report.type === "inbound-rtp" && report.kind === "video") {
          jitter = report.jitter ?? jitter;
          packetsLost = report.packetsLost ?? packetsLost;
          packetsReceived = report.packetsReceived ?? packetsReceived;
          framesDropped = report.framesDropped ?? framesDropped;
          framesReceived = report.framesReceived ?? framesReceived;
          framesDecoded = report.framesDecoded ?? framesDecoded;
        } else if (report.type === "candidate-pair" && report.state === "succeeded") {
          rtt = report.currentRoundTripTime ?? rtt;
          sawRttSource = true;
        } else if (report.type === "remote-inbound-rtp") {
          if (typeof report.roundTripTime === "number") rtt = report.roundTripTime;
          sawRttSource = true;
        }
      };
      stats.forEach(parseReport);

      // Fallback RTT : si on a utilisé le report filtré et qu'il n'exposait
      // aucune source de RTT (candidate-pair / remote-inbound-rtp), refaire un
      // getStats() global et re-parcourir pour récupérer le RTT.
      if (videoTrack && !sawRttSource) {
        try {
          const globalStats = await rtcPeer.getStats();
          globalStats.forEach(parseReport);
        } catch {
          // ignorer : on garde les valeurs déjà extraites du report filtré.
        }
      }
      // Deltas de perte sur l'intervalle.
      const lostDelta = Math.max(0, packetsLost - prevPacketsLost);
      const recvDelta = Math.max(0, packetsReceived - prevPacketsReceived);
      prevPacketsLost = packetsLost;
      prevPacketsReceived = packetsReceived;
      const lossRatio = recvDelta > 0 ? lostDelta / (lostDelta + recvDelta) : 0;
      const dropRatio = framesReceived > 0 ? framesDropped / framesReceived : 0;

      // Détecteur de stall : aucune frame décodée en plus depuis le tick
      // précédent → on incrémente ; au seuil → fallback WSS (le flux WebRTC
      // est gelé alors que la PeerConnection reste `connected`).
      if (prevFramesDecoded >= 0 && framesDecoded <= prevFramesDecoded) {
        webrtcStallTicks += 1;
        if (webrtcStallTicks >= WEBRTC_STALL_TICKS_MAX && !fallbackEngaged) {
          console.warn("[webrtc] flux gelé (0 frame décodée) — bascule WSS");
          fallbackToWss("webrtc-media-stall");
          return;
        }
      } else {
        webrtcStallTicks = 0;
      }
      prevFramesDecoded = framesDecoded;

      // HUD : RTT en ms approximé par l'intervalle inter-frames.
      setFrameIntervalMs(Math.round(rtt * 1000));

      // Seuils de dégradation : RTT > 250 ms, perte > 3 %, jitter > 50 ms,
      // ou drop de frames > 5 %.
      const degraded = rtt > 0.25 || lossRatio > 0.03 || jitter > 0.05 || dropRatio > 0.05;
      abrEvaluate(degraded);
    } catch {
      // getStats peut échouer transitoirement — ignorer ce tick.
    }
  }

  // En mode WSS : pas de getStats(), on s'appuie sur l'EMA `frameIntervalMs`
  // déjà calculée par recordFrameMetric(). Cadence haute = réseau lent.
  function evaluateWssAbr() {
    const interval = frameIntervalMs();
    if (interval <= 0) return; // pas encore de mesure
    // > 130 ms entre frames ≈ < 8 fps effectifs → dégradé.
    abrEvaluate(interval > 130);
  }

  function startAbrStats() {
    if (abrStatsTimer) return;
    prevPacketsLost = 0;
    prevPacketsReceived = 0;
    abrBadTicks = 0;
    abrGoodSince = 0;
    abrLastChangeAt = Date.now();
    // Aligner le palier de départ sur le débit courant choisi par l'opérateur.
    setAbrRung(abrRungForBitrate(bitrate()));
    abrStatsTimer = setInterval(() => {
      if (transport() === "webrtc") {
        void collectWebrtcStats();
      } else {
        evaluateWssAbr();
      }
    }, 2000);
  }

  function stopAbrStats() {
    if (abrStatsTimer) {
      clearInterval(abrStatsTimer);
      abrStatsTimer = null;
    }
  }

  onCleanup(() => {
    stopAbrStats();
    if (webrtcWatchdog) clearTimeout(webrtcWatchdog);
  });

  function updateBitrate(val: number) {
    setFreeParams((p) => ({ ...p, bitrate: val }));
    if (selectedQualityId() !== "free") setSelectedQualityId("free");
    // Recaler le palier ABR sur le réglage manuel pour éviter qu'un tick ABR
    // ne le réécrase aussitôt ; on diffère aussi la prochaine décision (cooldown).
    setAbrRung(abrRungForBitrate(val));
    abrLastChangeAt = Date.now();
    if (streaming() || webrtcStreaming()) {
      ws.send({
        type: "video:control",
        action: "update",
        params: { bitrate: val },
      });
    }
  }

  // ── WS-H : émission périodique de métriques navigateur vers le relais ────────
  // Le backpressure (ws.bufferedAmount) + les métriques de décode (WS-D) sont
  // poussés ~1×/s tant qu'un flux est en cours. Le relais consomme `video:metrics`
  // pour son feedback ABR. No-op s'il n'y a aucun flux actif (les valeurs WS-D
  // restent à 0 quand le flag WS-D est OFF — l'émission est inoffensive).
  const VIDEO_METRICS_INTERVAL_MS = 1000;
  const videoMetricsTimer = setInterval(() => {
    if (!ws.isOpen()) return;
    if (!streaming() && !webrtcStreaming()) return;
    ws.send({
      type: "video:metrics",
      bufferedAmount: ws.bufferedAmount(),
      decodeMs: decodeMs(),
      droppedFrames: droppedFrames(),
      decodeQueueSize: decodeQueueSize(),
    } as never);
  }, VIDEO_METRICS_INTERVAL_MS);
  onCleanup(() => clearInterval(videoMetricsTimer));

  const showVideo = () => streaming() && mode() === "fmp4";
  const showCanvas = () => streaming() && mode() === "annexb";
  const selectedCodecLabel = () => selectedPreset().label;
  const selectedQualityLabel = () => findQualityProfile(selectedQualityId()).label;
  const isGrayscale = () => selectedQualityId() === "free" && freeParams().colorMode === "grayscale";

  return (
    <div
      ref={(el) => (viewerWrapRef = el)}
      class="viewer-wrap"
      classList={{
        "viewer--streaming": streaming(),
        "viewer--control": controlFocused(),
        "viewer--focus-mode": focusMode(),
      }}
    >
      {/* Interactive surface */}
      <div
        ref={(el) => {
          surfaceEl = el;
          if (native && nativeSession) nativeSession.mount(el);
          else attachViewportObservers(el);
        }}
        class="viewer-surface"
        classList={{
          "viewer-surface--active": streaming() && controlFocused(),
          "viewer-surface--zoomed": isZoomed(),
        }}
        tabIndex={streaming() ? 0 : undefined}
        onMouseMove={native ? undefined : onSurfaceMouseMove}
        onMouseDown={native ? undefined : onSurfaceMouseDown}
        onMouseUp={native ? undefined : onSurfaceMouseUp}
        onMouseLeave={native ? undefined : onSurfaceMouseLeave}
        onWheel={native ? undefined : onSurfaceWheel}
        onContextMenu={native ? undefined : onSurfaceContextMenu}
        onKeyDown={native ? nativeSession?.onKeyDown : onSurfaceKeyDown}
        onKeyUp={native ? nativeSession?.onKeyUp : onSurfaceKeyUp}
        onPaste={native ? undefined : onSurfacePaste}
        onFocus={onSurfaceFocus}
        onBlur={onSurfaceBlur}
        onClick={native ? undefined : onSurfaceClick}
        onPointerDown={native ? nativeSession?.onPointerDown : undefined}
        onPointerMove={native ? nativeSession?.onPointerMove : undefined}
        onPointerUp={native ? nativeSession?.onPointerUp : undefined}
        onTouchStart={native ? undefined : onSurfaceTouchStart}
        onTouchMove={native ? undefined : onSurfaceTouchMove}
        onTouchEnd={native ? undefined : onSurfaceTouchEnd}
        style={(() => {
          const base: Record<string, string> = {};
          if (native && streaming()) base.background = "transparent";
          // Pan local en mode 1:1 : translate transitoire dans la marge avant
          // re-crop serveur (cumulable avec le zoom CSS, rare en mode real).
          const pan = panOffset();
          const tx = zoomOffset().x + pan.x;
          const ty = zoomOffset().y + pan.y;
          const hasPan = pan.x !== 0 || pan.y !== 0;
          if (isZoomed() || hasPan) {
            base.transform = `translate(${tx}px, ${ty}px) scale(${isZoomed() ? zoomScale() : 1})`;
            base["transform-origin"] = "center";
            base["will-change"] = "transform";
          }
          return Object.keys(base).length > 0 ? base : undefined;
        })()}
      >
        <Show when={!native}>
          <video
            ref={videoEl}
            class="viewer-video"
            style={{ display: showVideo() ? "block" : "none", "image-rendering": isZoomed() ? "crisp-edges" : "auto" }}
            muted
            playsinline
          />
          <canvas
            ref={canvasEl}
            class="viewer-canvas"
            style={{
              display: showCanvas() ? "block" : "none",
              filter: isGrayscale() ? "grayscale(1)" : "none",
              "image-rendering": isZoomed() ? "crisp-edges" : "auto",
            }}
          />
          <video
            ref={rtcVideoEl}
            class="viewer-video"
            style={{
              display: webrtcStreaming() ? "block" : "none",
              "image-rendering": isZoomed() ? "crisp-edges" : "auto",
            }}
            muted
            playsinline
            autoplay
          />
        </Show>

        {/* Curseur virtuel (mode trackpad mobile). Position en % de la
            surface affichée ; les events `move` envoyés au host utilisent
            la résolution du bureau distant pour le mapping coords. */}
        <Show when={trackpadMode() && (streaming() || webrtcStreaming())}>
          <div
            class="viewer-virtual-cursor"
            aria-hidden="true"
            style={{
              left: `${virtualCursor().x * 100}%`,
              top: `${virtualCursor().y * 100}%`,
            }}
          />
        </Show>

        {/* Curseurs des AUTRES opérateurs (multi-op, off par défaut). Position
            en % de la surface ; étiquette + couleur stable par opérateur. */}
        <Show when={showRemoteCursors() && (streaming() || webrtcStreaming())}>
          <For each={remoteCursorList()}>
            {(c) => (
              <div
                class="viewer-remote-cursor"
                aria-hidden="true"
                style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, "--rc-color": c.color }}
              >
                <span class="viewer-remote-cursor-label">{c.label}</span>
              </div>
            )}
          </For>
        </Show>

        {/* PiP actif : la vidéo est déportée dans la fenêtre Picture-in-Picture.
            On masque le média ici (le canvas continue d'être peint dessous pour
            alimenter captureStream) pour ne pas afficher la vidéo deux fois. */}
        <Show when={pipActive()}>
          <div class="viewer-pip-mask" role="status">
            <span>📌 Vidéo en Picture-in-Picture</span>
            <button class="btn btn--sm" onClick={() => closePiP()}>
              ↩ Rapatrier
            </button>
          </div>
        </Show>

        {/* Fenêtre séparée ouverte : la vidéo est portée par le popout, on masque
            ici pour ne pas l'afficher deux fois. Bouton de rapatriement. */}
        <Show when={popoutOpen() && !pipActive()}>
          <div class="viewer-pip-mask" role="status">
            <span>🗗 Vidéo dans la fenêtre séparée</span>
            <button class="btn btn--sm" onClick={() => closePopout()}>
              ↩ Rapatrier
            </button>
          </div>
        </Show>

        {/* Indicateur transient "1.5×" pendant un pinch — auto-fade 800 ms
            après la fin du geste. Affiché au centre, par-dessus tout. */}
        <Show when={zoomIndicatorVisible() && (streaming() || webrtcStreaming())}>
          <div
            class="viewer-zoom-indicator"
            classList={{ "viewer-zoom-indicator--active": pinching() }}
            aria-hidden="true"
          >
            {zoomScale().toFixed(1)}×
          </div>
        </Show>

        {/* M5 — overlay "Hôte hors ligne / reconnexion" quand le flux est
            figé > 5 s en plein streaming. La reconnexion WS est gérée
            ailleurs (shared/ws.tsx) ; on n'affiche que l'état visible. */}
        <Show when={streamStale()}>
          <div class="viewer-stale-overlay" role="status" aria-live="polite">
            <div class="viewer-stale-overlay__box">
              <span class="viewer-stale-overlay__title">Flux figé</span>
              <span class="viewer-stale-overlay__msg">
                Aucune frame reçue depuis {Math.round((Date.now() - (lastFrameAt() ?? Date.now())) / 1000)}&nbsp;s —
                reconnexion automatique en cours.
              </span>
            </div>
          </div>
        </Show>

        {/* WS-O — badge d'avertissement quand l'agent a détecté une inspection TLS
            (proxy MITM en firewall strict). Informatif, jamais bloquant. Le détail
            (émetteur du cert + raison) est en tooltip. */}
        <Show when={tlsInspected()}>
          {(info) => (
            <div
              class="viewer-tls-inspected"
              role="status"
              aria-live="polite"
              title={`${info().reason} (émetteur : ${info().issuer})`}
            >
              ⚠ Trafic inspecté par un proxy
            </div>
          )}
        </Show>

        <Show when={keyboardDebugVisible() && (streaming() || webrtcStreaming())}>
          <div class="vfx-key-debug" role="status" aria-live="polite">
            <div class="vfx-key-debug__head">
              <span>Debug clavier</span>
              <span>swap: {swapCmdCtrl() ? "on" : "off"}</span>
            </div>
            <Show
              when={keyboardDebugEntries().length > 0}
              fallback={<div class="vfx-key-debug__empty">Aucune touche reçue</div>}
            >
              <For each={keyboardDebugEntries()}>
                {(entry) => (
                  <div class="vfx-key-debug__row">
                    <span class="vfx-key-debug__action">{entry.action}</span>
                    <span class="vfx-key-debug__key" title={entry.key}>
                      {entry.key}
                    </span>
                    <span class="vfx-key-debug__code" title={entry.code}>
                      {entry.code}
                    </span>
                    <span class="vfx-key-debug__vk">{entry.vk}</span>
                    <span class="vfx-key-debug__mods">{entry.modifiers}</span>
                    <Show when={entry.note}>
                      <span class="vfx-key-debug__note">{entry.note}</span>
                    </Show>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </Show>

        {/* M1 — input invisible qui sert à pop le clavier OS sur mobile.
            Toujours présent dans le DOM (le focus doit venir d'un click
            synchrone, sinon iOS refuse), monté/démonté selon streaming(). */}
        <Show when={streaming() || webrtcStreaming()}>
          <input
            ref={(el) => (mobileKbInput = el)}
            class="viewer-keyboard-input"
            type="text"
            inputmode="text"
            autocapitalize="off"
            autocorrect="off"
            spellcheck={false}
            aria-label="Saisie clavier vers l'hôte distant"
            tabindex={-1}
            onInput={onMobileKbInput}
            onKeyDown={onMobileKbKeydown}
            onBlur={() => setKeyboardOpen(false)}
          />
        </Show>
      </div>

      <Show when={error() && !streaming() && !webrtcStreaming()}>
        <div class="viewer-idle-status" role="status" aria-live="polite">
          {error()}
        </div>
      </Show>

      <Show when={unattendedDialogOpen()}>
        <div class="viewer-unattended-backdrop" onClick={cancelUnattended}>
          <div class="viewer-unattended-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Acces non assiste</h3>
            <p>
              Aucun utilisateur n'est connecte sur ce poste. Le mot de passe est chiffre pour l'agent puis transmis
              uniquement pour ouvrir une session interactive.
            </p>
            <CredentialInput
              usage="powershell-runas"
              vaultDefaults={props.vaultDefaults}
              layout="stacked"
              fields={{ username: true, password: true, domain: "optional" }}
              defaults={{ username: "administrator" }}
              disabled={unattendedBusy()}
              requestAgentKey={requestCredentialKeyOnce}
              onResult={(r) => setUnattendedResult(r)}
            />
            <Show when={unattendedError()}>
              <div class="viewer-unattended-error">{unattendedError()}</div>
            </Show>
            <div class="viewer-unattended-actions">
              <button
                type="button"
                class="btn btn--sm btn--ghost"
                disabled={unattendedBusy()}
                onClick={cancelUnattended}
              >
                Annuler
              </button>
              <button
                type="button"
                class="btn btn--sm btn--primary"
                disabled={
                  unattendedBusy() ||
                  !(unattendedResult().ready || (unattendedResult().mode === "volt" && unattendedResult().credentialId))
                }
                onClick={() => void submitUnattended()}
              >
                {unattendedBusy() ? "Ouverture..." : "Ouvrir session"}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Session info sheet — overlay plein écran (style TeamViewer mobile).
          Liste transport, codec, qualité, résolutions, FPS, durée, multi-op. */}
      <Show when={sessionInfoOpen() && (streaming() || webrtcStreaming())}>
        <div class="viewer-info-sheet" role="dialog" aria-modal="true" aria-label="Informations de session">
          <div class="viewer-info-sheet__topbar">
            <button class="btn btn--sm btn--ghost" onClick={() => setSessionInfoOpen(false)}>
              ← Retour
            </button>
            <span class="viewer-info-sheet__title">Informations de session</span>
          </div>
          <div class="viewer-info-sheet__body">
            <div class="viewer-info-sheet__group">
              <div class="viewer-info-sheet__group-label">Transport</div>
              <div class="viewer-info-sheet__row">
                <span>Protocole</span>
                <span>{transport() === "webrtc" ? "WebRTC + TURN" : "WebSocket (WSS)"}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Codec</span>
                <span>{selectedPreset().label}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Profil qualité</span>
                <span>{findQualityProfile(selectedQualityId()).label}</span>
              </div>
            </div>

            <div class="viewer-info-sheet__group">
              <div class="viewer-info-sheet__group-label">Affichage</div>
              <div class="viewer-info-sheet__row">
                <span>Résolution encodeur</span>
                <span>{resolution() ? `${resolution()!.w} × ${resolution()!.h}` : "—"}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Résolution bureau</span>
                <span>{desktopRes() ? `${desktopRes()!.w} × ${desktopRes()!.h}` : "—"}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>FPS mesurés</span>
                <span>{measuredFps() > 0 ? `${measuredFps()} fps` : "—"}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Cadence inter-frames</span>
                <span>{frameIntervalMs() > 0 ? `${frameIntervalMs()} ms` : "—"}</span>
              </div>
            </div>

            <div class="viewer-info-sheet__group">
              <div class="viewer-info-sheet__group-label">Session</div>
              <div class="viewer-info-sheet__row">
                <span>Durée</span>
                <span>{sessionDuration()}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Code session</span>
                <span class="mono">{ws.state.sessionCode ?? "—"}</span>
              </div>
              <div class="viewer-info-sheet__row">
                <span>Opérateurs connectés</span>
                <span>{operators().length}</span>
              </div>
              <Show when={isMultiOp()}>
                <div class="viewer-info-sheet__row">
                  <span>Opérateur actif</span>
                  <span>{activeOp()?.label || activeOp()?.id.slice(0, 8) || "—"}</span>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </Show>

      {/* M3 — overlay "Connexion à <host>…" entre le clic protocole et
          la 1ʳᵉ frame / l'apparition de la surface RDP. Effacé via
          createEffect quand streaming/webrtcStreaming/rdp passent à true. */}
      <Show when={connecting()}>
        {(c) => (
          <div class="viewer-connecting-overlay" role="status" aria-live="polite">
            <div class="viewer-connecting-overlay__box">
              <span class="viewer-connecting-overlay__spinner" aria-hidden="true" />
              <span class="viewer-connecting-overlay__title">
                Connexion à {c().host.displayName || c().host.hostname}…
              </span>
              <span class="viewer-connecting-overlay__proto">via {c().protocol.toUpperCase()}</span>
              <button class="btn btn--xs btn--ghost" onClick={clearConnecting}>
                Annuler
              </button>
            </div>
          </div>
        )}
      </Show>

      {/* Proposition RDP non-bloquante : une session RDP active existe, mais on
          NE force PLUS la bascule (la session physique est capturée en WS). On
          propose de la rejoindre. Disparaît si la session RDP n'est plus active
          ou si on est déjà en mode RDP (rdpSuggestionVisible). */}
      <Show when={rdpSuggestionVisible()} keyed>
        {(sess) => (
          <div class="rdp-suggestion-banner" role="status" aria-live="polite">
            <span class="rdp-suggestion-banner__text">
              Session RDP active <strong>{rdpSessionLabel(sess)}</strong> — prise en main de la session physique en
              cours. Vous pouvez plutôt rejoindre la session RDP.
            </span>
            <span class="rdp-suggestion-banner__actions">
              <button class="btn btn--sm btn--primary" onClick={() => switchToRdpLoginForWs(sess)}>
                Rejoindre la session RDP #{sess.sessionId}
              </button>
              <button class="btn btn--sm btn--ghost" onClick={() => setRdpSuggestion(null)}>
                Ignorer
              </button>
            </span>
          </div>
        )}
      </Show>

      {/* Mode RDP — rendu inline du RdpPanel quand l'opérateur choisit RDP
          depuis la liste des hôtes. Le panneau RDP existant (onglet séparé)
          continue de fonctionner indépendamment. */}
      <Show when={isRdpMode()}>
        <div class="rdp-embedded-wrap">
          <div class="rdp-embedded-topbar">
            <button class="btn btn--sm btn--ghost" onClick={exitRdpMode} title="Revenir à la liste des hôtes">
              ← Liste des hôtes
            </button>
            <span class="rdp-embedded-title">Connexion RDP</span>
          </div>
          <Show
            keyed
            when={rdpSessionCode()}
            fallback={
              <RdpPanel visible={props.visible !== false && isRdpMode()} onCaptureSessionWs={startCaptureWithSession} />
            }
          >
            {(sessionCode) => (
              <RdpPanel
                sessionCode={sessionCode}
                visible={props.visible !== false && isRdpMode()}
                defaultHost={rdpDefaultsForActiveHost().host}
                defaultPort={3389}
                defaultDomain={rdpDefaultsForActiveHost().domain}
                defaultUsername={rdpDefaultsForActiveHost().username}
                onCaptureSessionWs={startCaptureWithSession}
              />
            )}
          </Show>
        </div>
      </Show>

      {/* Éditeur de profil clavier (modal partagé). Ouvert via le bouton
          "Profil" de la barre flottante. Permet Cmd→Ctrl, liste noire Mac,
          profil personnalisé éditable. */}
      <KeyboardProfileEditor
        open={profileEditorOpen()}
        onClose={() => setProfileEditorOpen(false)}
        activeProfileId={activeProfileId()}
        onProfileChange={onProfileChange}
      />

      {/* Mode MSRA — rendu inline du panel MSRA */}
      <Show when={isMsraMode() && !streaming() && !webrtcStreaming()}>
        <MsraInlinePanel host={remoteControlHost()} onClose={exitMsraMode} />
      </Show>

      {/* Idle view : hosts browser quand pas de streaming et pas en mode RDP/MSRA */}
      <Show when={!streaming() && !webrtcStreaming() && !isRdpMode() && !isMsraMode()}>
        <MainIdleView
          selectedPresetId={selectedPresetId()}
          selectedQualityId={selectedQualityId()}
          freeParams={freeParams()}
          dependencyReport={ws.state.agentDependencies}
          onPresetChange={(id) => {
            // WS-B — choix manuel : verrouille la sélection auto H265 par défaut.
            presetTouchedByUser = true;
            setSelectedPresetId(id);
          }}
          onQualityChange={setSelectedQualityId}
          onFreeParamsChange={(p) => setFreeParams((prev) => ({ ...prev, ...p }))}
          isPresetSupported={isPresetSupported}
          onStart={startCapture}
          onStartWebRtc={startWebRtcCapture}
          onStartRdp={startRdpCapture}
          onStartMsra={startMsraCapture}
          onConnecting={startConnecting}
          sessionHost={props.sessionHost ?? null}
        />
      </Show>

      {/* HUD de télémétrie — overlay optionnel dans le coin de la vidéo */}
      <Show when={(streaming() || webrtcStreaming()) && hudVisible()}>
        <div class="vfx-hud" aria-live="off">
          <div class="vfx-hud-row">
            <span class="vfx-hud-label">transport</span>
            <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": transport() === "wss" }}>
              {transport() === "webrtc" ? "WebRTC/TURN" : "WSS (fallback)"}
            </span>
          </div>
          <div class="vfx-hud-row">
            <span class="vfx-hud-label">ABR</span>
            <span class="vfx-hud-value vfx-hud-value--muted">
              {abrEnabled() ? `palier ${abrRung() + 1}/5` : "manuel"}
            </span>
          </div>
          {/* WS-B — télémétrie codec : demandé → effectif (divergence = cascade
              agent H265→H264). Toujours affiché : info légère déjà calculée. */}
          <Show when={effectiveCodec()}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">codec</span>
              <span
                class="vfx-hud-value"
                classList={{
                  "vfx-hud-value--warn":
                    requestedCodec() !== "" && effectiveCodec() !== "" && requestedCodec() !== effectiveCodec(),
                }}
              >
                {requestedCodec() && requestedCodec() !== effectiveCodec()
                  ? `${requestedCodec()} → ${effectiveCodec()}`
                  : effectiveCodec()}
                {h265DefaultApplied() ? " · H265 défaut" : ""}
              </span>
            </div>
          </Show>
          <div class="vfx-hud-row">
            <span class="vfx-hud-label">fps</span>
            <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": measuredFps() > 0 && measuredFps() < 10 }}>
              {measuredFps()}
            </span>
          </div>
          <div class="vfx-hud-row">
            <span class="vfx-hud-label">cadence</span>
            <Show when={frameIntervalMs() > 0} fallback={<span class="vfx-hud-value vfx-hud-value--muted">—</span>}>
              <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": frameIntervalMs() > 120 }}>
                {frameIntervalMs()} ms
              </span>
            </Show>
          </div>
          <Show when={fps() > 0}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">cible</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{fps()} fps</span>
            </div>
          </Show>
          <Show when={desktopRes()}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">source</span>
              <span class="vfx-hud-value vfx-hud-value--muted">
                {desktopRes()!.w}×{desktopRes()!.h}
              </span>
            </div>
          </Show>
          <Show when={resolution()}>
            {(r) => {
              // En mode 1:1, l'encodée a le ratio de sourceRect (portion cropée),
              // pas du bureau entier → comparer au bon référent pour le warning.
              const sr = sourceRect();
              const refAr =
                sr && sr.h ? sr.w / sr.h : desktopRes() && desktopRes()!.h ? desktopRes()!.w / desktopRes()!.h : null;
              const mismatch = refAr != null && !!r().h && Math.abs(refAr - r().w / r().h) > 0.02;
              return (
                <div class="vfx-hud-row">
                  <span class="vfx-hud-label">encodée</span>
                  <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": mismatch }}>
                    {r().w}×{r().h}
                    {mismatch ? " ⚠ ratio≠source" : ""}
                  </span>
                </div>
              );
            }}
          </Show>
          <div class="vfx-hud-row">
            <span class="vfx-hud-label">cadrage</span>
            <span class="vfx-hud-value" classList={{ "vfx-hud-value--info": framingMode() === "real" }}>
              {framingMode() === "real" ? "1:1 (taille réelle)" : "adapté (fit)"}
              {sessionKind() ? ` · ${sessionKind() === "rdp" ? "RDP" : "console"}` : ""}
            </span>
          </div>
          <Show when={sourceRect()}>
            {(sr) => (
              <div class="vfx-hud-row">
                <span class="vfx-hud-label">fenêtre 1:1</span>
                <span class="vfx-hud-value vfx-hud-value--muted">
                  {sr().w}×{sr().h} @ {sr().x},{sr().y}
                </span>
              </div>
            )}
          </Show>
          <Show when={viewport()}>
            {(v) => (
              <div class="vfx-hud-row">
                <span class="vfx-hud-label">viewport</span>
                <span class="vfx-hud-value vfx-hud-value--muted">
                  {v().w}×{v().h} ×{v().dpr}
                </span>
              </div>
            )}
          </Show>
          <Show when={liveHostOs()}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">OS</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{liveHostOs()}</span>
            </div>
          </Show>
          <Show when={liveHostIp()}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">IP</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{liveHostIp()}</span>
            </div>
          </Show>
          <Show when={liveHostVlan()}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">VLAN</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{liveHostVlan()}</span>
            </div>
          </Show>
          <Show when={liveMetricsAt() !== null}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">buffer WS</span>
              <span
                class="vfx-hud-value"
                classList={{ "vfx-hud-value--warn": liveWsBufferedBytes() > 4 * 1024 * 1024 }}
              >
                {formatBytes(liveWsBufferedBytes())}
              </span>
            </div>
          </Show>
          <Show when={bitrateKbps() > 0}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">bitrate 30s</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{bitrateKbps()} kbps</span>
            </div>
          </Show>
          <Show when={helperCpu() !== null}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">cpu hôte</span>
              <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": (helperCpu() ?? 0) > 25 }}>
                {helperCpu()}%
              </span>
            </div>
          </Show>
          {/* WS-D — métriques navigateur fines (decode-time, drops, queue).
              Affichées seulement si le flag videoFineMetrics est ON. */}
          <Show when={fineMetrics}>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">decode</span>
              <Show when={decodeMs() > 0} fallback={<span class="vfx-hud-value vfx-hud-value--muted">—</span>}>
                <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": decodeMs() > 20 }}>
                  {decodeMs()} ms
                </span>
              </Show>
            </div>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">drops</span>
              <span class="vfx-hud-value vfx-hud-value--muted">{droppedFrames()}</span>
            </div>
            <div class="vfx-hud-row">
              <span class="vfx-hud-label">file décodeur</span>
              <span class="vfx-hud-value" classList={{ "vfx-hud-value--warn": decodeQueueSize() > 5 }}>
                {decodeQueueSize()}
              </span>
            </div>
          </Show>
        </div>
      </Show>

      {/* Floating Action Bar — barre de contrôle superposée en haut */}
      <Show when={streaming()}>
        <div
          class="vfx-floating-bar"
          classList={{
            "vfx-floating-bar--hidden": !barVisible(),
            "vfx-floating-bar--collapsed": !floatingBarVisible(),
          }}
          onMouseMove={wakeBar}
          onMouseEnter={wakeBar}
        >
          <Show
            when={floatingBarVisible()}
            fallback={
              <button class="btn btn--sm btn--ghost" onClick={toggleFloatingBar} title="Afficher la barre">
                ⏶ Barre
              </button>
            }
          >
            <button class="btn btn--sm btn--ghost" onClick={toggleFloatingBar} title="Masquer la barre">
              ⏷ Barre
            </button>

            <button class="btn btn--sm btn--danger" onClick={stopCapture}>
              Arrêter
            </button>

            {/* Cadrage : dans la barre plutôt que dans le menu « Paramètres
                vidéo ». C'est le remède au bureau qui ne tient pas dans le
                cadre — il doit être visible sans fouiller. */}
            <button
              class="btn btn--sm"
              classList={{ "btn--info": framingMode() === "real" }}
              onClick={toggleFraming}
              title={
                framingMode() === "real"
                  ? "Mode taille réelle (1:1) actif — pixels natifs, déplacement aux ascenseurs, à la molette, au bouton du milieu ou aux bords. Cliquer pour revenir au cadrage adapté."
                  : "Taille réelle (1:1) : affiche le bureau distant pixel pour pixel (net sur les bureaux 4K). Déplacement aux ascenseurs, à la molette, au bouton du milieu, ou aux bords. Redémarre brièvement le flux."
              }
            >
              Taille réelle (1:1) {framingMode() === "real" ? "ON" : "OFF"}
            </button>

            {/* Groupement : vidéo */}
            <div class="vfx-control-group">
              <button
                class="btn btn--sm"
                classList={{ "btn--info": qualityPickerOpen() }}
                onClick={toggleVideoSettingsMenu}
                title="Paramètres vidéo (qualité, bitrate, auto, zoom)"
              >
                🎬 Paramètres vidéo
              </button>
              <Show when={qualityPickerOpen()}>
                <div class="vfx-control-group__menu vfx-control-group__menu--wide">
                  <div class="vfx-control-group__heading">Qualité vidéo</div>
                  <For each={QUALITY_PROFILES}>
                    {(p) => (
                      <button
                        type="button"
                        class="btn btn--sm"
                        classList={{ "btn--info": p.id === selectedQualityId() }}
                        title={p.description}
                        onClick={() => void switchQuality(p.id)}
                      >
                        {p.label}
                      </button>
                    )}
                  </For>
                  <label class="viewer-controls-label">
                    <span class="vfx-control-group__label">Bitrate</span>
                    <input
                      type="range"
                      min="200"
                      max="12000"
                      step="200"
                      value={bitrate()}
                      onInput={(e) => updateBitrate(Number(e.currentTarget.value))}
                    />
                    {bitrate()} kbps
                  </label>
                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": abrEnabled() }}
                    onClick={toggleAbr}
                    title="Adaptation automatique du débit selon la qualité réseau mesurée."
                  >
                    ABR {abrEnabled() ? "ON" : "OFF"}
                  </button>
                  <Show when={isZoomed()}>
                    <button
                      class="btn btn--sm btn--ghost"
                      onClick={resetZoom}
                      title={`Zoom ${zoomScale().toFixed(1)}× — réinitialiser`}
                    >
                      🔍 {zoomScale().toFixed(1)}× ↺
                    </button>
                  </Show>
                </div>
              </Show>
            </div>

            {/* Commandes spéciales */}
            <div class="vfx-control-group">
              <button
                class="btn btn--sm"
                classList={{ "btn--info": specialKeysOpen() }}
                onClick={toggleSpecialKeysMenu}
                title="Commandes clavier rapides vers la machine distante"
              >
                ⌨ Commandes spéciales
              </button>
              <Show when={specialKeysOpen()}>
                <div class="vfx-control-group__menu">
                  <For each={activeSpecialKeys()}>
                    {(item) => (
                      <button class="btn btn--sm vfx-control-group__item" onClick={item.run}>
                        {item.label}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>

            {/* Presse-papier */}
            <div class="vfx-control-group">
              <button
                class="btn btn--sm"
                classList={{ "btn--info": clipboardMenuOpen() }}
                onClick={toggleClipboardMenu}
                title="Presse-papier : envoi, copie et récupération"
              >
                📋 Presse-papier
              </button>
              <Show when={clipboardMenuOpen()}>
                <div class="vfx-control-group__menu">
                  <button
                    class="btn btn--sm"
                    onClick={pasteAsTyping}
                    title="Coller le presse-papier local en frappe clavier"
                  >
                    Coller (frappe)
                  </button>
                  <button
                    class="btn btn--sm"
                    onClick={() => void pushClipboard()}
                    title="Envoyer mon presse-papier vers la machine distante"
                  >
                    Envoyer presse-papier
                  </button>
                  <button
                    class="btn btn--sm"
                    onClick={requestClipboard}
                    title="Récupérer le presse-papier de la machine distante"
                  >
                    Récupérer presse-papier
                  </button>
                </div>
              </Show>
            </div>

            {/* Paramètres prise en main */}
            <div class="vfx-control-group">
              <button
                class="btn btn--sm"
                classList={{ "btn--info": controlSettingsOpen() }}
                onClick={toggleControlSettingsMenu}
                title="Prise en main et contrôles souris/clavier"
              >
                🎮 Prise en main
              </button>
              <Show when={controlSettingsOpen()}>
                <div class="vfx-control-group__menu">
                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": pointerLocked() }}
                    onClick={togglePointerLock}
                    title={pointerLocked() ? "Libérer la souris (Echap)" : "Verrouiller pour prise en main"}
                  >
                    {pointerLocked() ? "Libérer" : "Verrouiller souris"}
                  </button>

                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": trackpadMode() }}
                    onClick={() => setTrackpadMode((v) => !v)}
                    title="Mode trackpad : un doigt déplace le curseur virtuel (relatif), tap = clic, 2 doigts = clic droit. Recommandé sur mobile."
                  >
                    Trackpad {trackpadMode() ? "ON" : "OFF"}
                  </button>

                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": keyboardOpen() }}
                    onClick={toggleMobileKeyboard}
                    title="Ouvrir/fermer le clavier de l'appareil — chaque touche est envoyée au host"
                  >
                    ⌨ {keyboardOpen() ? "ON" : "Clavier"}
                  </button>

                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": hudVisible() }}
                    onClick={() => setHudVisible((v) => !v)}
                    title="Afficher/masquer le HUD de télémétrie (fps, cadence réseau)"
                  >
                    HUD {hudVisible() ? "ON" : "OFF"}
                  </button>

                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": followMouse() }}
                    onClick={toggleFollowMouse}
                    title="Quand activé, le curseur distant suit en continu la souris. Désactivé, seuls les clics et le glisser agissent."
                  >
                    Suivre la souris {followMouse() ? "ON" : "OFF"}
                  </button>

                  <button
                    class="btn btn--sm"
                    classList={{ "btn--info": swapCmdCtrl() }}
                    onClick={() => setSwapCmdCtrl((v) => !v)}
                    title="Quand activé, la touche Cmd (Mac) est envoyée comme Ctrl à Windows"
                  >
                    Cmd→Ctrl {swapCmdCtrl() ? "ON" : "OFF"}
                  </button>
                  <button
                    class="btn btn--sm"
                    onClick={() => setProfileEditorOpen(true)}
                    title="Profil clavier : Cmd→Ctrl, liste noire Mac, profil personnalisé"
                  >
                    ⌨ Profil
                  </button>
                </div>
              </Show>
            </div>

            {/* Bouton "Info session" — ouvre le sheet plein écran avec les
                métriques (transport, codec, résolution, FPS, durée, multi-op). */}
            <button
              class="btn btn--sm"
              classList={{ "btn--info": sessionInfoOpen() }}
              onClick={() => setSessionInfoOpen(true)}
              title="Informations détaillées sur la session en cours"
            >
              ⓘ Info
            </button>

            {/* Plein écran */}
            <button
              class="btn btn--sm"
              onClick={toggleFullscreen}
              title={isFullscreen() ? "Quitter le plein écran" : "Plein écran"}
            >
              {isFullscreen() ? "⛶" : "⛶"} {isFullscreen() ? "Réduire" : "Plein écran"}
            </button>

            {/* Focus mode */}
            <button
              class="btn btn--sm"
              classList={{ "btn--info": focusMode() }}
              onClick={toggleFocusMode}
              title={focusMode() ? "Quitter le mode focus" : "Mode focus"}
            >
              {focusMode() ? "🖵" : "🖵"} {focusMode() ? "Focus OFF" : "Focus"}
            </button>

            {/* Document PiP (Chrome/Edge uniquement) */}
            <Show when={pipAvailable()}>
              <button
                class="btn btn--sm"
                classList={{ "btn--info": pipActive() }}
                onClick={togglePiP}
                title={pipActive() ? "Fermer le Picture-in-Picture" : "Picture-in-Picture"}
              >
                📌 PiP {pipActive() ? "ON" : ""}
              </button>
            </Show>

            {/* Fenêtre séparée (reconnectée au même code). Quand elle est
                ouverte, la vidéo d'origine est masquée pour éviter le doublon. */}
            <button
              class="btn btn--sm"
              classList={{ "btn--info": popoutOpen() }}
              onClick={() => (popoutOpen() ? closePopout() : openPopout())}
              title={popoutOpen() ? "Fermer la fenêtre séparée" : "Ouvrir dans une fenêtre séparée"}
            >
              🗗 {popoutOpen() ? "Rapatrier" : "Fenêtre"}
            </button>

            {/* Multi-op : afficher/masquer les curseurs des autres opérateurs.
                Désactivé par défaut ("curseur qui suit" off). */}
            <Show when={isMultiOp()}>
              <button
                class="btn btn--sm"
                classList={{ "btn--info": showRemoteCursors() }}
                onClick={toggleRemoteCursors}
                title={
                  showRemoteCursors()
                    ? "Masquer les curseurs des autres opérateurs"
                    : "Afficher les curseurs des autres opérateurs"
                }
              >
                ➤ Curseurs {showRemoteCursors() ? "ON" : "OFF"}
              </button>
            </Show>

            {/* M4 — badge multi-opérateur : indique si on contrôle ou si on
                n'est qu'observateur. Bouton "Prendre la main" si on est passif. */}
            <Show when={isMultiOp()}>
              <Show
                when={isMeActive()}
                fallback={
                  <button
                    class="btn btn--sm"
                    classList={{ "btn--primary": activeOp() != null }}
                    onClick={() => ws.send({ type: "operator:activate" } as never)}
                    title={
                      activeOp() != null
                        ? `Contrôle exclusif : ${activeOp()?.label || "autre opérateur"} — cliquer pour le prendre`
                        : "Clic direct partagé — cliquer pour prendre le contrôle exclusif"
                    }
                  >
                    {activeOp() != null ? "👥 Prendre la main" : "👥 Contrôle exclusif"}
                  </button>
                }
              >
                <button
                  class="btn btn--sm vfx-multi-op vfx-multi-op--active"
                  onClick={() => ws.send({ type: "operator:deactivate" } as never)}
                  title="Vous avez le contrôle exclusif — cliquer pour le relâcher (retour au clic direct partagé)"
                >
                  ✋ Vous contrôlez — relâcher
                </button>
              </Show>
            </Show>

            <Show when={toast()}>
              <span class="viewer-controls-info viewer-controls-toast">{toast()}</span>
            </Show>
            <Show when={error()}>
              <span class="viewer-controls-error">{error()}</span>
            </Show>
            <span class="viewer-controls-sep" />
            <span class="viewer-controls-info">
              {selectedCodecLabel()} · {selectedQualityLabel()}
            </span>
            <Show when={resolution()}>
              {(res) => (
                <span class="viewer-controls-info">
                  {res().w}×{res().h}
                  <Show when={fps()}> @ {fps()} fps</Show>
                </span>
              )}
            </Show>
          </Show>
        </div>
      </Show>
      {/* WebRTC overlay controls */}
      <Show when={webrtcStreaming()}>
        <div class="viewer-controls">
          <button class="btn btn--sm btn--danger" onClick={stopWebRtcCapture}>
            Arrêter WebRTC
          </button>
          <button
            class="btn btn--sm"
            classList={{ "btn--info": abrEnabled() }}
            onClick={toggleAbr}
            title="Adaptation automatique du débit selon la qualité réseau mesurée. FPS/résolution restent manuels."
          >
            ABR {abrEnabled() ? "ON" : "OFF"}
          </button>
          <button
            class="btn btn--sm"
            classList={{ "btn--info": hudVisible() }}
            onClick={() => setHudVisible((v) => !v)}
            title="Afficher/masquer le HUD de télémétrie"
          >
            HUD {hudVisible() ? "ON" : "OFF"}
          </button>
          <label class="viewer-controls-label">
            <input
              type="range"
              min="200"
              max="12000"
              step="200"
              value={bitrate()}
              onInput={(e) => updateBitrate(Number(e.currentTarget.value))}
            />
            {bitrate()} kbps
          </label>
          <Show when={error()}>
            <span class="viewer-controls-error">{error()}</span>
          </Show>
        </div>
      </Show>

      {/* Ascenseurs — mode 1:1 uniquement (en « fit » le bureau entier tient
          dans le cadre). Rendus FRÈRES de .viewer-surface : celle-ci porte le
          transform du pan, des ascenseurs à l'intérieur défileraient avec
          l'image. Étant hors de la surface, y entrer déclenche son mouseleave,
          ce qui coupe l'auto-pan aux bords — pas de conflit avec le bord. */}
      <Show when={scrollThumb("x")}>
        {(t) => (
          <div
            class="viewer-scrollbar viewer-scrollbar--h"
            onPointerDown={(e) => onScrollTrackDown("x", e)}
            title="Déplacer la vue horizontalement"
          >
            <div
              class="viewer-scrollbar__thumb"
              style={{ left: `${t().pos * 100}%`, width: `${t().size * 100}%` }}
              onPointerDown={(e) => onScrollThumbDown("x", e)}
              onPointerMove={onScrollThumbMove}
              onPointerUp={onScrollThumbUp}
              onPointerCancel={onScrollThumbUp}
            />
          </div>
        )}
      </Show>
      <Show when={scrollThumb("y")}>
        {(t) => (
          <div
            class="viewer-scrollbar viewer-scrollbar--v"
            onPointerDown={(e) => onScrollTrackDown("y", e)}
            title="Déplacer la vue verticalement"
          >
            <div
              class="viewer-scrollbar__thumb"
              style={{ top: `${t().pos * 100}%`, height: `${t().size * 100}%` }}
              onPointerDown={(e) => onScrollThumbDown("y", e)}
              onPointerMove={onScrollThumbMove}
              onPointerUp={onScrollThumbUp}
              onPointerCancel={onScrollThumbUp}
            />
          </div>
        )}
      </Show>
    </div>
  );
};

export default VideoPanel;
